// AUTO-GENERATED by scripts/build-data.js
// Do not edit manually. Re-run the script after updating framework markdown files.

export const frameworkContent = {
  naming: {
    README: `# 01 — Brand Naming

This section provides a repeatable methodology for inventing, evaluating, and legally screening brand names across industries.

## Files

- \`naming_methodology.md\` — Step-by-step naming process.
- \`industry_naming_matrix.md\` — Name archetypes, suffixes, and examples by industry.
- \`name_evaluation_scorecard.md\` — Quantitative scorecard for candidate names.
- \`trademark_screening_checklist.md\` — Pre-filing and pre-launch screening steps.

## Process Summary

1. Define strategy (positioning, audience, geography, tone).
2. Generate name candidates using industry archetypes and word banks.
3. Shortlist against availability, pronunciation, and memorability criteria.
4. Score candidates with the evaluation scorecard.
5. Screen for trademark, domain, and social-handle availability.
6. Validate with target audience research.
7. Finalize and register.
`,
    industry_naming_matrix: `# Industry Naming Matrix

Name archetypes, common suffixes, tone cues, and example naming patterns by industry.

## Technology / SaaS

- Archetypes: Invented, compound, metaphor
- Suffixes: -ly, -ify, -io, -cloud, -edge, -bit, -scale, -flow
- Tone: Modern, efficient, intelligent
- Examples: Shopify, Datadog, Notion, Figma
- Cues: Use short, memorable names that suggest speed, data, or connectivity.

## Financial Services / Fintech

- Archetypes: Evocative, trust-based, compound
- Suffixes: -vest, -bank, -pay, -fin, -wealth, -capital, -clear
- Tone: Secure, credible, simple
- Examples: Stripe, Robinhood, Square, Wise
- Cues: Avoid gimmicky spellings; emphasize stability and transparency.

## Healthcare / Biotech

- Archetypes: Descriptive, scientific, Latin/Greek roots
- Suffixes: -care, -med, -bio, -gen, -x, -health, -life
- Tone: Professional, compassionate, precise
- Examples: Medtronic, Pfizer, Teladoc, AstraZeneca
- Cues: Verify regulatory naming restrictions in each market.

## Consumer / Retail / E-Commerce

- Archetypes: Evocative, founder, compound
- Suffixes: -co, -goods, -shop, -mart, -box, -hive
- Tone: Friendly, aspirational, lifestyle
- Examples: Everlane, Glossier, Allbirds, Warby Parker
- Cues: Names should photograph well and feel shareable on social media.

## Professional Services

- Archetypes: Founder, descriptive, geography-based
- Suffixes: & Partners, Group, Advisors, Consulting, Associates
- Tone: Authoritative, relationship-driven
- Examples: McKinsey, Deloitte, Boston Consulting Group
- Cues: Longer names are acceptable when credibility is the priority.

## Manufacturing / Industrial

- Archetypes: Descriptive, geography, strength metaphor
- Suffixes: -tech, -works, -industries, -systems, -steel, -power
- Tone: Robust, reliable, engineering-focused
- Examples: Caterpillar, Siemens, Bosch, 3M
- Cues: Convey scale, durability, and technical competence.

## Food / Beverage

- Archetypes: Evocative, founder, sensory
- Suffixes: -kitchen, -table, -farm, -brew, -bite, -blend
- Tone: Appetizing, authentic, craft
- Examples: Impossible Foods, Chobani, Blue Bottle, Oatly
- Cues: Names should evoke taste, origin, or ritual.

## Education / EdTech

- Archetypes: Evocative, descriptive, aspirational
- Suffixes: -academy, -school, -learn, -edu, -mind, -path
- Tone: Encouraging, credible, accessible
- Examples: Coursera, Khan Academy, Duolingo, MasterClass
- Cues: Suggest growth, mastery, and community.

## Energy / Sustainability

- Archetypes: Metaphor, nature-based, technical
- Suffixes: -sun, -wind, -green, -earth, -nova, -grid, -source
- Tone: Optimistic, responsible, forward-looking
- Examples: Tesla, NextEra, Vestas, Sunrun
- Cues: Avoid greenwashing cues that feel generic.

## Hospitality / Travel

- Archetypes: Evocative, geography, sensory
- Suffixes: -stays, -hotel, -voyage, -wander, -escape
- Tone: Welcoming, adventurous, relaxing
- Examples: Airbnb, Marriott, Booking.com, Four Seasons
- Cues: Names should trigger emotion and place.

## Naming Suffix Register

| Suffix | Connotation | Best For |
|--------|-------------|----------|
| -ly | Friendly, action-oriented | SaaS, tools |
| -ify | To make, transform | Platforms, automation |
| -io | Tech, data, input/output | Software, hardware |
| -cloud | Scalable, online | Infrastructure, SaaS |
| -edge | Cutting-edge, fast | Tech, security |
| -flow | Process, smooth | Workflow, creative tools |
| -care | Service, health | Health, wellness |
| -pay | Payments, transactions | Fintech |
| -works | Craft, manufacturing | Industrial, agencies |
| -co | Modern, simple | Consumer, DTC |
`,
    name_evaluation_scorecard: `# Name Evaluation Scorecard

Rate each candidate name across eight dimensions. Use a 1–5 scale (1 = poor, 5 = excellent).

## Scoring Matrix

| Criterion | Weight | Score | Weighted Score | Notes |
|-----------|--------|-------|----------------|-------|
| Memorability | 15% | | | Can people recall it after one exposure? |
| Pronounceability | 10% | | | Easy to say in all target markets? |
| Spelling | 10% | | | Can people spell it after hearing it? |
| Distinctiveness | 15% | | | Stands out from competitors? |
| Relevance | 15% | | | Fits industry and positioning? |
| Scalability | 10% | | | Allows product or geographic expansion? |
| Legal Availability | 15% | | | Domain, trademark, social handles? |
| Linguistic Safety | 10% | | | No negative meanings in target languages? |
| **Total** | **100%** | | | |

## Decision Thresholds

- **Proceed**: 4.0 or higher; begin formal trademark screening.
- **Rework**: 3.0–3.9; address weak dimensions before reconsidering.
- **Reject**: Below 3.0; do not advance.

## Minimum Gate Criteria

A name must score at least **3** on each of the following to advance:

- Legal Availability
- Linguistic Safety
- Pronounceability

Even a high average fails if a critical gate criterion is weak.

## Worked Example

| Criterion | Weight | Score | Weighted |
|-----------|--------|-------|----------|
| Memorability | 15% | 4 | 0.60 |
| Pronounceability | 10% | 5 | 0.50 |
| Spelling | 10% | 4 | 0.40 |
| Distinctiveness | 15% | 4 | 0.60 |
| Relevance | 15% | 3 | 0.45 |
| Scalability | 10% | 5 | 0.50 |
| Legal Availability | 15% | 3 | 0.45 |
| Linguistic Safety | 10% | 5 | 0.50 |
| **Total** | | | **4.00** |

Result: Proceed with legal deep-dive.
`,
    naming_methodology: `# Brand Naming Methodology

A repeatable process for creating brand names.

## 1. Strategy Inputs

| Input | Question |
|-------|----------|
| Industry | What sector and sub-sector? |
| Audience | Who is the primary buyer and user? |
| Geography | Local, national, regional, or global? |
| Positioning | Premium, accessible, innovative, traditional? |
| Tone | Playful, technical, trustworthy, disruptive? |
| Longevity | Is this a product, product line, or enduring company? |
| Languages | Which languages must the name work in? |

## 2. Name Architecture

Decide the naming hierarchy before generating candidates:

- **Masterbrand**: One name covers all products and services.
- **Endorsed**: Sub-brands carry the parent name.
- **Freestanding**: Each offering has its own name.
- **Hybrid**: Masterbrand for core, freestanding for ventures.

## 3. Name Types

| Type | Description | Example |
|------|-------------|---------|
| Descriptive | Says what the company does | General Motors |
| Evocative | Suggests a feeling or idea | Apple |
| Invented | New, coined word | Kodak |
| Acronym | Initials of a longer name | IBM |
| Founder | Based on founder name | Ford |
| Compound | Two words joined | Microsoft |
| Geographical | Place-based | Cisco (San Francisco) |
| Metaphor | Borrowed concept | Amazon |

## 4. Naming Workshop Steps

1. **Dump**: Generate 50–100 raw words, roots, and concepts. No filtering.
2. **Cluster**: Group by theme, feeling, and word structure.
3. **Construct**: Build 20–30 candidate names using prefixes, suffixes, compounds, and truncations.
4. **Filter**: Remove names that fail basic checks (negative connotations, hard to pronounce, obvious conflicts).
5. **Score**: Apply the \`name_evaluation_scorecard.md\`.
6. **Validate**: Test top candidates with the target audience.

## 5. Naming Rules

- Easy to pronounce in all target markets.
- Easy to spell after hearing it once.
- Short enough for URLs, social handles, and headlines.
- Distinctive within the competitive set.
- Emotionally aligned with brand positioning.
- Legally and linguistically safe.

## 6. Outputs

- Naming strategy brief
- Candidate list with rationale
- Scorecard results
- Availability screening report
- Final recommendation with risk notes
`,
    trademark_screening_checklist: `# Trademark Screening Checklist

Use this checklist before finalizing a brand name.

## Preliminary Search (Internal)

- [ ] Search exact name in major search engines.
- [ ] Search common misspellings and transliterations.
- [ ] Check domain availability:
  - [ ] \`.com\`
  - [ ] Country-code TLDs (e.g., \`.de\`, \`.co.uk\`, \`.fr\`, \`.jp\`)
  - [ ] Relevant new gTLDs (e.g., \`.io\`, \`.tech\`, \`.app\`)
- [ ] Check social media handles:
  - [ ] LinkedIn
  - [ ] X / Twitter
  - [ ] Instagram
  - [ ] Facebook
  - [ ] TikTok
  - [ ] YouTube
- [ ] Check app store availability (if relevant).
- [ ] Review competitor names in the same category and adjacent categories.

## Formal Search (Legal Counsel)

- [ ] Order a trademark clearance search in each target jurisdiction.
- [ ] Review identical and similar marks in relevant classes.
- [ ] Assess likelihood-of-confusion risk.
- [ ] Check for well-known / famous marks that may receive broader protection.
- [ ] Identify any pending oppositions or litigation involving similar marks.

## Linguistic and Cultural Checks

- [ ] Translate name into each official language of target markets.
- [ ] Check phonetic similarity to offensive or negative words.
- [ ] Verify no unintended associations with historical events, religions, or slang.
- [ ] Test pronunciation with native speakers.

## Registration and Protection

- [ ] Confirm final spelling and any stylization.
- [ ] Identify all relevant Nice Classification classes.
- [ ] File trademark applications in priority jurisdictions.
- [ ] Secure matching domains and social handles.
- [ ] Record ownership and renewal dates in the IP register.

## Go / No-Go Sign-Off

| Reviewer | Role | Decision | Date |
|----------|------|----------|------|
| | Naming Lead | | |
| | Legal / IP Counsel | | |
| | Marketing Lead | | |
| | Local Market Reviewer (if global) | | |

**Final decision**: Go / Go with reservations / No-go
`,
  },
  legal: {
    README: `# 02 — Legal Structures

This section helps founders and counsel choose legal entities and jurisdictional structures for national and international companies.

## Files

- \`entity_selection_framework.md\` — Decision logic for choosing an entity type.
- \`jurisdiction_matrix.md\` — Comparison of popular jurisdictions by factor.
- \`international_holding_structures.md\` — Common multi-jurisdiction setups.
- \`compliance_checklist.md\` — Ongoing legal and regulatory obligations.

## When to Use

- Incorporating a new company.
- Expanding into new countries.
- Restructuring for investment, tax, or liability reasons.
- Setting up a holding company or IP vehicle.
`,
    compliance_checklist: `# Legal and Regulatory Compliance Checklist

Ongoing obligations after incorporation.

## Corporate Maintenance

- [ ] Annual returns / confirmation statements filed.
- [ ] Director and shareholder registers kept up to date.
- [ ] Statutory books maintained.
- [ ] Registered office address and agent current.
- [ ] Share issuances and transfers properly documented.
- [ ] Board and shareholder minutes archived.

## Tax Compliance

- [ ] Corporate income tax returns filed on time.
- [ ] VAT / GST / sales tax registration and filings current.
- [ ] Payroll taxes withheld and remitted.
- [ ] Transfer pricing documentation prepared.
- [ ] Tax residency and substance reviewed annually.
- [ ] Country-by-country reporting (if applicable).

## Employment and Labor

- [ ] Employment contracts in place for all employees.
- [ ] Worker classification reviewed (employee vs. contractor).
- [ ] Minimum wage, overtime, and leave rules followed.
- [ ] Health and safety obligations met.
- [ ] Equal opportunity and anti-discrimination policies implemented.

## Intellectual Property

- [ ] Trademark renewals tracked and filed.
- [ ] Patent and copyright registrations maintained.
- [ ] Domain names renewed.
- [ ] IP assignments from founders / contractors recorded.
- [ ] Confidentiality agreements in place.

## Data Protection and Privacy

- [ ] Privacy policy published and current.
- [ ] Data processing agreements with vendors.
- [ ] Cross-border transfer mechanisms documented.
- [ ] Data subject request process operational.
- [ ] Security incident response plan tested.

## Contracts and Commercial

- [ ] Standard contract templates reviewed annually.
- [ ] Major contracts tracked for renewal and termination.
- [ ] Limitation of liability and indemnity terms consistent.
- [ ] Dispute resolution clauses appropriate to jurisdiction.

## Industry-Specific Licensing

- [ ] Financial services licenses and registrations.
- [ ] Healthcare / medical device approvals.
- [ ] Food and beverage permits.
- [ ] Telecommunications and broadcasting licenses.
- [ ] Crypto / money transmission registrations.

## Record Retention

| Record Type | Minimum Retention |
|-------------|-------------------|
| Corporate minutes | Permanent |
| Tax records | 7+ years |
| Employment records | Duration + 7 years |
| Contracts | Duration + 7 years |
| Financial statements | Permanent |
| IP registrations | Permanent |
| Data subject requests | 3+ years |

## Review Schedule

| Activity | Frequency | Owner |
|----------|-----------|-------|
| Compliance self-assessment | Quarterly | General Counsel / COO |
| Annual filings | Annual | Company Secretary |
| Contract template review | Annual | Legal |
| Privacy policy review | Annual / on law change | DPO / Legal |
| IP renewal review | Quarterly | IP Counsel |
`,
    entity_selection_framework: `# Entity Selection Framework

A decision framework for choosing the legal structure of a company.

## Decision Factors

| Factor | Question |
|--------|----------|
| Liability | How much personal liability protection is needed? |
| Tax | What is the desired tax treatment? |
| Capital | Will the company raise external investment? |
| Control | How many owners and what governance structure? |
| Compliance | What level of administrative burden is acceptable? |
| Geography | Where will the company operate and be taxed? |
| Exit | What is the intended exit path? |

## Common National Entities

### United States

| Entity | Liability | Tax | Best For |
|--------|-----------|-----|----------|
| Sole Proprietorship | None | Pass-through | Solo founders, low risk, testing ideas |
| Partnership | Joint | Pass-through | Multiple founders, professional services |
| LLC | Limited | Flexible (pass-through or corporate) | Small to medium businesses, asset holding |
| S-Corp | Limited | Pass-through | Owner-operated businesses seeking tax efficiency |
| C-Corp | Limited | Corporate + shareholder tax | Startups raising venture capital, public exits |
| Nonprofit | Limited | Tax-exempt (if approved) | Charitable, educational, social missions |

### United Kingdom

| Entity | Liability | Tax | Best For |
|--------|-----------|-----|----------|
| Sole Trader | None | Income tax | Solo operators, low risk |
| Partnership | Joint | Income tax on partners | Small multi-founder firms |
| LLP | Limited | Transparent / member tax | Professional partnerships |
| Private Limited Company (Ltd) | Limited | Corporation tax | Most trading businesses |
| Public Limited Company (PLC) | Limited | Corporation tax | Publicly traded companies |

### European Union — Representative Examples

| Jurisdiction | Entity | Notes |
|--------------|--------|-------|
| Germany | GmbH | Common private company; mini-GmbH for lower capital |
| France | SAS / SASU | Flexible governance; popular for startups |
| Netherlands | BV | Flexible share structure; holding company friendly |
| Ireland | Private Limited Company | Low corporate tax; EU base |
| Estonia | OÜ | Fully digital setup; e-residency option |

### Asia-Pacific — Representative Examples

| Jurisdiction | Entity | Notes |
|--------------|--------|-------|
| Singapore | Private Limited Company | Pro-business, low tax, strong IP protection |
| Hong Kong | Private Limited Company | Territorial tax system; gateway to mainland China |
| Australia | Proprietary Limited Company (Pty Ltd) | Common for SMEs |
| India | Private Limited Company | Common for startups and subsidiaries |
| Japan | Kabushiki Kaisha (KK) / Godo Kaisha (GK) | KK for public credibility; GK for LLC-like flexibility |

## Selection Algorithm

1. If raising institutional venture capital in the US → **C-Corp (Delaware)**.
2. If small founder-owned business, no external equity → **LLC / Ltd / local equivalent**.
3. If professional partnership → **LLP or local equivalent**.
4. If holding company / IP ownership → **Netherlands BV / Luxembourg S.à r.l. / Singapore**.
5. If public exit planned → **C-Corp / PLC / KK**.
6. If social mission and tax exemption → **Nonprofit / Charity / Foundation**.

## Output Document

The entity selection should be recorded with:

- Selected entity and jurisdiction
- Rationale for each decision factor
- Capital and ownership structure
- Registered agent and address
- Expected annual compliance obligations
`,
    international_holding_structures: `# International Holding Structures

Common multi-jurisdiction corporate structures and when to use them.

## Objectives of an International Structure

- Limit liability for each country operation.
- Optimize capital flow and withholding taxes.
- Protect intellectual property.
- Simplify fundraising and future exits.
- Meet local substance and regulatory requirements.

## Structure 1: Single Home Entity

\`\`\`
[Founders]
    |
[HoldCo in Home Jurisdiction]
    |
[Operating Subsidiaries in each market]
\`\`\`

- **Best for**: Companies operating in a few similar jurisdictions.
- **Pros**: Simple governance, consolidated reporting.
- **Cons**: Less tax optimization, local liabilities may affect group.

## Structure 2: Intermediate Holding Company

\`\`\`
[Founders]
    |
[TopCo in Investor-Friendly Jurisdiction, e.g., Delaware / UK / Singapore]
    |
[Regional HoldCo in Treaty Hub, e.g., Netherlands / Luxembourg / Ireland]
    |
[Operating Subsidiaries in each market]
\`\`\`

- **Best for**: Multi-region operations, institutional investors, dividend repatriation.
- **Pros**: Treaty benefits, centralized IP or treasury, investor familiarity.
- **Cons**: Complexity, substance requirements, anti-hybrid rules.

## Structure 3: IP Holding Structure

\`\`\`
[Founders / Investors]
    |
[HoldCo]
    |--------------------
    |                   |
[OpCo A]          [IP HoldCo]
(Market A)        (License IP to OpCos)
    |
[OpCo B]
(Market B)
\`\`\`

- **Best for**: IP-heavy businesses (software, biotech, media).
- **Pros**: Centralized IP ownership, licensing revenue, tax-efficient R&D.
- **Cons**: Transfer pricing scrutiny, local R&D and substance rules.

## Structure 4: Regional Operating Model

\`\`\`
[Global HoldCo]
    |
[Regional Hubs: Americas / EMEA / APAC]
    |
[Local OpCos and branches]
\`\`\`

- **Best for**: Global sales, local customer support, distributed teams.
- **Pros**: Local presence, compliance, customer trust.
- **Cons**: Multiple incorporations, payroll, and regulatory filings.

## Key Design Decisions

| Decision | Options | Considerations |
|----------|---------|----------------|
| TopCo location | Delaware, UK, Singapore, etc. | Investor preference, future IPO, governance law |
| Intermediate HoldCo | Netherlands, Luxembourg, Ireland | Treaty network, substance, EU directives |
| OpCo form | Branch vs. subsidiary | Liability, tax, local licensing |
| IP ownership | Central vs. local | R&D location, transfer pricing, enforcement |
| Treasury / Cash pooling | Regional vs. global | FX, banking, withholding taxes |

## Anti-Avoidance and Substance

Modern structures must demonstrate real economic activity:

- Directors with decision-making power in the holding jurisdiction.
- Local board meetings and resolutions.
- Employees performing finance, legal, or IP management functions.
- Properly documented intercompany agreements and transfer pricing.

Always obtain local tax and legal advice before implementing a multi-jurisdiction structure.
`,
    jurisdiction_matrix: `# Jurisdiction Matrix

Compare key jurisdictions across factors relevant to company setup.

## Comparison Factors

| Factor | Description |
|--------|-------------|
| Incorporation Cost | One-time and annual fees |
| Corporate Tax Rate | Statutory rate and effective rate |
| Personal Tax on Dividends | Tax treatment for owners |
| Ease of Setup | Time, bureaucracy, remote options |
| Banking Access | Difficulty opening accounts |
| Legal System | Common law vs. civil law; precedent predictability |
| IP Protection | Strength of trademark, patent, copyright enforcement |
| Double Tax Treaties | Network of treaties |
| Workforce Access | Visa rules and labor market |
| Regulatory Burden | Reporting, audit, and compliance requirements |

## Jurisdiction Comparison Table

| Jurisdiction | Corp Tax | Ease | Banking | IP | Treaties | Best For |
|--------------|----------|------|---------|----|----------|----------|
| Delaware, USA | 21% federal + state | High | Moderate | Very strong | Broad | Venture-backed startups, public companies |
| UK | 25% (main rate) | High | Moderate | Strong | Broad | European hub, English-language base |
| Ireland | 12.5% (trading) | High | Moderate | Strong | Broad | EU HQ, IP holding, tech |
| Netherlands | 25.8% (2024) | High | Moderate | Strong | Very broad | Holding companies, EU distribution |
| Luxembourg | Competitive | High | Moderate | Strong | Very broad | Holding, finance, investment funds |
| Singapore | 17% | Very high | Easy | Strong | Broad | Asia HQ, trade, tech |
| Hong Kong | 8.25–16.5% | Very high | Easy | Strong | Growing | Asia gateway, China access |
| UAE (Free Zone) | 0–9% | High | Easy | Moderate | Growing | Regional hub, tax optimization |
| Estonia | 20% (deferred) | Very high | Easy | Moderate | Broad | Digital nomads, EU entity |
| Cayman Islands | 0% | Moderate | Moderate | Moderate | Limited | Investment funds, holding vehicles |

## Selection Guidance

- **Primary operating market**: Incorporate where revenue and customers are concentrated.
- **Tax efficiency**: Use treaty networks and holding structures, not just headline rates.
- **Investor preference**: US VCs often require Delaware C-Corp; EU investors accept local equivalents.
- **IP holding**: Separate IP into a jurisdiction with strong IP law and favorable tax treaties.
- **Regulatory alignment**: Financial services, healthcare, and crypto face sector-specific licensing constraints.

## Multi-Jurisdiction Setup

A typical expansion path:

1. **Home entity** in the primary market.
2. **Operating subsidiaries** in each country with customers or employees.
3. **Holding company** in a jurisdiction optimized for capital and dividends.
4. **IP holding company** where IP is created, licensed, or protected.

Always model transfer pricing, substance requirements, and anti-avoidance rules before finalizing.
`,
  },
  brandIdentity: {
    README: `# 03 — Brand Identity

This section defines the core brand strategy, positioning, personality, tone of voice, and visual direction.

## Files

- \`brand_positioning_template.md\` — Fill-in-the-blanks positioning framework.
- \`visual_identity_brief.md\` — Brief for logo, color, typography, and imagery.
- \`tone_of_voice_guide.md\` — Voice principles and writing rules.
- \`brand_guidelines_checklist.md\` — Checklist for a complete brand book.

## Purpose

A complete brand identity ensures every customer touchpoint communicates a consistent, differentiated, and credible message.
`,
    brand_guidelines_checklist: `# Brand Guidelines Checklist

Use this checklist to ensure the brand book is complete and useful.

## Strategy

- [ ] Brand purpose / mission statement
- [ ] Vision statement
- [ ] Core values
- [ ] Positioning statement
- [ ] Target audience profiles
- [ ] Competitive differentiation
- [ ] Brand promise
- [ ] Brand pillars

## Identity

- [ ] Logo and logo variations
- [ ] Minimum size and clear space
- [ ] Incorrect logo usage
- [ ] Color palette (primary, secondary, neutrals)
- [ ] Color accessibility guidance
- [ ] Typography system
- [ ] Iconography style
- [ ] Imagery and photography style
- [ ] Illustration style
- [ ] Motion and sound principles (if applicable)

## Voice

- [ ] Tone of voice principles
- [ ] Writing style rules
- [ ] Channel-specific tone adjustments
- [ ] Boilerplate and taglines
- [ ] Naming conventions for products and features
- [ ] Inclusive language policy

## Application

- [ ] Business card
- [ ] Letterhead and email signature
- [ ] Website design system
- [ ] Social media templates
- [ ] Presentation templates
- [ ] Document templates
- [ ] Branded merchandise
- [ ] Signage and environmental graphics
- [ ] App and product UI (if applicable)

## Governance

- [ ] Version history
- [ ] Brand owner / contact
- [ ] Approval process for deviations
- [ ] Distribution and access rules
- [ ] Review schedule
- [ ] Asset library location

## Approval

| Role | Name | Approved | Date |
|------|------|----------|------|
| Brand / Marketing Lead | | | |
| Design Lead | | | |
| Executive Sponsor | | | |
`,
    brand_positioning_template: `# Brand Positioning Template

Use this template to define a clear, differentiated position in the market.

## 1. Brand Essence

In one sentence, what does the brand stand for?

> _Example: Stripe — "Increase the GDP of the internet."_

## 2. Target Audience

### Primary Audience

- Role / demographic:
- Pain points:
- Goals:
- Buying triggers:

### Secondary Audience

- Role / demographic:
- Why they matter:

## 3. Market Context

- Category:
- Market size and growth:
- Key trends:
- Regulatory or economic forces:

## 4. Competitive Landscape

| Competitor | Positioning | Strength | Weakness |
|------------|-------------|----------|----------|
| | | | |
| | | | |
| | | | |

## 5. Differentiation

What makes this brand unique and hard to copy?

- **Functional difference**:
- **Emotional difference**:
- **Proof points**:

## 6. Brand Promise

The one thing the brand guarantees to its customers:

> _Example: "We make professional-grade analytics accessible to any team in minutes."_

## 7. Positioning Statement

For **[target audience]**, **[brand name]** is the **[category]** that **[key benefit]**, because **[reason to believe]**.

## 8. Brand Pillars

Three to five attributes that define the brand experience:

1.
2.
3.
4.
5.

## 9. Brand Personality

If the brand were a person, how would it behave?

| Trait | How it shows up |
|-------|-----------------|
| | |
| | |
| | |

## 10. Customer Journey Summary

Map the key stages where brand positioning must be reinforced:

| Stage | Customer Need | Brand Action |
|-------|---------------|--------------|
| Awareness | | |
| Consideration | | |
| Purchase | | |
| Onboarding | | |
| Retention | | |
| Advocacy | | |
`,
    tone_of_voice_guide: `# Tone of Voice Guide

Rules for how the brand communicates in writing and speech.

## 1. Voice Principles

Define 3–5 principles. For each, state what we are and what we are not.

| Principle | We Are | We Are Not |
|-----------|--------|------------|
| Example: Clear | Direct and jargon-free | Overly technical or vague |
| | | |
| | | |
| | | |

## 2. Writing Style

### Grammar and Mechanics

- Sentence length: short (≤20 words) / medium / varied
- Active vs. passive voice preference:
- First, second, or third person?
- Oxford comma:
- Capitalization style: sentence case / title case / all lowercase
- Date, time, and number formats:

### Vocabulary

- Words we use:
- Words we avoid:
- Industry terms: explain or assume?

### Inclusive Language

- Use gender-neutral terms.
- Avoid idioms that do not translate.
- Describe people with dignity and accuracy.
- Use accessibility best practices (alt text, plain language).

## 3. Channel Voice

| Channel | Tone Adjustment | Example |
|---------|-----------------|---------|
| Website | | |
| Email marketing | | |
| Social media | | |
| Support chat | | |
| Press release | | |
| Internal memo | | |

## 4. Messaging Do's and Don'ts

### Do

- Lead with the customer benefit.
- Use concrete examples and data.
- Be honest about limitations.
- Proofread before publishing.

### Don't

- Use hyperbole or unsupported claims.
- Criticize competitors directly.
- Use jargon without explanation.
- Write long blocks without headings or lists.

## 5. Message Architecture

### Boilerplate

A 50–75 word company description for press, partners, and directories.

> _[Write boilerplate here]_

### Tagline Options

1.
2.
3.

### Elevator Pitch

30-second spoken summary.

> _[Write pitch here]_

## 6. Review Checklist

Before publishing any content:

- [ ] Does it sound like the brand?
- [ ] Is the customer benefit clear?
- [ ] Is the language inclusive and accessible?
- [ ] Are claims accurate and defensible?
- [ ] Is the call to action clear?
`,
    visual_identity_brief: `# Visual Identity Brief

A template for briefing designers on logo, color, typography, and imagery.

## 1. Brand Context

- Brand name:
- Industry:
- Target audience:
- Positioning summary:
- Tone / personality:

## 2. Logo Requirements

- **Wordmark, symbol, or combination?**
- **Primary use cases**: digital, print, app icon, signage, merchandise
- **Style direction**: minimal, bold, playful, premium, technical
- **Must-include elements**:
- **Must-avoid elements**:
- **Competitor logos to avoid resembling**:

## 3. Color Palette

| Role | Color Name | HEX | Usage |
|------|------------|-----|-------|
| Primary | | | |
| Secondary | | | |
| Accent | | | |
| Neutral | | | |
| Dark | | | |
| Light | | | |

### Color Usage Rules

- Minimum contrast ratios for accessibility:
- Background combinations allowed / prohibited:
- When to use accent color:

## 4. Typography

| Role | Font Family | Weights | Usage |
|------|-------------|---------|-------|
| Headline | | | |
| Body | | | |
| Accent / Mono | | | |

### Typography Rules

- Base size and line height:
- Heading scale:
- Maximum line lengths for readability:

## 5. Imagery Style

- Photography style: candid, staged, abstract, documentary
- Illustration style: flat, 3D, line art, isometric
- Icon style: outlined, filled, duotone
- Subject matter and mood:
- Diversity and inclusion expectations:

## 6. Motion and Interaction (Digital)

- Animation style: subtle, playful, precise
- Transitions and micro-interactions:
- Sound or haptic considerations:

## 7. Application Examples

Required mockups:

- [ ] Business card
- [ ] Letterhead
- [ ] Website homepage
- [ ] Mobile app icon
- [ ] Social media profile
- [ ] Presentation template
- [ ] Branded merchandise

## 8. Deliverables

- [ ] Logo files (SVG, PNG, EPS)
- [ ] Color palette specification
- [ ] Font files and license
- [ ] Icon set
- [ ] Photography and illustration library
- [ ] Brand guidelines PDF
- [ ] Design system / component library
`,
  },
  sops: {
    README: `# 04 — Standard Operating Procedures (SOPs)

This section contains reusable SOP templates and ready-to-adapt procedures for every major business function.

## Files

- \`sop_template.md\` — Standard SOP format to use for all procedures.
- \`sop_index.md\` — Master list of all SOPs by function and owner.
- \`hr/\` — Hiring, onboarding, offboarding, performance, leave.
- \`finance/\` — Invoicing, payroll, expense reimbursement, month-end close.
- \`operations/\` — Procurement, vendor management, quality control.
- \`marketing/\` — Campaign planning, content approval, social media.
- \`legal/\` — Contract review, data subject requests, IP filings.
- \`it/\` — Access control, incident response, software procurement.

## Governance

All SOPs follow the document-control policy in \`06-governance/\` and are reviewed on the cycle defined there.
`,
    finance: {
      'sop-fin-001-invoicing': `# SOP-FIN-001: Invoicing and Collections

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-FIN-001 |
| Title | Invoicing and Collections |
| Function | Finance |
| Owner | Finance Manager |
| Reviewer | CFO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To ensure accurate, timely invoicing and effective collection of receivables.

## 2. Scope

Applies to all customer invoicing and payment collection activities.

## 3. Roles and Responsibilities

| Role | Responsibility |
|------|----------------|
| Sales / Account Management | Confirms deliverables and billing contacts |
| Finance | Generates invoices, applies payments, manages collections |
| Legal | Handles disputes and escalation |

## 4. Procedure

1. **Trigger**
   - Sales confirms milestone, shipment, or subscription renewal ready to bill.
   - Billing data is entered or synced to the accounting system.

2. **Invoice Generation**
   - Finance verifies pricing, tax, and purchase order references.
   - Invoice is generated and sent to customer within one business day.

3. **Payment Tracking**
   - Finance monitors due dates and applies cash receipts.
   - Unmatched payments are investigated within two business days.

4. **Collections**
   - Automated reminders sent at defined intervals.
   - Overdue invoices are escalated to Account Manager at 15 days.
   - Legal engaged at 60+ days or per agreed threshold.

5. **Reconciliation**
   - Monthly AR aging reviewed.
   - Bad debt provisions assessed.

## 5. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Invoices issued within SLA | ≥95% | Monthly |
| DSO (Days Sales Outstanding) | ≤30 days | Monthly |
| Bad debt ratio | ≤1% | Quarterly |
`,
      'sop-fin-002-expenses': `# SOP-FIN-002: Expense Reimbursement

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-FIN-002 |
| Title | Expense Reimbursement |
| Function | Finance |
| Owner | Finance Manager |
| Reviewer | CFO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To reimburse employees for approved business expenses promptly and accurately.

## 2. Scope

Applies to all employees incurring business expenses.

## 3. Procedure

1. **Expense Incurred**
   - Employee pays for an eligible business expense.
   - Original receipt is retained.

2. **Submission**
   - Employee submits expense report within 30 days.
   - Report includes date, amount, category, business purpose, and receipt.

3. **Approval**
   - Manager reviews and approves within five business days.
   - Finance verifies policy compliance and receipt validity.

4. **Reimbursement**
   - Approved expenses are paid in the next payroll cycle or expense run.
   - Employee receives payment confirmation.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Reimbursement cycle time | ≤10 business days | Monthly |
| Rejected expense rate | ≤5% | Monthly |
`,
      'sop-fin-003-payroll': `# SOP-FIN-003: Payroll Processing

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-FIN-003 |
| Title | Payroll Processing |
| Function | Finance |
| Owner | Payroll Manager |
| Reviewer | CFO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To pay employees accurately and on time while meeting tax and reporting obligations.

## 2. Scope

Applies to all employees on company payroll.

## 3. Procedure

1. **Data Collection**
   - HR provides new hires, terminations, and changes.
   - Managers approve time sheets and variable pay inputs.

2. **Validation**
   - Payroll reviews data for completeness and accuracy.
   - Tax, benefits, and garnishment changes applied.

3. **Processing**
   - Payroll run executed in payroll system.
   - Preliminary reports reviewed and approved by Finance.

4. **Payment and Posting**
   - Salaries disbursed on scheduled pay date.
   - Accounting entries posted.

5. **Reporting**
   - Statutory filings submitted on time.
   - Payslips distributed to employees.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| On-time payroll | 100% | Per pay run |
| Payroll accuracy | ≥99% | Quarterly |
| Statutory filing compliance | 100% | Per filing |
`,
      'sop-fin-004-month-end': `# SOP-FIN-004: Month-End Close

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-FIN-004 |
| Title | Month-End Close |
| Function | Finance |
| Owner | Controller |
| Reviewer | CFO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To produce accurate and timely monthly financial statements.

## 2. Scope

Applies to all entities included in the company's consolidated reporting.

## 3. Procedure

1. **Pre-Close Preparation**
   - Confirm calendar and task assignments.
   - Freeze subledger entries.

2. **Revenue Recognition**
   - Review contracts and recognize revenue according to policy.
   - Reconcile AR and deferred revenue.

3. **Expense Accruals**
   - Record accrued expenses and prepaid amortization.
   - Reconcile AP and credit cards.

4. **Payroll and Headcount**
   - Confirm payroll costs allocated by department and entity.
   - Reconcile headcount changes.

5. **Fixed Assets and Depreciation**
   - Record additions, disposals, and depreciation.

6. **Intercompany Reconciliation**
   - Reconcile intercompany balances and eliminate in consolidation.

7. **Review and Approval**
   - Controller reviews draft financials.
   - CFO approves final statements.

8. **Distribution and Filing**
   - Distribute package to leadership.
   - Archive working papers.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Close cycle time | ≤10 business days | Monthly |
| Reconciliation exceptions | 0 unresolved | Monthly |
`,
      'sop-fin-005-procurement': `# SOP-FIN-005: Purchase Order and Procurement

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-FIN-005 |
| Title | Purchase Order and Procurement |
| Function | Finance |
| Owner | Procurement Lead |
| Reviewer | CFO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To control spending, ensure proper approvals, and maintain reliable vendor relationships.

## 2. Scope

Applies to all purchases above a defined threshold. Low-value purchases may follow a separate policy.

## 3. Procedure

1. **Request**
   - Requester completes purchase requisition with justification, budget code, and vendor.

2. **Approval**
   - Manager reviews need and budget.
   - Finance approves if amount exceeds delegation threshold.

3. **Purchase Order**
   - Procurement issues a numbered purchase order to vendor.
   - Order is logged in the procurement system.

4. **Receipt**
   - Goods/services received by requester.
   - Receipt recorded and invoice matched to PO.

5. **Payment**
   - Three-way match (PO, receipt, invoice) verified.
   - Invoice approved for payment per terms.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| PO coverage rate | ≥90% of spend | Monthly |
| Three-way match exceptions | ≤5% | Monthly |
| Purchase cycle time | ≤7 days | Monthly |
`,
    },
    hr: {
      'sop-hr-001-recruitment': `# SOP-HR-001: Recruitment and Hiring

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-HR-001 |
| Title | Recruitment and Hiring |
| Function | Human Resources |
| Owner | HR Manager |
| Reviewer | Head of People |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To ensure consistent, fair, and compliant hiring practices that attract qualified candidates and provide a positive candidate experience.

## 2. Scope

Applies to all full-time, part-time, and fixed-term employee hires. Does not apply to contractors or interns unless converted.

## 3. Definitions

| Term | Definition |
|------|------------|
| Requisition | Approved request to fill a position |
| Hiring Manager | Department leader requesting the hire |
| Offer Letter | Formal employment offer document |

## 4. Roles and Responsibilities

| Role | Responsibility |
|------|----------------|
| Hiring Manager | Defines role, interviews, makes recommendation |
| HR | Posts job, screens, coordinates, extends offer |
| Finance | Confirms budget and compensation band |
| Legal | Reviews offer letters and background-check policy |

## 5. Procedure

1. **Requisition**
   - Hiring Manager submits a requisition form with role title, level, responsibilities, and budget.
   - Finance confirms budget availability.
   - HR approves requisition and assigns a recruiter.

2. **Sourcing**
   - HR drafts job description using the approved template.
   - Job is posted on company careers page, relevant job boards, and internal channels.
   - Recruiter sources passive candidates as needed.

3. **Screening**
   - Recruiter reviews applications against minimum qualifications.
   - Shortlisted candidates complete a phone or video screen.
   - Top candidates advance to Hiring Manager interviews.

4. **Interviewing**
   - Hiring Manager schedules structured interviews with the interview panel.
   - Panel uses scorecards aligned to competencies.
   - HR coordinates reference checks for final candidates.

5. **Decision**
   - Interview panel submits scorecards.
   - Hiring Manager and HR meet to select final candidate.
   - HR verifies right-to-work and prepares background check consent.

6. **Offer**
   - HR drafts offer letter within approved compensation band.
   - Legal reviews offer letter if non-standard terms requested.
   - Hiring Manager or HR extends offer verbally, followed by written offer.

7. **Pre-boarding**
   - Candidate accepts offer and returns signed documents.
   - HR initiates background check and collects required documentation.
   - IT is notified to prepare equipment and accounts.

## 6. Required Inputs and Outputs

| Input | Source | Output | Destination |
|-------|--------|--------|-------------|
| Requisition form | Hiring Manager | Approved requisition | HR |
| Job description | HR | Published job posting | Public channels |
| Interview scorecards | Panel | Hiring recommendation | Hiring Manager / HR |
| Offer approval | HR / Finance | Signed offer letter | New hire file |

## 7. Tools, Systems, and References

- Applicant Tracking System (ATS)
- HR information system (HRIS)
- Offer letter template
- Background check provider
- Related SOP: SOP-HR-002 New Employee Onboarding

## 8. Exceptions and Escalation

- Offers above approved band require CFO or CEO approval.
- Any deviation from standard background-check policy requires Legal approval.
- Disputes about candidate selection are escalated to Head of People.

## 9. Metrics and KPIs

| Metric | Target | Frequency |
|--------|--------|-----------|
| Time to fill | ≤45 days | Monthly |
| Offer acceptance rate | ≥85% | Quarterly |
| Candidate satisfaction | ≥4.0 / 5 | Quarterly |
`,
      'sop-hr-002-onboarding': `# SOP-HR-002: New Employee Onboarding

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-HR-002 |
| Title | New Employee Onboarding |
| Function | Human Resources |
| Owner | HR Manager |
| Reviewer | Head of People |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To integrate new employees into the company, equip them with necessary tools and knowledge, and accelerate time-to-productivity.

## 2. Scope

Applies to all new hires converting from offer to active employee status.

## 3. Definitions

| Term | Definition |
|------|------------|
| Onboarding plan | 30-60-90 day roadmap for new hire |
| Buddy | Peer assigned to help new hire navigate the company |

## 4. Roles and Responsibilities

| Role | Responsibility |
|------|----------------|
| HR | Coordinates onboarding, paperwork, and orientation |
| IT | Provisions accounts, equipment, and access |
| Hiring Manager | Sets role expectations and onboarding plan |
| Buddy | Provides informal support and cultural guidance |
| Payroll | Adds employee to payroll system |

## 5. Procedure

1. **Before Day 1**
   - HR confirms start date and sends welcome email.
   - IT prepares laptop, peripherals, and system accounts.
   - Hiring Manager drafts onboarding plan and schedules first-week meetings.
   - Buddy is assigned and introduced via email.

2. **Day 1**
   - HR conducts orientation: company overview, values, policies, benefits.
   - Employee completes required documents and direct deposit setup.
   - IT distributes equipment and verifies access.
   - Hiring Manager hosts role-specific welcome meeting.

3. **Week 1**
   - New hire meets key stakeholders.
   - Completes mandatory training (security, compliance, anti-harassment).
   - Reviews role responsibilities and first-month goals.

4. **30-Day Check-In**
   - Hiring Manager reviews progress against onboarding plan.
   - HR checks in on experience and resolves issues.

5. **60-Day Check-In**
   - Review deeper role goals and cross-functional projects.
   - Identify development needs.

6. **90-Day Check-In**
   - Formal probation / onboarding completion review.
   - Set ongoing performance goals.

## 6. Required Inputs and Outputs

| Input | Source | Output | Destination |
|-------|--------|--------|-------------|
| Signed offer | HR | Active employee record | HRIS |
| Onboarding plan | Hiring Manager | Completed checklist | HR |
| Training records | LMS | Compliance completion | HR / Compliance |

## 7. Tools, Systems, and References

- HRIS
- Learning management system (LMS)
- IT asset tracking
- Onboarding checklist template

## 8. Exceptions and Escalation

- Remote hires: IT ships equipment and conducts setup via video call.
- Access issues: escalate to IT within 4 hours of report.

## 9. Metrics and KPIs

| Metric | Target | Frequency |
|--------|--------|-----------|
| Onboarding completion | 100% by day 30 | Monthly |
| New hire 90-day retention | ≥90% | Quarterly |
| Time to productivity | Defined per role | Quarterly |
`,
      'sop-hr-003-offboarding': `# SOP-HR-003: Employee Offboarding

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-HR-003 |
| Title | Employee Offboarding |
| Function | Human Resources |
| Owner | HR Manager |
| Reviewer | Head of People |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To manage employee exits in a respectful, compliant, and secure manner while protecting company assets and knowledge.

## 2. Scope

Applies to all voluntary and involuntary terminations, resignations, and retirements.

## 3. Roles and Responsibilities

| Role | Responsibility |
|------|----------------|
| HR | Coordinates exit process, paperwork, and final pay |
| Manager | Communicates decision, plans handover |
| IT | Revokes access and recovers equipment |
| Finance | Processes final payment and expense settlements |
| Legal | Advises on sensitive or high-risk exits |

## 4. Procedure

1. **Notice or Decision**
   - Resignation: employee submits written notice.
   - Termination: manager and HR align on rationale and timing.

2. **Exit Communication**
   - Manager and HR notify relevant stakeholders.
   - Communicate to team in a respectful and professional manner.

3. **Handover Planning**
   - Manager documents ongoing work and assigns transitions.
   - Employee completes knowledge transfer sessions.

4. **Access and Asset Recovery**
   - IT disables system access on last working day.
   - Employee returns laptop, badge, keys, and any company property.
   - Finance cancels corporate cards.

5. **Final Payroll and Benefits**
   - HR calculates final pay, unused leave, and entitlements.
   - Benefits termination and COBRA / local equivalent notifications sent.

6. **Exit Interview**
   - HR conducts voluntary exit interview.
   - Feedback is recorded and reported to leadership in aggregate.

7. **Archive and Close**
   - Employee file archived per retention policy.
   - Offboarding checklist signed off by HR, IT, and Manager.

## 5. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Access revoked by EOD | 100% | Per exit |
| Exit interview completion | ≥70% voluntary exits | Monthly |
`,
      'sop-hr-004-performance': `# SOP-HR-004: Performance Management

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-HR-004 |
| Title | Performance Management |
| Function | Human Resources |
| Owner | HR Manager |
| Reviewer | Head of People |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To align individual performance with company goals, provide regular feedback, and support employee development.

## 2. Scope

Applies to all employees.

## 3. Procedure

1. **Goal Setting**
   - Managers and employees define SMART goals within 30 days of period start.
   - Goals are documented in the performance system.

2. **Ongoing Feedback**
   - Managers provide regular 1:1 coaching.
   - Employees and peers give feedback as situations occur.

3. **Mid-Year Review**
   - Manager and employee review progress against goals.
   - Adjust goals if priorities have changed.

4. **Annual Review**
   - Employee completes self-assessment.
   - Manager evaluates performance and behavior against competencies.
   - Rating and development plan are discussed.

5. **Development Planning**
   - Identify training, stretch assignments, or mentorship needs.
   - Record development actions.

6. **Calibration and Compensation**
   - Leadership calibrates ratings across teams.
   - Compensation adjustments are processed according to budget and policy.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Goal completion rate | ≥80% | Annual |
| Review completion | 100% | Annual |
| Employee development plans | ≥90% | Annual |
`,
      'sop-hr-005-leave': `# SOP-HR-005: Leave and Time-Off Requests

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-HR-005 |
| Title | Leave and Time-Off Requests |
| Function | Human Resources |
| Owner | HR Manager |
| Reviewer | Head of People |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To manage employee time-off requests consistently and ensure adequate coverage.

## 2. Scope

Applies to all paid and unpaid leave types as defined by local policy and law.

## 3. Procedure

1. **Request**
   - Employee submits leave request through HRIS or approved form.
   - Request includes dates, type of leave, and coverage plan.

2. **Approval**
   - Manager reviews request for coverage and workload impact.
   - Manager approves or declines within two business days.
   - HR notified for extended leaves or statutory entitlements.

3. **Tracking**
   - Approved leave is recorded in the leave balance system.
   - Team calendar is updated.

4. **Return**
   - Employee confirms return date.
   - Manager conducts return-to-work check-in for long-term leaves.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Leave request response time | ≤2 business days | Monthly |
| Unused leave compliance | Per local law | Annual |
`,
    },
    it: {
      'sop-it-001-access-control': `# SOP-IT-001: User Access Control and Provisioning

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-IT-001 |
| Title | User Access Control and Provisioning |
| Function | IT / Security |
| Owner | IT Manager |
| Reviewer | CISO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To grant, modify, and revoke system access based on role and least privilege.

## 2. Scope

Applies to all user accounts, systems, applications, and physical access.

## 3. Procedure

1. **Access Request**
   - Manager submits access request form with justification and role.

2. **Approval**
   - System owner or security approves based on role matrix.
   - High-risk access requires additional approval.

3. **Provisioning**
   - IT creates account and assigns appropriate groups and permissions.
   - Multi-factor authentication enforced where available.

4. **Review**
   - Access reviewed quarterly for sensitive systems.
   - Managers certify direct reports' access annually.

5. **Modification**
   - Role changes trigger access update request.
   - IT adjusts permissions within one business day.

6. **Deprovisioning**
   - HR notifies IT of termination.
   - Access revoked on last working day or immediately for cause.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Access provisioning time | ≤1 business day | Monthly |
| Deprovisioning completion by EOD | 100% | Per exit |
| Access certification completion | 100% | Annual |
`,
      'sop-it-002-software-procurement': `# SOP-IT-002: Software Procurement and Approval

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-IT-002 |
| Title | Software Procurement and Approval |
| Function | IT |
| Owner | IT Manager |
| Reviewer | CISO / CFO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To manage software purchases, ensure security review, and control shadow IT.

## 2. Scope

Applies to all SaaS, on-premise software, and tools used for business.

## 3. Procedure

1. **Request**
   - Requester submits software request with business need, users, and budget.

2. **Security Review**
   - IT / Security assesses data handling, SSO, MFA, and compliance.
   - High-risk vendors require security questionnaire or DP.

3. **Budget Approval**
   - Finance confirms budget and pricing.
   - Procurement reviews contract terms.

4. **Procurement**
   - IT or Procurement purchases license.
   - Tool added to software inventory.

5. **Deployment**
   - IT provisions users and integrates with identity provider if applicable.
   - Requester confirms functionality.

6. **Renewal and Retirement**
   - Track renewal dates.
   - Cancel unused licenses.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Security review cycle time | ≤3 business days | Monthly |
| Unapproved software detected | ≤2 per quarter | Quarterly |
`,
      'sop-it-003-incident-response': `# SOP-IT-003: Incident Response and Escalation

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-IT-003 |
| Title | Incident Response and Escalation |
| Function | IT / Security |
| Owner | CISO |
| Reviewer | CTO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To respond to IT security incidents quickly and minimize business impact.

## 2. Scope

Applies to all cyber security incidents, outages caused by security events, and suspected breaches.

## 3. Procedure

1. **Detection**
   - Monitor alerts, reports, and anomalies.
   - Log incident in ticket system.

2. **Initial Assessment**
   - Classify severity: Low, Medium, High, Critical.
   - Notify incident response team.

3. **Containment**
   - Isolate affected systems.
   - Preserve logs and evidence.

4. **Eradication**
   - Remove threat actor or root cause.
   - Patch vulnerabilities.

5. **Recovery**
   - Restore systems from known-good backups.
   - Validate integrity before returning to production.

6. **Communication**
   - Notify leadership and affected users as appropriate.
   - Coordinate with Legal for regulatory notifications.

7. **Post-Incident Review**
   - Write incident report.
   - Update controls and SOPs.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Mean time to detect | ≤4 hours | Per incident |
| Mean time to respond | ≤1 hour critical | Per incident |
`,
      'sop-it-004-backup': `# SOP-IT-004: Backup and Disaster Recovery

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-IT-004 |
| Title | Backup and Disaster Recovery |
| Function | IT |
| Owner | IT Manager |
| Reviewer | CTO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To ensure data is backed up and can be recovered in case of loss or disaster.

## 2. Scope

Applies to all critical business data, systems, and configurations.

## 3. Procedure

1. **Backup Schedule**
   - Define RPO and RTO for each system.
   - Configure automated backups according to schedule.

2. **Backup Verification**
   - Monitor backup jobs daily.
   - Investigate and re-run failed backups.

3. **Offsite / Cloud Storage**
   - Ensure backups are stored in geographically separated location.
   - Encrypt data in transit and at rest.

4. **Retention**
   - Apply retention policy per data classification.
   - Test restore procedures quarterly.

5. **Disaster Recovery**
   - Activate disaster recovery plan when primary systems fail.
   - Follow runbook to restore services in priority order.
   - Communicate status to stakeholders.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Backup success rate | ≥99% | Weekly |
| Restore tests passed | 100% | Quarterly |
| RTO achieved | Per system SLA | Annual DR test |
`,
      'sop-it-005-asset-management': `# SOP-IT-005: IT Asset Management

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-IT-005 |
| Title | IT Asset Management |
| Function | IT |
| Owner | IT Manager |
| Reviewer | CFO / CTO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To track, maintain, and dispose of IT assets throughout their lifecycle.

## 2. Scope

Applies to all company-owned hardware, software licenses, and mobile devices.

## 3. Procedure

1. **Procurement**
   - Asset request approved per IT procurement SOP.
   - Asset received and entered into asset register.

2. **Deployment**
   - Asset configured and assigned to user.
   - User signs acceptance and usage policy.

3. **Maintenance**
   - Track warranty, support, and refresh cycle.
   - Apply patches and updates.

4. **Movement**
   - Record transfers between users or locations.
   - Update asset register.

5. **Disposal**
   - Wipe data securely before disposal or reuse.
   - Dispose through approved e-waste vendor.
   - Remove from asset register.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Asset register accuracy | ≥98% | Quarterly |
| Devices wiped before disposal | 100% | Per disposal |
`,
    },
    legal: {
      'sop-leg-001-contracts': `# SOP-LEG-001: Contract Review and Approval

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-LEG-001 |
| Title | Contract Review and Approval |
| Function | Legal |
| Owner | Legal Counsel |
| Reviewer | General Counsel |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To ensure contracts are reviewed, negotiated, and approved consistently.

## 2. Scope

Applies to all contracts, amendments, and material terms of business.

## 3. Procedure

1. **Intake**
   - Requester submits contract request with business context, term, and value.

2. **Triage**
   - Legal assigns risk tier and reviewer.
   - Standard low-value contracts may use pre-approved templates.

3. **Review**
   - Legal reviews terms, liability, indemnity, IP, termination, and compliance.
   - Finance reviews payment terms.
   - Security reviews data protection clauses if applicable.

4. **Negotiation**
   - Legal or authorized negotiator manages redlines.
   - Significant concessions require executive approval.

5. **Approval**
   - Contract approved per delegation matrix.

6. **Execution and Storage**
   - Contract signed by authorized signatories.
   - Original stored in contract repository.
   - Key terms extracted and tracked.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Contract review cycle time | ≤5 business days standard | Monthly |
| Standard template usage | ≥70% | Quarterly |
`,
      'sop-leg-002-dsar': `# SOP-LEG-002: Data Subject Access Request Handling

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-LEG-002 |
| Title | Data Subject Access Request Handling |
| Function | Legal / Privacy |
| Owner | Data Protection Officer |
| Reviewer | General Counsel |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To respond to data subject rights requests in compliance with privacy laws.

## 2. Scope

Applies to requests for access, deletion, correction, portability, and objection.

## 3. Procedure

1. **Receipt**
   - Request received via privacy inbox or designated channel.
   - Acknowledge receipt within statutory timeframe (e.g., 24–72 hours).

2. **Verification**
   - Verify requester identity to prevent unauthorized disclosure.

3. **Scoping**
   - Determine scope of request and applicable law.
   - Identify systems and data involved.

4. **Collection**
   - Gather responsive data from relevant systems.
   - Exclude legally privileged or third-party information.

5. **Review and Redaction**
   - Legal reviews for exemptions or conflicts.
   - Redact where necessary.

6. **Response**
   - Provide response within statutory deadline.
   - Document action taken.

7. **Close and Report**
   - Close request in privacy register.
   - Report metrics quarterly.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| On-time response | 100% | Quarterly |
| Identity verification failures | ≤1% | Quarterly |
`,
      'sop-leg-003-incident': `# SOP-LEG-003: Incident and Breach Notification

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-LEG-003 |
| Title | Incident and Breach Notification |
| Function | Legal / Security |
| Owner | Data Protection Officer |
| Reviewer | General Counsel / CISO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To detect, assess, and respond to security and privacy incidents.

## 2. Scope

Applies to all suspected or confirmed security incidents and personal data breaches.

## 3. Procedure

1. **Detection and Reporting**
   - Incidents reported to security@ or privacy@ inbox.
   - Automated alerts monitored by IT Security.

2. **Triage**
   - Assess severity and impact.
   - Assemble incident response team.

3. **Containment**
   - Take immediate steps to limit harm.
   - Preserve evidence.

4. **Investigation**
   - Determine root cause and scope.
   - Identify affected data and individuals.

5. **Notification Decision**
   - Legal determines notification obligations under applicable law.
   - Notify regulators and affected individuals within statutory deadlines.

6. **Remediation**
   - Fix root cause and strengthen controls.

7. **Post-Incident Review**
   - Document lessons learned.
   - Update SOPs and controls as needed.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Incident response time | ≤2 hours critical | Per incident |
| Breach notification compliance | 100% | Per incident |
`,
      'sop-leg-004-ip': `# SOP-LEG-004: Intellectual Property Filings

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-LEG-004 |
| Title | Intellectual Property Filings |
| Function | Legal / IP |
| Owner | IP Counsel |
| Reviewer | General Counsel |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To protect company intellectual property through timely filings and registrations.

## 2. Scope

Applies to trademarks, patents, copyrights, and domain names.

## 3. Procedure

1. **Identification**
   - R&D, Marketing, or Product flags new IP for review.
   - IP Counsel evaluates protectability.

2. **Search and Clearance**
   - Conduct clearance searches for trademarks and patentability searches for inventions.

3. **Filing Decision**
   - Decide on filing based on business value, cost, and risk.
   - Obtain budget approval.

4. **Filing**
   - Prepare and file application in relevant jurisdictions.
   - Record filing details in IP register.

5. **Prosecution and Maintenance**
   - Respond to office actions.
   - Pay renewal or maintenance fees.
   - Monitor deadlines.

6. **Enforcement**
   - Monitor for infringement.
   - Take action through cease-and-desist, opposition, or litigation as appropriate.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Renewal deadlines met | 100% | Quarterly |
| IP register accuracy | ≥98% | Annual |
`,
      'sop-leg-005-whistleblower': `# SOP-LEG-005: Whistleblower Complaint Handling

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-LEG-005 |
| Title | Whistleblower Complaint Handling |
| Function | Legal / Compliance |
| Owner | Compliance Officer |
| Reviewer | General Counsel |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To receive, investigate, and resolve whistleblower reports confidentially and fairly.

## 2. Scope

Applies to reports of illegal, unethical, or unsafe conduct.

## 3. Procedure

1. **Receipt**
   - Reports received via hotline, email, or designated channel.
   - Acknowledge receipt without disclosing identity.

2. **Triage**
   - Compliance Officer reviews report and determines severity.
   - Engage Legal, HR, or external counsel as needed.

3. **Investigation**
   - Appoint impartial investigator.
   - Preserve confidentiality and protect against retaliation.
   - Gather evidence and interview relevant parties.

4. **Resolution**
   - Determine findings and recommended actions.
   - Implement corrective or disciplinary measures.

5. **Feedback**
   - Provide outcome to reporter where legally permissible.
   - Maintain records securely.

6. **Reporting**
   - Report aggregated statistics to board or audit committee.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Reports acknowledged within 48 hours | 100% | Quarterly |
| Retaliation complaints | 0 | Quarterly |
`,
    },
    marketing: {
      'sop-mkt-001-campaigns': `# SOP-MKT-001: Campaign Planning and Launch

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-MKT-001 |
| Title | Campaign Planning and Launch |
| Function | Marketing |
| Owner | Marketing Manager |
| Reviewer | CMO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To plan, execute, and measure marketing campaigns consistently.

## 2. Scope

Applies to product launches, demand-generation campaigns, brand campaigns, and events.

## 3. Procedure

1. **Objective Setting**
   - Define campaign goal, audience, budget, and KPIs.
   - Align with sales and product teams.

2. **Strategy and Messaging**
   - Develop campaign theme and messaging.
   - Ensure brand guidelines compliance.

3. **Asset Production**
   - Create copy, visuals, landing pages, emails, and ads.
   - Route through content approval process.

4. **Channel Plan**
   - Select channels and build calendar.
   - Set up tracking and attribution.

5. **Launch**
   - Execute campaign according to plan.
   - Monitor performance daily in first week.

6. **Review**
   - Measure against KPIs.
   - Document learnings and optimize follow-up.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Campaign ROI | ≥3:1 | Per campaign |
| Lead quality score | ≥70 | Per campaign |
`,
      'sop-mkt-002-content': `# SOP-MKT-002: Content Approval and Publishing

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-MKT-002 |
| Title | Content Approval and Publishing |
| Function | Marketing |
| Owner | Content Lead |
| Reviewer | CMO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To ensure all published content is accurate, on-brand, and legally safe.

## 2. Scope

Applies to blog posts, whitepapers, videos, web pages, and external communications.

## 3. Procedure

1. **Brief**
   - Content owner completes brief with audience, message, and channel.

2. **Draft**
   - Writer or creator produces draft following tone-of-voice guidelines.

3. **Review**
   - Editor reviews for quality, accuracy, and brand alignment.
   - Legal or compliance reviews regulated content.
   - Subject matter expert verifies technical accuracy.

4. **Approve**
   - Approver signs off in workflow tool.

5. **Publish**
   - Content is scheduled or published.
   - Distribution plan activated.

6. **Measure**
   - Track engagement and update or retire outdated content.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Content review cycle time | ≤5 business days | Monthly |
| Published errors / retractions | ≤1 per quarter | Quarterly |
`,
      'sop-mkt-003-social': `# SOP-MKT-003: Social Media Management

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-MKT-003 |
| Title | Social Media Management |
| Function | Marketing |
| Owner | Social Media Manager |
| Reviewer | CMO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To manage social media channels consistently, safely, and effectively.

## 2. Scope

Applies to all company-managed social media accounts.

## 3. Procedure

1. **Planning**
   - Maintain a content calendar aligned to campaigns and brand pillars.
   - Define posting frequency per channel.

2. **Creation**
   - Create posts following tone-of-voice and visual guidelines.
   - Use approved hashtags and handles.

3. **Approval**
   - Posts reviewed by content lead or designated approver.
   - Sensitive topics require additional review.

4. **Publishing**
   - Schedule posts using approved tool.
   - Monitor for errors or engagement immediately after posting.

5. **Community Management**
   - Respond to comments and messages within SLA.
   - Escalate negative or legal issues to leadership.

6. **Reporting**
   - Track reach, engagement, and sentiment.
   - Report monthly to marketing leadership.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Response time to messages | ≤4 hours | Weekly |
| Engagement rate | Channel benchmark | Monthly |
`,
      'sop-mkt-004-events': `# SOP-MKT-004: Event Planning and Execution

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-MKT-004 |
| Title | Event Planning and Execution |
| Function | Marketing |
| Owner | Events Manager |
| Reviewer | CMO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To plan and execute events that achieve marketing and business objectives.

## 2. Scope

Applies to webinars, trade shows, conferences, and company-hosted events.

## 3. Procedure

1. **Objective and Budget**
   - Define event goals, audience, and budget.
   - Secure budget approval.

2. **Planning**
   - Select venue or platform.
   - Build run-of-show and timeline.
   - Identify speakers and sponsors.

3. **Promotion**
   - Create registration page and promotional assets.
   - Execute email and social promotion.

4. **Execution**
   - Conduct rehearsals.
   - Manage logistics, AV, and staffing.
   - Capture leads and content.

5. **Follow-Up**
   - Thank attendees and share materials.
   - Score and distribute leads to sales.

6. **Review**
   - Measure against goals and budget.
   - Document lessons learned.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Attendee satisfaction | ≥4.0 / 5 | Per event |
| Pipeline generated | Per target | Per event |
`,
      'sop-mkt-005-brand-assets': `# SOP-MKT-005: Brand Asset Requests

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-MKT-005 |
| Title | Brand Asset Requests |
| Function | Marketing |
| Owner | Brand Manager |
| Reviewer | CMO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To manage requests for brand assets and ensure consistent usage.

## 2. Scope

Applies to logos, templates, photography, design files, and brand guidelines.

## 3. Procedure

1. **Request**
   - Internal team submits asset request with use case and deadline.

2. **Triage**
   - Brand Manager evaluates request and priority.
   - Directs requester to self-service library if asset already exists.

3. **Creation or Fulfillment**
   - Design team creates or adapts asset.
   - Asset follows brand guidelines.

4. **Approval**
   - Brand Manager approves final asset.

5. **Distribution**
   - Asset delivered with usage instructions.
   - Asset added to library if reusable.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Request fulfillment time | ≤5 business days | Monthly |
| Brand guideline violations | ≤2 | Quarterly |
`,
    },
    operations: {
      'sop-ops-001-vendor-management': `# SOP-OPS-001: Vendor Selection and Management

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-OPS-001 |
| Title | Vendor Selection and Management |
| Function | Operations |
| Owner | Operations Manager |
| Reviewer | COO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To select, onboard, and manage vendors that meet quality, cost, and risk standards.

## 2. Scope

Applies to all third-party suppliers of goods and services.

## 3. Procedure

1. **Need Identification**
   - Define requirements, budget, and success criteria.
   - Determine strategic importance and risk level.

2. **Sourcing**
   - Identify potential vendors.
   - Issue request for proposal or quotation as appropriate.

3. **Evaluation**
   - Score vendors on cost, capability, references, security, and compliance.
   - Conduct due diligence for high-risk vendors.

4. **Selection and Contracting**
   - Select preferred vendor.
   - Negotiate terms and execute contract.
   - Record vendor in vendor register.

5. **Ongoing Management**
   - Track performance against SLAs.
   - Conduct periodic reviews.
   - Maintain risk assessments and renewals.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Vendor SLA compliance | ≥95% | Quarterly |
| Critical vendor risk reviews | 100% | Annual |
`,
      'sop-ops-002-procurement': `# SOP-OPS-002: Procurement of Goods and Services

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-OPS-002 |
| Title | Procurement of Goods and Services |
| Function | Operations |
| Owner | Procurement Lead |
| Reviewer | COO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To standardize the purchase of goods and services needed for operations.

## 2. Scope

Applies to operational purchases not covered by SOP-FIN-005.

## 3. Procedure

1. **Request**
   - User submits procurement request with specifications and quantity.

2. **Vendor Selection**
   - Procurement identifies vendor or uses preferred vendor list.
   - Competitive quotes obtained for high-value purchases.

3. **Approval**
   - Manager and Finance approve per delegation matrix.

4. **Order and Receive**
   - Procurement places order.
   - User confirms receipt and quality.

5. **Invoice and Pay**
   - Procurement forwards invoice to Finance for payment.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Cost savings vs budget | ≥3% | Quarterly |
| On-time delivery | ≥95% | Monthly |
`,
      'sop-ops-003-quality': `# SOP-OPS-003: Quality Control and Assurance

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-OPS-003 |
| Title | Quality Control and Assurance |
| Function | Operations |
| Owner | Quality Manager |
| Reviewer | COO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To ensure products and services consistently meet defined quality standards.

## 2. Scope

Applies to all deliverables, products, and customer-facing services.

## 3. Procedure

1. **Define Standards**
   - Document quality criteria and acceptance thresholds.
   - Train team on standards.

2. **Inspect / Test**
   - Conduct inspections, tests, or reviews at defined checkpoints.
   - Record results.

3. **Identify Defects**
   - Log non-conformances and root causes.
   - Classify severity.

4. **Correct and Prevent**
   - Implement corrective actions.
   - Update procedures to prevent recurrence.

5. **Report**
   - Quality metrics reported to operations leadership.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Defect rate | ≤2% | Monthly |
| Customer quality complaints | ≤1% | Monthly |
`,
      'sop-ops-004-inventory': `# SOP-OPS-004: Inventory Management

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-OPS-004 |
| Title | Inventory Management |
| Function | Operations |
| Owner | Warehouse / Operations Manager |
| Reviewer | COO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To maintain optimal inventory levels and accurate records.

## 2. Scope

Applies to physical inventory, raw materials, finished goods, and supplies.

## 3. Procedure

1. **Receive Goods**
   - Inspect incoming shipments.
   - Record quantities and update inventory system.

2. **Store**
   - Place items in designated locations.
   - Follow first-in-first-out (FIFO) or defined rotation policy.

3. **Issue**
   - Pick items against authorized requisition or sales order.
   - Update inventory system.

4. **Count**
   - Conduct cycle counts regularly.
   - Perform full physical inventory at least annually.

5. **Reconcile**
   - Investigate and resolve discrepancies.
   - Adjust records with proper approval.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Inventory accuracy | ≥98% | Monthly |
| Stockout rate | ≤2% | Monthly |
`,
      'sop-ops-005-facilities': `# SOP-OPS-005: Facilities and Workplace Safety

## Header

| Field | Value |
|-------|-------|
| SOP ID | SOP-OPS-005 |
| Title | Facilities and Workplace Safety |
| Function | Operations |
| Owner | Facilities Manager |
| Reviewer | COO |
| Effective Date | YYYY-MM-DD |
| Review Cycle | Annual |
| Version | 1.0 |
| Status | Draft |

## 1. Purpose

To maintain a safe, compliant, and productive workplace.

## 2. Scope

Applies to all offices, warehouses, and remote-work equipment provided by the company.

## 3. Procedure

1. **Inspections**
   - Conduct regular safety inspections.
   - Maintain inspection logs.

2. **Hazard Reporting**
   - Employees report hazards to facilities or HR.
   - Facilities assesses and remediates within defined timelines.

3. **Emergency Preparedness**
   - Maintain emergency exits, signage, and equipment.
   - Conduct drills as required by local law.

4. **Remote Work Equipment**
   - Assess ergonomic needs.
   - Issue approved equipment and track assets.

5. **Incident Response**
   - Document workplace injuries or safety incidents.
   - Report to insurers and regulators as required.

## 4. Metrics

| Metric | Target | Frequency |
|--------|--------|-----------|
| Safety incidents | Zero serious incidents | Monthly |
| Hazard remediation time | ≤5 business days | Monthly |
`,
    },
    sop_index: `# SOP Index

Master list of all standard operating procedures.

## Legend

| Status | Meaning |
|--------|---------|
| Draft | In development |
| Approved | Active and in use |
| Under Review | Scheduled or in-progress review |
| Archived | No longer active |

## Human Resources

| ID | Title | Owner | Review Cycle | Status |
|----|-------|-------|--------------|--------|
| SOP-HR-001 | Recruitment and Hiring | | Annual | Draft |
| SOP-HR-002 | New Employee Onboarding | | Annual | Draft |
| SOP-HR-003 | Employee Offboarding | | Annual | Draft |
| SOP-HR-004 | Performance Management | | Annual | Draft |
| SOP-HR-005 | Leave and Time-Off Requests | | Annual | Draft |

## Finance

| ID | Title | Owner | Review Cycle | Status |
|----|-------|-------|--------------|--------|
| SOP-FIN-001 | Invoicing and Collections | | Annual | Draft |
| SOP-FIN-002 | Expense Reimbursement | | Annual | Draft |
| SOP-FIN-003 | Payroll Processing | | Annual | Draft |
| SOP-FIN-004 | Month-End Close | | Annual | Draft |
| SOP-FIN-005 | Purchase Order and Procurement | | Annual | Draft |

## Operations

| ID | Title | Owner | Review Cycle | Status |
|----|-------|-------|--------------|--------|
| SOP-OPS-001 | Vendor Selection and Management | | Annual | Draft |
| SOP-OPS-002 | Procurement of Goods and Services | | Annual | Draft |
| SOP-OPS-003 | Quality Control and Assurance | | Annual | Draft |
| SOP-OPS-004 | Inventory Management | | Annual | Draft |
| SOP-OPS-005 | Facilities and Workplace Safety | | Annual | Draft |

## Marketing

| ID | Title | Owner | Review Cycle | Status |
|----|-------|-------|--------------|--------|
| SOP-MKT-001 | Campaign Planning and Launch | | Annual | Draft |
| SOP-MKT-002 | Content Approval and Publishing | | Annual | Draft |
| SOP-MKT-003 | Social Media Management | | Annual | Draft |
| SOP-MKT-004 | Event Planning and Execution | | Annual | Draft |
| SOP-MKT-005 | Brand Asset Requests | | Annual | Draft |

## Legal and Compliance

| ID | Title | Owner | Review Cycle | Status |
|----|-------|-------|--------------|--------|
| SOP-LEG-001 | Contract Review and Approval | | Annual | Draft |
| SOP-LEG-002 | Data Subject Access Request Handling | | Annual | Draft |
| SOP-LEG-003 | Incident and Breach Notification | | Annual | Draft |
| SOP-LEG-004 | Intellectual Property Filings | | Annual | Draft |
| SOP-LEG-005 | Whistleblower Complaint Handling | | Annual | Draft |

## Information Technology

| ID | Title | Owner | Review Cycle | Status |
|----|-------|-------|--------------|--------|
| SOP-IT-001 | User Access Control and Provisioning | | Annual | Draft |
| SOP-IT-002 | Software Procurement and Approval | | Annual | Draft |
| SOP-IT-003 | Incident Response and Escalation | | Annual | Draft |
| SOP-IT-004 | Backup and Disaster Recovery | | Annual | Draft |
| SOP-IT-005 | IT Asset Management | | Annual | Draft |
`,
    sop_template: `# Standard Operating Procedure (SOP) Template

Use this template for every SOP in the organization.

## SOP Header

| Field | Value |
|-------|-------|
| SOP ID | e.g., SOP-HR-001 |
| Title | |
| Function | e.g., Human Resources |
| Owner | Name and role |
| Reviewer | Name and role |
| Effective Date | YYYY-MM-DD |
| Review Cycle | e.g., Annual |
| Version | e.g., 1.0 |
| Status | Draft / Approved / Archived |

## 1. Purpose

State why this SOP exists and what it achieves.

## 2. Scope

Define who and what this SOP applies to.

- **Applies to**:
- **Does not apply to**:

## 3. Definitions

| Term | Definition |
|------|------------|
| | |

## 4. Roles and Responsibilities

| Role | Responsibility |
|------|----------------|
| | |

## 5. Procedure

Numbered steps. Include decision points, approvals, and documentation.

1. Step one.
2. Step two.
3. Step three.

### 5.1 Sub-Procedure (if needed)

1. Sub-step.
2. Sub-step.

## 6. Required Inputs and Outputs

| Input | Source | Output | Destination |
|-------|--------|--------|-------------|
| | | | |

## 7. Tools, Systems, and References

- Systems:
- Forms:
- Related SOPs:
- Policies:
- Regulations:

## 8. Exceptions and Escalation

- Exceptions allowed:
- Escalation path:

## 9. Metrics and KPIs

| Metric | Target | Frequency |
|--------|--------|-----------|
| | | |

## 10. Revision History

| Version | Date | Author | Change Description |
|---------|------|--------|--------------------|
| 1.0 | | | Initial release |

## 11. Approval

| Role | Name | Signature / Date |
|------|------|------------------|
| Owner | | |
| Function Head | | |
| Compliance (if required) | | |
`,
  },
  reports: {
    README: `# 05 — Report Structures

This section standardizes recurring business reports so they are consistent, decision-oriented, and easy to produce.

## Files

- \`report_structures.md\` — Principles and anatomy of every business report.
- \`executive_report_template.md\` — Monthly / as-needed executive summary.
- \`quarterly_business_review_template.md\` — QBR format and KPI tables.
- \`status_report_template.md\` — Project or initiative status report.

## Usage

Each template can be copied into a document or slide deck. Customize sections based on audience and cadence.
`,
    executive_report_template: `# Executive Report Template

Use this template for monthly or ad-hoc executive updates.

---

## Report Metadata

| Field | Value |
|-------|-------|
| Report Title | Executive Report — [Month / Quarter] |
| Period | YYYY-MM-DD to YYYY-MM-DD |
| Prepared by | Name, Title |
| Date | YYYY-MM-DD |
| Distribution | CEO, CFO, COO, Board (if applicable) |

## Executive Summary

- Key highlight 1.
- Key highlight 2.
- Key risk or concern.
- Decision needed.

## Business Performance

| Metric | Target | Actual | Variance | Trend |
|--------|--------|--------|----------|-------|
| Revenue | | | | |
| Gross Margin | | | | |
| Net Income / Burn | | | | |
| Headcount | | | | |
| Customer NPS | | | | |

## Functional Updates

### Sales and Revenue

- Pipeline summary
- Wins and losses
- Forecast vs. plan

### Operations

- Key operational metrics
- Blockers and resolutions
- Quality / delivery updates

### Product / Service

- Releases or milestones
- Customer feedback themes
- Roadmap updates

### People

- Hiring progress
- Retention highlights
- Culture or engagement notes

### Finance

- Cash position
- Burn rate and runway
- Major expenses or variances

## Risks and Issues

| Risk | Impact | Likelihood | Mitigation | Owner |
|------|--------|------------|------------|-------|
| | | | | |

## Decisions and Actions

| Action | Owner | Due Date | Status |
|--------|-------|----------|--------|
| | | | |

## Appendix

- Detailed financial statements
- Charts and supporting data
`,
    quarterly_business_review_template: `# Quarterly Business Review (QBR) Template

Use this template for end-of-quarter leadership reviews.

---

## QBR Metadata

| Field | Value |
|-------|-------|
| Quarter | Q[X] YYYY |
| Date | YYYY-MM-DD |
| Facilitator | |
| Attendees | |

## 1. Quarter at a Glance

### Scorecard

| Objective | KPI | Target | Actual | Status |
|-----------|-----|--------|--------|--------|
| Financial | Revenue | | | |
| Financial | Gross Margin | | | |
| Customer | NPS | | | |
| Customer | Churn | | | |
| Internal | On-time delivery | | | |
| Internal | Employee engagement | | | |
| Learning | Product releases | | | |

### Top Achievements

1.
2.
3.

### Top Challenges

1.
2.
3.

## 2. Financial Review

- Revenue and growth vs. plan
- Expense analysis
- Profitability and cash flow
- Forecast for next quarter

## 3. Market and Customer Review

- Market trends
- Competitive updates
- Customer feedback summary
- Win/loss analysis

## 4. Functional Deep Dives

### Sales

- Pipeline coverage
- Conversion rates
- Top deals

### Marketing

- Campaign performance
- Lead generation
- Brand metrics

### Product / Engineering

- Roadmap progress
- Quality metrics
- Technical debt

### Operations

- Capacity and efficiency
- Vendor performance
- Cost control

### People

- Hiring vs. plan
- Retention
- Development and engagement

## 5. Strategic Initiatives

| Initiative | Status | Progress | Next Milestone | Owner |
|------------|--------|----------|----------------|-------|
| | | | | |

## 6. Risks and Mitigations

| Risk | Trend | Mitigation | Owner |
|------|-------|------------|-------|
| | | | |

## 7. Priorities for Next Quarter

1.
2.
3.
4.
5.

## 8. Open Decisions

| Decision | Options | Recommendation | Decision Maker | Due |
|----------|---------|----------------|----------------|-----|
| | | | | |

## 9. Appendix

- Detailed financials
- Department dashboards
- Customer quotes / case studies
`,
    report_structures: `# Report Structures

Guiding principles for all business reports.

## 1. Principles

- **Audience-first**: Tailor depth and tone to the reader.
- **Decision-oriented**: Every report should lead to an action, decision, or insight.
- **Consistent format**: Use the same structure, labels, and metrics over time.
- **Visual clarity**: Prefer charts and tables over dense text.
- **Single source of truth**: Pull from approved systems and define assumptions.
- **Timeliness**: Deliver on the agreed cadence.

## 2. Anatomy of a Business Report

Every recurring report should include:

1. **Title and Metadata**
   - Report name
   - Period covered
   - Prepared by and date
   - Distribution list

2. **Executive Summary**
   - 3–5 bullets on what happened and what matters.
   - Key decisions needed.

3. **Context / Methodology**
   - Scope, definitions, and data sources.
   - Any assumptions or exclusions.

4. **Body / Findings**
   - Organized by theme, priority, or function.
   - Use charts, tables, and callouts.

5. **Actions and Owners**
   - Table of next steps, owners, and due dates.

6. **Appendix (if needed)**
   - Detailed data, definitions, or supporting analysis.

## 3. Report Types and Cadence

| Report | Cadence | Audience | Primary Purpose |
|--------|---------|----------|---------------|
| Executive Report | Monthly | CEO / Board | Strategic snapshot |
| Quarterly Business Review | Quarterly | Leadership team | Deep performance review |
| Status Report | Weekly / bi-weekly | Project stakeholders | Track initiative progress |
| Financial Report | Monthly | CFO / Finance | Financial performance |
| Operations Report | Weekly | COO / Operations | Operational health |
| Marketing Report | Monthly | CMO / Marketing | Campaign and pipeline performance |
| Sales Report | Weekly | CRO / Sales | Pipeline and revenue |
| Risk and Compliance Report | Quarterly | General Counsel / Audit | Risk posture |

## 4. Writing Rules

- State the headline in the first paragraph.
- Use active voice.
- Define acronyms on first use.
- Keep paragraphs short (3–4 sentences max).
- Use numbered lists for actions.
- Include a "So what?" for every metric.

## 5. Distribution and Storage

- Reports are stored in the designated shared drive or reporting portal.
- Distribution lists are maintained by report owner.
- Historical reports are archived by fiscal year.
`,
    status_report_template: `# Status Report Template

Use this template for project or initiative status reporting.

---

## Report Metadata

| Field | Value |
|-------|-------|
| Project / Initiative | |
| Report Period | |
| Prepared by | |
| Date | |
| Distribution | |

## Overall Status

| Status | Meaning |
|--------|---------|
| 🟢 On Track | No issues expected |
| 🟡 At Risk | Issues exist but recoverable |
| 🔴 Off Track | Significant risk of missing goals |
| ⚪ On Hold | Paused or blocked |

**Current Status**: [Select one]

## Summary

- One-line project purpose.
- Key progress this period.
- Top risk or blocker.

## Milestones

| Milestone | Target Date | Actual Date | Status | Notes |
|-----------|-------------|-------------|--------|-------|
| | | | | |

## Key Metrics

| Metric | Target | Current | Trend |
|--------|--------|---------|-------|
| | | | |

## Risks and Issues

| Issue | Impact | Owner | Mitigation | Status |
|-------|--------|-------|------------|--------|
| | | | | |

## Work Completed This Period

1.
2.
3.

## Work Planned Next Period

1.
2.
3.

## Decisions Needed

| Decision | From | By When |
|----------|------|---------|
| | | |

## Budget / Resources

| Item | Budget | Spent | Remaining |
|------|--------|-------|-----------|
| | | | |

## Notes and Appendix

- Links to detailed plans
- Key documents or deliverables
`,
  },
  governance: {
    README: `# 06 — Governance

This section defines how documents, SOPs, and reports are controlled, reviewed, approved, and archived.

## Files

- \`document_control_policy.md\` — Versioning, ownership, storage, and retention.
- \`review_cycle_calendar.md\` — Standard review schedule by document type.
- \`approval_matrix.md\` — Who must approve what, by category and impact.

## Goal

Ensure the framework stays accurate, compliant, and useful as the company grows.
`,
    approval_matrix: `# Approval Matrix

Defines who must approve documents, contracts, spending, and actions by category and impact.

## Document Approvals

| Document Type | Owner Reviews | Function Head | Legal | Compliance | CEO / Board |
|---------------|---------------|---------------|-------|------------|-------------|
| SOP | ✓ | ✓ | — | If regulated | — |
| Policy | ✓ | ✓ | If legal | If regulated | ✓ |
| Brand Guidelines | ✓ | ✓ | ✓ | — | ✓ |
| Contract < \$10k | ✓ | ✓ | — | — | — |
| Contract \$10k–\$50k | ✓ | ✓ | ✓ | If DP/data | — |
| Contract > \$50k | ✓ | ✓ | ✓ | If DP/data | ✓ |
| Offer Letter — standard | ✓ | ✓ | — | — | — |
| Offer Letter — non-standard | ✓ | ✓ | ✓ | — | If C-suite |
| Marketing campaign | ✓ | ✓ | If claims | If regulated | If > budget |
| Press release | ✓ | ✓ | ✓ | If regulated | ✓ |

## Spending Approvals

| Spend Category | Threshold | Approver |
|----------------|-----------|----------|
| Routine operational | Up to \$500 | Manager |
| Operational | \$500–\$5,000 | Function Head |
| Capital / significant | \$5,000–\$25,000 | CFO + Function Head |
| Major investment | Above \$25,000 | CEO + Board (if required) |

## Personnel Actions

| Action | Approver |
|--------|----------|
| Hire within budget | Hiring Manager + HR |
| Hire above budget / new role | Function Head + CFO + HR |
| Termination — voluntary | Manager + HR |
| Termination — involuntary | Function Head + HR + Legal |
| Compensation change >10% | Function Head + CFO + HR |
| Promotion | Function Head + HR |

## IT and Security Actions

| Action | Approver |
|--------|----------|
| Standard user access | Manager + IT |
| Privileged access | Function Head + CISO |
| New software purchase < \$1k/month | Manager + IT |
| New software purchase > \$1k/month | Function Head + CISO + Finance |
| Security exception | CISO + Legal |
| Incident declaration | CISO + Legal / DPO |

## Emergency Authority

In urgent situations, the most senior accountable executive may approve actions outside normal thresholds, provided:

- The emergency and rationale are documented within 24 hours.
- The relevant approver is notified within 24 hours.
- A retrospective review is conducted within 30 days.

## Delegation

- Approvers may delegate in writing.
- Delegation must be recorded in the approval matrix register.
- Delegation does not transfer accountability.
`,
    document_control_policy: `# Document Control Policy

## 1. Purpose

To ensure documents in the brand and company framework are accurate, accessible, and properly maintained.

## 2. Scope

Applies to all policies, SOPs, guidelines, templates, and controlled reports.

## 3. Document Categories

| Category | Examples | Control Level |
|----------|----------|---------------|
| Critical | Policies, SOPs, legal contracts, financial controls | High |
| Standard | Guidelines, templates, training materials | Medium |
| Reference | Articles, market research, historical reports | Low |

## 4. Naming and Numbering

- **Policies**: POL-[FUNCTION]-### — e.g., POL-IT-001
- **SOPs**: SOP-[FUNCTION]-### — e.g., SOP-HR-001
- **Guidelines**: GL-[FUNCTION]-### — e.g., GL-MKT-001
- **Reports**: RPT-[TYPE]-YYYY-MM — e.g., RPT-EXEC-2026-07
- **Templates**: TPL-[TYPE] — e.g., TPL-OFFER

## 5. Version Control

- Use semantic versioning: MAJOR.MINOR.
- Major version for substantive changes requiring retraining.
- Minor version for clarifications, corrections, or formatting.
- Draft versions are labeled 0.x until approved.
- Obsolete versions are archived, not deleted.

## 6. Storage and Access

- Master controlled documents stored in designated document repository.
- Access permissions based on role.
- Public-facing documents approved by Legal / Marketing before publication.

## 7. Change Control

1. Change request submitted.
2. Document owner assesses impact.
3. Relevant stakeholders review.
4. Approver signs off per approval matrix.
5. New version published; old version archived.
6. Affected teams notified of changes.

## 8. Record Retention

| Document Type | Retention Period |
|---------------|------------------|
| Policies and SOPs | Current version + 7 years |
| Financial records | 7+ years |
| HR records | Duration of employment + 7 years |
| Contracts | Duration + 7 years |
| Incident reports | 7 years |
| Training records | Duration + 3 years |

## 9. Roles

| Role | Responsibility |
|------|----------------|
| Document Owner | Maintains accuracy and schedules reviews |
| Function Head | Reviews and approves function-specific documents |
| Compliance / Legal | Reviews regulated or high-risk documents |
| Company Secretary / COO | Maintains master document register |

## 10. Compliance

- Employees must use current versions of controlled documents.
- Deviations from SOPs must be documented and approved.
- Annual audit of document control compliance.
`,
    review_cycle_calendar: `# Review Cycle Calendar

Standard review schedule for framework documents.

## Review Triggers

Documents must be reviewed on schedule or earlier if:

- Regulations change.
- Significant business change occurs (merger, new market, new product).
- Incident or audit finding points to a gap.
- Process or system changes materially affect the procedure.

## Standard Schedule

| Document Type | Review Frequency | Review Month | Responsible Role |
|---------------|------------------|--------------|------------------|
| Policies | Annual | Q1 | Function Head |
| SOPs | Annual | Q2 | SOP Owner |
| Brand Guidelines | Annual | Q1 | Brand Manager |
| Tone of Voice Guide | Annual | Q1 | Content Lead |
| Naming Framework | Biennial | Q1 | Brand / Legal |
| Legal Structure Templates | As needed / Biennial | Q1 | Legal / Finance |
| Compliance Checklist | Annual | Q1 | Compliance Officer |
| Report Templates | Annual | Q4 | COO / Function Heads |
| Document Control Policy | Annual | Q1 | Company Secretary |
| Approval Matrix | Annual | Q1 | CEO / COO |
| Vendor Risk Assessments | Annual | Q3 | Procurement / Security |
| IT Security SOPs | Annual | Q3 | CISO |
| Privacy SOPs | Annual | Q1 | DPO |

## Review Workflow

1. **30 days before due**: Owner receives reminder.
2. **Review**: Owner updates content, verifies links, and consults stakeholders.
3. **15 days before due**: Draft submitted for approval.
4. **Approval**: Approver reviews and signs off.
5. **Publication**: New version published; old version archived.
6. **Communication**: Changes communicated to affected teams.

## Calendar Template

| Month | Reviews Due |
|-------|-------------|
| January | Policies, Brand Guidelines, Naming, Compliance, Document Control, Privacy |
| February | |
| March | |
| April | SOPs |
| May | |
| June | |
| July | |
| August | |
| September | Vendor Risk, IT Security |
| October | |
| November | Report Templates |
| December | |

## Overdue Escalation

- Overdue by 15 days: reminder to owner and function head.
- Overdue by 30 days: escalation to COO / General Counsel.
`,
  },
} as const;

export const frameworkContentDe = {
  naming: {
    README: `# 01 — Marken-Namensgebung

Dieser Abschnitt liefert eine wiederholbare Methodik zur Entwicklung, Bewertung und rechtlichen Prüfung von Markennamen über Branchen und Ländergrenzen hinweg.

## Dateien

- \`naming_methodology.md\` — Schritt-für-Schritt-Namensprozess.
- \`industry_naming_matrix.md\` — Namensarchetypen, Suffixe und Branchenbeispiele.
- \`name_evaluation_scorecard.md\` — Quantitative Bewertung von Namenskandidaten.
- \`trademark_screening_checklist.md\` — Schritte vor Markenanmeldung und Marktstart.

## Prozessübersicht

1. Strategie definieren (Positionierung, Zielgruppe, Region, Tonfall).
2. Namenskandidaten anhand von Branchenarchetypen und Wortfeldern generieren.
3. Kurzliste nach Verfügbarkeit, Aussprache und Merkbarkeit filtern.
4. Kandidaten mit dem Bewertungsscoring bewerten.
5. Marken-, Domain- und Social-Handle-Verfügbarkeit prüfen.
6. Mit Zielgruppenforschung validieren.
7. Auswahl finalisieren und registrieren.
`,
    industry_naming_matrix: `# Branchen-Namensmatrix

Namensarchetypen, gängige Suffixe, Tonfall-Anhaltspunkte und Beispielmuster nach Branche.

## Technologie / SaaS

- Archetypen: Erfinden, Kompositum, Metapher
- Suffixe: -ly, -ify, -io, -cloud, -edge, -bit, -scale, -flow
- Tonfall: Modern, effizient, intelligent
- Beispiele: Shopify, Datadog, Notion, Figma
- Hinweise: Kurze, einprägsame Namen, die Geschwindigkeit, Daten oder Konnektivität suggerieren.

## Finanzdienstleistungen / Fintech

- Archetypen: Evokativ, vertrauensbasiert, Kompositum
- Suffixe: -vest, -bank, -pay, -fin, -wealth, -capital, -clear
- Tonfall: Sicher, glaubwürdig, einfach
- Beispiele: Stripe, Robinhood, Square, Wise
- Hinweise: Gimmick-Schreibweisen vermeiden; Stabilität und Transparenz betonen.

## Gesundheit / Biotech

- Archetypen: Deskriptiv, wissenschaftlich, lateinische/griechische Wurzeln
- Suffixe: -care, -med, -bio, -gen, -x, -health, -life
- Tonfall: Professionell, mitfühlend, präzise
- Beispiele: Medtronic, Pfizer, Teladoc, AstraZeneca
- Hinweise: Regulatorische Namensbeschränkungen in jedem Markt prüfen.

## Konsumgüter / Einzelhandel / E-Commerce

- Archetypen: Evokativ, Gründername, Kompositum
- Suffixe: -co, -goods, -shop, -mart, -box, -hive
- Tonfall: Freundlich, inspirierend, lifestyle-orientiert
- Beispiele: Everlane, Glossier, Allbirds, Warby Parker
- Hinweise: Namen sollten fotogen und in sozialen Medien teilbar wirken.

## Professionelle Dienstleistungen

- Archetypen: Gründername, deskriptiv, geografiebasiert
- Suffixe: & Partner, Group, Beratung, Consulting, Associates
- Tonfall: Autoritär, beziehungsorientiert
- Beispiele: McKinsey, Deloitte, Boston Consulting Group
- Hinweise: Längere Namen sind akzeptabel, wenn Glaubwürdigkeit Priorität hat.

## Produktion / Industrie

- Archetypen: Deskriptiv, Geografie, Stärkemetapher
- Suffixe: -tech, -works, -industries, -systems, -steel, -power
- Tonfall: Robust, zuverlässig, technikorientiert
- Beispiele: Caterpillar, Siemens, Bosch, 3M
- Hinweise: Größe, Langlebigkeit und technische Kompetenz vermitteln.

## Lebensmittel / Getränke

- Archetypen: Evokativ, Gründername, sinnlich
- Suffixe: -kitchen, -table, -farm, -brew, -bite, -blend
- Tonfall: Appetitanregend, authentisch, handwerklich
- Beispiele: Impossible Foods, Chobani, Blue Bottle, Oatly
- Hinweise: Namen sollten Geschmack, Herkunft oder Ritual evozieren.

## Bildung / EdTech

- Archetypen: Evokativ, deskriptiv, aspirational
- Suffixe: -academy, -school, -learn, -edu, -mind, -path
- Tonfall: Ermutigend, glaubwürdig, zugänglich
- Beispiele: Coursera, Khan Academy, Duolingo, MasterClass
- Hinweise: Wachstum, Meisterschaft und Gemeinschaft andeuten.

## Energie / Nachhaltigkeit

- Archetypen: Metapher, naturbezogen, technisch
- Suffixe: -sun, -wind, -green, -earth, -nova, -grid, -source
- Tonfall: Optimistisch, verantwortungsbewusst, zukunftsorientiert
- Beispiele: Tesla, NextEra, Vestas, Sunrun
- Hinweise: Generische Greenwashing-Anleihen vermeiden.

## Hospitality / Reisen

- Archetypen: Evokativ, Geografie, sinnlich
- Suffixe: -stays, -hotel, -voyage, -wander, -escape
- Tonfall: Einladend, abenteuerlustig, entspannend
- Beispiele: Airbnb, Marriott, Booking.com, Four Seasons
- Hinweise: Namen sollten Emotionen und Orte auslösen.

## Namens-Suffix-Register

| Suffix | Konnotation | Bestens geeignet für |
|--------|-------------|----------------------|
| -ly | Freundlich, aktivitätsorientiert | SaaS, Tools |
| -ify | Herstellen, transformieren | Plattformen, Automatisierung |
| -io | Technik, Daten, Input/Output | Software, Hardware |
| -cloud | Skalierbar, online | Infrastruktur, SaaS |
| -edge | Spitze, schnell | Tech, Sicherheit |
| -flow | Prozess, reibungslos | Workflows, Kreativtools |
| -care | Service, Gesundheit | Gesundheit, Wellness |
| -pay | Zahlungen, Transaktionen | Fintech |
| -works | Handwerk, Produktion | Industrie, Agenturen |
| -co | Modern, einfach | Konsum, DTC |

## Deutsch- und EU-spezifische Hinweise

- **Rechtsform-Suffixe**: Bei deutschen Unternehmen können „GmbH", „UG (haftungsbeschränkt)", „AG" oder „SE" ergänzt werden, ohne den Markennamen zu überladen.
- **Domain-Präferenz**: \`.de\` ist im deutschsprachigen Raum Standard; \`.eu\`, \`.ch\`, \`.at\` ergänzen den regionalen Schutz.
- **Markenanmeldung**: DPMA (Deutschland), EUIPO (EU-Marke) und bei Bedarf WIPO für internationale Schutzstrategien prüfen.
- **Sprachliche Prüfung**: Achten Sie auf regionale Dialekte, Umlaute und Aussprachebarrieren in Österreich und der Schweiz.
`,
    name_evaluation_scorecard: `# Bewertungsscoring für Namenskandidaten

Bewerten Sie jeden Namenskandidaten anhand von acht Dimensionen. Verwenden Sie eine Skala von 1–5 (1 = schwach, 5 = exzellent).

## Bewertungsmatrix

| Kriterium | Gewichtung | Punkte | Gewichteter Wert | Notizen |
|-----------|------------|--------|------------------|---------|
| Merkbarkeit | 15% | | | Kann man sich den Namen nach einem Kontakt merken? |
| Aussprache | 10% | | | In allen Zielmärkten leicht auszusprechen? |
| Schreibweise | 10% | | | Nach dem Hören korrekt zu schreiben? |
| Unterscheidungskraft | 15% | | | Sticht er aus der Konkurrenz heraus? |
| Relevanz | 15% | | | Passt er zu Branche und Positionierung? |
| Skalierbarkeit | 10% | | | Ermöglicht er Produkt- oder geografische Expansion? |
| Rechtliche Verfügbarkeit | 15% | | | Domain, Marke, Social-Handles? |
| Sprachliche Unbedenklichkeit | 10% | | | Keine negativen Bedeutungen in Zielsprachen? |
| **Gesamt** | **100%** | | | |

## Entscheidungsschwellen

- **Weiterführen**: 4,0 oder höher; formale Markenprüfung beginnen.
- **Überarbeiten**: 3,0–3,9; schwache Dimensionen klären, dann neu bewerten.
- **Ablehnen**: Unter 3,0; nicht weiterverfolgen.

## Mindestkriterien

Ein Name muss in jedem der folgenden Kriterien mindestens **3** Punkte erreichen, um weitergeführt zu werden:

- Rechtliche Verfügbarkeit
- Sprachliche Unbedenklichkeit
- Aussprache

Auch ein hoher Durchschnitt scheitert, wenn ein kritisches Mindestkriterium schwach ist.

## Rechenbeispiel

| Kriterium | Gewichtung | Punkte | Gewichtet |
|-----------|------------|--------|-----------|
| Merkbarkeit | 15% | 4 | 0,60 |
| Aussprache | 10% | 5 | 0,50 |
| Schreibweise | 10% | 4 | 0,40 |
| Unterscheidungskraft | 15% | 4 | 0,60 |
| Relevanz | 15% | 3 | 0,45 |
| Skalierbarkeit | 10% | 5 | 0,50 |
| Rechtliche Verfügbarkeit | 15% | 3 | 0,45 |
| Sprachliche Unbedenklichkeit | 10% | 5 | 0,50 |
| **Gesamt** | | | **4,00** |

Ergebnis: Weiterführen mit vertiefter Markenprüfung.
`,
    naming_methodology: `# Methodik der Marken-Namensgebung

Ein wiederholbarer Prozess zur Entwicklung von Markennamen.

## 1. Strategische Eingaben

| Eingabe | Fragestellung |
|---------|---------------|
| Branche | Welcher Sektor und welche Teilszene? |
| Zielgruppe | Wer ist primärer Käufer und Nutzer? |
| Region | Lokal, national, EU-weit oder global? |
| Positionierung | Premium, zugänglich, innovativ, traditionell? |
| Tonfall | Spielerisch, technisch, vertrauenswürdig, disruptiv? |
| Lebensdauer | Produkt, Produktlinie oder dauerhaftes Unternehmen? |
| Sprachen | In welchen Sprachen muss der Name funktionieren? |

## 2. Namensarchitektur

Entscheide vor der Kreativphase über die Namenshierarchie:

- **Masterbrand**: Ein Name umfasst alle Produkte und Dienstleistungen.
- **Endorsed**: Submarken führen den Mutternamen mit.
- **Freestanding**: Jedes Angebot trägt einen eigenen Namen.
- **Hybrid**: Masterbrand für Kernangebot, freistehende Namen für Ventures.

## 3. Namenstypen

| Typ | Beschreibung | Beispiel |
|-----|--------------|----------|
| Deskriptiv | Sagt, was das Unternehmen tut | General Motors |
| Evokativ | Ruft ein Gefühl oder eine Idee hervor | Apple |
| Erfinden | Neues, geprägtes Wort | Kodak |
| Akronym | Initialen eines längeren Namens | IBM |
| Gründername | Basiert auf dem Namen des Gründers | Ford |
| Kompositum | Zwei Wörter verbunden | Microsoft |
| Geografisch | Ortsbezogen | Cisco (San Francisco) |
| Metapher | Übertragener Begriff | Amazon |

## 4. Namens-Workshop-Schritte

1. **Sammeln**: 50–100 rohe Wörter, Wurzeln und Konzepte generieren. Kein Filter.
2. **Clustern**: Nach Themen, Gefühl und Wortstruktur gruppieren.
3. **Konstruieren**: 20–30 Namenskandidaten aus Präfixen, Suffixen, Komposita und Kürzungen bilden.
4. **Filtern**: Namen entfernen, die grundlegende Prüfungen nicht bestehen (negative Konnotationen, schwere Aussprache, offensichtliche Konflikte).
5. **Bewerten**: Das \`name_evaluation_scorecard.md\` anwenden.
6. **Validieren**: Top-Kandidaten mit der Zielgruppe testen.

## 5. Namensregeln

- Leicht auszusprechen in allen Zielmärkten.
- Leicht zu schreiben, nachdem man ihn einmal gehört hat.
- Kurz genug für URLs, Social-Media-Handles und Schlagzeilen.
| Unterscheidbar innerhalb des Wettbewerbsumfelds.
- Emotional im Einklang mit der Markenpositionierung.
- Rechtlich und sprachlich unbedenklich.

## 6. Ergebnisse

- Namensstrategie-Briefing
- Kandidatenliste mit Begründung
- Scorecard-Ergebnisse
- Verfügbarkeitsprüfungsbericht
- Finale Empfehlung mit Risikohinweisen
`,
    trademark_screening_checklist: `# Checkliste zur Markenprüfung

Diese Checkliste wird vor der Finalisierung eines Markennamens verwendet.

## Vorschlagssuche (intern)

- [ ] Exakten Namen in gängigen Suchmaschinen prüfen.
- [ ] Häufige Schreibfehler und Transliterationen durchsuchen.
- [ ] Domain-Verfügbarkeit prüfen:
  - [ ] \`.com\`
  - [ ] Länderspezifische TLDs (z. B. \`.de\`, \`.co.uk\`, \`.fr\`, \`.jp\`)
  - [ ] Relevante neue gTLDs (z. B. \`.io\`, \`.tech\`, \`.app\`, \`.berlin\`)
- [ ] Social-Media-Handles prüfen:
  - [ ] LinkedIn
  - [ ] X / Twitter
  - [ ] Instagram
  - [ ] Facebook
  - [ ] TikTok
  - [ ] YouTube
- [ ] App-Store-Verfügbarkeit prüfen (falls relevant).
- [ ] Konkurrenznamen in gleicher und benachbarter Kategorie bewerten.

## Formelle Suche (Rechtsbeistand)

- [ ] Markenfreiheitsrecherche in jedem Zielmarkt beauftragen.
- [ ] Identische und ähnliche Marken in relevanten Klassen prüfen.
- [ ] Risiko einer Verwechslungsgefahr einschätzen.
- [ ] Bekannte / berühmte Marken berücksichtigen, die einen weiteren Schutzumfang genießen.
- [ ] Laufende Widerspruchsverfahren oder Rechtsstreitigkeiten ähnlicher Marken ermitteln.

## Sprachliche und kulturelle Prüfung

- [ ] Namen in jede Amtssprache der Zielmärkte übersetzen.
- [ ] Phonetische Ähnlichkeit mit beleidigenden oder negativen Begriffen prüfen.
- [ ] Unbeabsichtigte Assoziationen mit historischen Ereignissen, Religionen oder Slang ausschließen.
- [ ] Aussprache mit Muttersprachlern testen.

## Anmeldung und Schutz

- [ ] Endgültige Schreibweise und etwaige stilisierte Varianten festlegen.
- [ ] Alle relevanten Klassen der Nizzaklassifikation bestimmen.
- [ ] Markenanmeldungen in Prioritätsmärkten einreichen (DPMA, EUIPO, WIPO nach Bedarf).
- [ ] Passende Domains und Social-Handles sichern.
- [ ] Eigentümerschaft und Erneuerungstermine im IP-Register erfassen.

## Go / No-Go-Freigabe

| Prüfer | Rolle | Entscheidung | Datum |
|--------|-------|--------------|-------|
| | Namensverantwortlich | | |
| | Rechts- / IP-Beistand | | |
| | Marketing-Verantwortlich | | |
| | Lokaler Marktprüfer (bei Globalisierung) | | |

**Endentscheidung**: Go / Go mit Vorbehalten / No-go
`,
  },
  legal: {
    README: `# 02 — Rechtsstrukturen

Dieser Abschnitt unterstützt Gründer und Rechtsbeistände bei der Wahl von Rechtsformen und gerichtsstandsübergreifenden Strukturen für nationale und internationale Unternehmen.

## Dateien

- \`entity_selection_framework.md\` — Entscheidungslogik zur Wahl der Rechtsform.
- \`jurisdiction_matrix.md\` — Vergleich populärer Gerichtsbarkeiten nach relevanten Faktoren.
- \`international_holding_structures.md\` — Gängige mehrstufige Aufbau-Strukturen.
- \`compliance_checklist.md\` — Laufende rechtliche und regulatorische Pflichten.

## Anwendungsfälle

- Gründung eines neuen Unternehmens.
- Expansion in neue Länder.
- Umstrukturierung aus Investitions-, Steuer- oder Haftungsgründen.
- Aufbau einer Holding- oder IP-Gesellschaft.
`,
    compliance_checklist: `# Checkliste für rechtliche und regulatorische Compliance

Laufende Pflichten nach der Unternehmensgründung.

## Unternehmensführung und -pflege

- [ ] Jahresabschluss / Gewerbesteuererklärung rechtzeitig eingereicht.
- [ ] Handelsregister- und Gesellschafterverzeichnis aktuell.
- [ ] Gesellschafterbeschlüsse und Geschäftsführerprotokolle archiviert.
- [ ] Geschäftsanschrift und eingetragener Vertreter aktuell.
- [ ] Kapitalerhöhungen und -herabsetzungen ordnungsgemäß dokumentiert.
- [ ] Vorstands- und Gesellschaftersitzungsprotokolle archiviert.

## Steuerliche Compliance

- [ ] Körperschaftsteuererklärung fristgerecht abgegeben.
- [ ] USt-IdNr. / USt und ggf. VAT/GST/Sales-Tax-Registrierung und -Meldungen aktuell.
- [ ] Lohnsteuer und Sozialabgaben abgeführt.
- [ ] Transferpricing-Dokumentation erstellt.
- [ ] Steuerlicher Wohnsitz und Substance jährlich überprüft.
- [ ] Country-by-Country-Reporting (falls anwendbar).

## Arbeit und Beschäftigung

- [ ] Arbeitsverträge für alle Arbeitnehmer vorhanden.
- [ ] Arbeitnehmer- vs. Auftragnehmer-Einstufung geprüft.
- [ ] Mindestlohn, Überstunden und Urlaubsregelungen eingehalten.
- [ ] Arbeitsschutz- und Sicherheitsverpflichtungen erfüllt.
- [ ] Chancengleichheits- und Antidiskriminierungsrichtlinien umgesetzt.

## Geistiges Eigentum

- [ ] Markenerneuerungen verfolgt und eingereicht.
- [ ] Patent- und Urheberrechtseintragungen gepflegt.
- [ ] Domainnamen verlängert.
- [ ] IP-Zessionen von Gründern / Auftragnehmern dokumentiert.
- [ ] Geheimhaltungsvereinbarungen vorhanden.

## Datenschutz und Privatsphäre

- [ ] Datenschutzerklärung veröffentlicht und aktuell.
- [ ] Auftragsverarbeitungsverträge (AVV) mit Dienstleistern.
- [ ] Cross-Border-Transfermechanismen dokumentiert.
- [ ] Betroffenenrechte-Verfahren eingerichtet (DSGVO: Auskunft, Löschung, Berichtigung, Widerspruch).
- [ ] Sicherheitsvorfall-Reaktionsplan getestet.

## Verträge und Kommerzielles

- [ ] Standardvertragsvorlagen jährlich überprüft.
- [ ] Wichtige Verträge auf Verlängerung und Kündigung überwacht.
- [ ] Haftungsbeschränkung und Indemnisierung konsistent.
- [ ] Streitbeilegungsklauseln der Gerichtsbarkeit angemessen.

## Branchenspezifische Lizensierung

- [ ] Finanzdienstleistungslizenzen und -registrierungen.
- [ ] Gesundheits- / Medizinproduktzulassungen.
- [ ] Lebensmittel- und Getränkegenehmigungen.
- [ ] Telekommunikations- und Rundfunklizenzen.
- [ ] Krypto- / Geldtransferregistrierungen.

## Aufbewahrungsfristen

| Dokumentenart | Mindestaufbewahrung |
|-----------------|---------------------|
| Gesellschaftsprotokolle | Dauerhaft |
| Steuerunterlagen | 7+ Jahre (DE: 10 Jahre bei HGB/KSt) |
| Personalakten | Dauer Beschäftigung + 7 Jahre |
| Verträge | Laufzeit + 7 Jahre |
| Jahresabschlüsse | Dauerhaft |
| IP-Registrierungen | Dauerhaft |
| Betroffenenanfragen | 3+ Jahre |

## Überprüfungskalender

| Aktivität | Häufigkeit | Verantwortlich |
|-----------|------------|----------------|
| Compliance-Selbstbewertung | Quartalsweise | General Counsel / COO / Beauftragter |
| Jährliche Meldungen | Jährlich | Geschäftsführer / Company Secretary |
| Vertragsvorlagen-Review | Jährlich | Legal |
| Datenschutzerklärungs-Review | Jährlich / bei Gesetzesänderung | DSB / Legal |
| IP-Erneuerungs-Review | Quartalsweise | IP-Beistand |
`,
    entity_selection_framework: `# Entscheidungsframework zur Rechtsformwahl

Ein Entscheidungsframework für die Wahl der Rechtsform eines Unternehmens.

## Entscheidungsfaktoren

| Faktor | Fragestellung |
|--------|---------------|
| Haftung | Wie viel persönlicher Haftungsschutz ist erforderlich? |
| Besteuerung | Welche steuerliche Behandlung ist gewünscht? |
| Kapital | Wird externes Kapital eingeworben? |
| Kontrolle | Wie viele Eigentümer und welche Governance-Struktur? |
| Compliance | Welcher Verwaltungsaufwand ist vertretbar? |
| Geografie | Wo wird das Unternehmen tätig und besteuert? |
| Exit | Welcher Exit-Pfad ist angestrebt? |

## Deutschland und deutschsprachiger Raum

| Rechtsform | Haftung | Besteuerung | Bestens geeignet für |
|------------|---------|-------------|----------------------|
| Einzelunternehmen | Keine | Einkommensteuer | Solopreneure, geringes Risiko, Ideentest |
| GbR (BGB-Gesellschaft) | Gesamthaftung | Transparent / Einkommensteuer | Mehrere Gründer, geringes Risiko |
| UG (haftungsbeschränkt) | Beschränkt | Körperschaftsteuer + GewSt + Kapitalertragsteuer | Startup-Gründung mit wenig Stammkapital |
| GmbH | Beschränkt | Körperschaftsteuer + Gewerbesteuer + KESt | Standardrechtsform für deutsche SMEs |
| AG | Beschränkt | Körperschaftsteuer + Gewerbesteuer + KESt | Börsennotierte Gesellschaften, große Unternehmen |
| SE | Beschränkt | Europäische Konzernrechtsform, steuerlich national | Europäische Konzerne, grenzüberschreitende M&A |
| GmbH & Co. KG | Beschränkt (Komplementär) | Transparent / Gestaltungsmöglichkeiten | Familienunternehmen, Vermögensverwaltung |
| Stiftung / gemeinnützige GmbH | Beschränkt | Steuerbefreit (bei Anerkennung) | Gemeinnützige, Bildungs-, Sozialmissionen |

## Vereinigte Staaten

| Rechtsform | Haftung | Besteuerung | Bestens geeignet für |
|------------|---------|-------------|----------------------|
| Sole Proprietorship | Keine | Pass-through | Solopreneure, geringes Risiko, Ideentest |
| Partnership | Gesamthaftung | Pass-through | Mehrere Gründer, Dienstleistungen |
| LLC | Beschränkt | Flexibel (pass-through oder Körperschaft) | KMU, Asset-Holding |
| S-Corp | Beschränkt | Pass-through | Eigentümergeführte Unternehmen mit Steueroptimierung |
| C-Corp | Beschränkt | Körperschaft + Shareholder-Steuer | Startups mit Venture Capital, Börsengang |
| Nonprofit | Beschränkt | Steuerbefreit (bei Anerkennung) | Gemeinnützige, Bildung, soziale Missionen |

## Vereinigtes Königreich

| Rechtsform | Haftung | Besteuerung | Bestens geeignet für |
|------------|---------|-------------|----------------------|
| Sole Trader | Keine | Einkommensteuer | Solopreneure, geringes Risiko |
| Partnership | Gesamthaftung | Einkommensteuer der Partner | Kleine Gründerfirmen |
| LLP | Beschränkt | Transparent / Mitgliedsteuer | Professionelle Partnerschaften |
| Private Limited Company (Ltd) | Beschränkt | Corporation Tax | Die meisten Handelsunternehmen |
| Public Limited Company (PLC) | Beschränkt | Corporation Tax | Börsennotierte Unternehmen |

## Europäische Union — weitere Beispiele

| Gerichtsbarkeit | Rechtsform | Hinweise |
|-----------------|------------|----------|
| Frankreich | SAS / SASU | Flexible Governance; beliebt bei Startups |
| Niederlande | BV | Flexible Anteilsstruktur; Holding-freundlich |
| Irland | Private Limited Company | Niedrige Körperschaftsteuer; EU-Standort |
| Estland | OÜ | Vollständig digitale Gründung; E-Residency-Option |
| Schweiz | GmbH / AG | Starke Glaubwürdigkeit, hoher Gründungsaufwand |

## Asien-Pazifik — Beispiele

| Gerichtsbarkeit | Rechtsform | Hinweise |
|-----------------|------------|----------|
| Singapur | Private Limited Company | Pro-business, niedrige Steuer, starker IP-Schutz |
| Hongkong | Private Limited Company | Territoriales Steuersystem; Tor nach China |
| Australien | Proprietary Limited Company (Pty Ltd) | Standard für KMU |
| Indien | Private Limited Company | Gängig für Startups und Tochtergesellschaften |
| Japan | Kabushiki Kaisha (KK) / Godo Kaisha (GK) | KK für öffentliche Glaubwürdigkeit; GK wie LLC |

## Auswahlalgorithmus

1. Wenn institutionelles US-Venture-Capital eingeworben wird → **C-Corp (Delaware)**.
2. Wenn kleines gründergeführtes Unternehmen ohne externes Eigenkapital → **UG/GmbH, Ltd, LLC oder lokales Äquivalent**.
3. Wenn professionelle Partnerschaft → **Partnerschaft, LLP oder lokales Äquivalent**.
4. Wenn Holding- / IP-Gesellschaft → **Niederländische BV / Luxemburg S.à r.l. / Singapur**.
5. Wenn Börsengang geplant → **AG / C-Corp / PLC / KK**.
6. Wenn soziale Mission und Steuerbefreiung → **gemeinnützige GmbH / Charity / Foundation / Nonprofit**.

## Ergebnisdokument

Die Rechtsformwahl sollte dokumentiert werden mit:

- Gewählter Rechtsform und Gerichtsbarkeit
- Begründung für jeden Entscheidungsfaktor
- Kapital- und Eigentümerstruktur
- Registriertem Vertreter und Geschäftsanschrift
- Jährlichen Compliance-Pflichten
`,
    international_holding_structures: `# Internationale Holdingstrukturen

Gängige gerichtsstandsübergreifende Unternehmensstrukturen und ihre Anwendungsfälle.

## Ziele einer internationalen Struktur

- Haftung für jedes Landesgeschäft begrenzen.
- Kapitalfluss und Quellensteuern optimieren.
- Geistiges Eigentum schützen.
- Fundraising und zukünftige Exits vereinfachen.
- Lokale Substance- und regulatorische Anforderungen erfüllen.

## Struktur 1: Einzelne Heimatgesellschaft

\`\`\`
[Gründer]
    |
[HoldCo im Heimatmarkt]
    |
[Operating Subsidiaries in jedem Markt]
\`\`\`

- **Bestens geeignet für**: Unternehmen in wenigen ähnlichen Gerichtsbarkeiten.
- **Vorteile**: Einfache Governance, konsolidierte Berichterstattung.
- **Nachteile**: Weniger Steueroptimierung, lokale Haftungen können Gruppe betreffen.

## Struktur 2: Zwischenholding

\`\`\`
[Gründer]
    |
[TopCo in investorenfreundlicher Gerichtsbarkeit, z. B. Delaware / UK / Singapur]
    |
[Regional HoldCo in Vertragsstaaten-Hub, z. B. Niederlande / Luxemburg / Irland]
    |
[Operating Subsidiaries in jedem Markt]
\`\`\`

- **Bestens geeignet für**: Multiregionale Operationen, institutionelle Investoren, Dividendenrepatriierung.
- **Vorteile**: Vertragsvorteile, zentralisiertes IP oder Treasury, Investorenvertrautheit.
- **Nachteile**: Komplexität, Substance-Anforderungen, Anti-Hybrid-Regeln.

## Struktur 3: IP-Holding-Struktur

\`\`\`
[Gründer / Investoren]
    |
[HoldCo]
    |--------------------
    |                   |
[OpCo A]          [IP HoldCo]
(Markt A)        (Lizenziert IP an OpCos)
    |
[OpCo B]
(Markt B)
\`\`\`

- **Bestens geeignet für**: IP-intensive Unternehmen (Software, Biotech, Medien).
- **Vorteile**: Zentralisierter IP-Eigentum, Lizenzgebühren, steuereffiziente F&E.
- **Nachteile**: Transferpricing-Prüfung, lokale F&E- und Substance-Regeln.

## Struktur 4: Regionales Betriebsmodell

\`\`\`
[Global HoldCo]
    |
[Regional Hubs: Americas / EMEA / APAC]
    |
[Lokale OpCos und Niederlassungen]
\`\`\`

- **Bestens geeignet für**: Globaler Vertrieb, lokaler Kundensupport, verteilte Teams.
- **Vorteile**: Lokale Präsenz, Compliance, Kundenvertrauen.
- **Nachteile**: Mehrere Gründungen, Lohnbuchhaltung und regulatorische Meldungen.

## Wichtige Gestaltungsentscheidungen

| Entscheidung | Optionen | Überlegungen |
|--------------|----------|--------------|
| TopCo-Standort | Delaware, UK, Singapur etc. | Investorenpräferenz, zukünftiger IPO, Gesellschaftsrecht |
| Zwischenholding | Niederlande, Luxemburg, Irland | Vertragsnetzwerk, Substance, EU-Richtlinien |
| OpCo-Form | Zweigniederlassung vs. Tochtergesellschaft | Haftung, Steuern, lokale Lizensierung |
| IP-Eigentum | Zentral vs. lokal | F&E-Standort, Transferpricing, Durchsetzung |
| Treasury / Cash Pooling | Regional vs. global | Währung, Banking, Quellensteuern |

## Anti-Avoidance und Substance

Moderne Strukturen müssen reale wirtschaftliche Aktivität nachweisen:

- Geschäftsführer / Direktoren mit Entscheidungsbefugnis im Holding-Standort.
- Lokale Vorstandssitzungen und Beschlüsse.
- Mitarbeiter, die Finanz-, Rechts- oder IP-Management-Funktionen wahrnehmen.
| Ordentlich dokumentierte Intercompany-Vereinbarungen und Transferpricing-Unterlagen.

Lokale Steuer- und Rechtsberatung vor der Implementierung einer mehrgerichtsständigen Struktur einholen.
`,
    jurisdiction_matrix: `# Gerichtsbarkeitsmatrix

Vergleich wichtiger Gerichtsbarkeiten anhand für die Unternehmensgründung relevanter Faktoren.

## Vergleichsfaktoren

| Faktor | Beschreibung |
|--------|--------------|
| Gründungskosten | Einmalige und jährliche Gebühren |
| Körperschaftsteuersatz | Satz und effektive Belastung |
| Besteuerung von Dividenden | Steuerliche Behandlung für Eigentümer |
| Gründungsaufwand | Zeit, Bürokratie, Remote-Optionen |
| Bankzugang | Schwierigkeit der Kontoeröffnung |
| Rechtssystem | Common Law vs. Zivilrecht; Präzedenzfall-Sicherheit |
| IP-Schutz | Stärke von Marken-, Patent- und Urheberrechtsdurchsetzung |
| Doppelbesteuerungsabkommen | Netzwerk von Abkommen |
| Zugang zu Arbeitskräften | Visaregeln und Arbeitsmarkt |
| Regulatorische Belastung | Berichts-, Prüfungs- und Compliance-Anforderungen |

## Gerichtsbarkeitsvergleich

| Gerichtsbarkeit | Körperschafts-steuer | Aufwand | Banking | IP | DBA | Bestens geeignet für |
|-------------------|----------------------|---------|---------|----|-----|----------------------|
| Deutschland | Körperschaftsteuer + Gewerbesteuer (~30%) | Hoch | Moderat | Sehr stark | Sehr breit | Europäische Heimatgesellschaft, Engineering, B2B |
| Delaware, USA | 21% federal + Bundesstaat | Hoch | Moderat | Sehr stark | Breit | VC-Startups, Börsenunternehmen |
| UK | 25% (Hauptsatz) | Hoch | Moderat | Stark | Breit | Europäischer Hub, englischsprachige Basis |
| Irland | 12,5% (Handel) | Hoch | Moderat | Stark | Breit | EU-HQ, IP-Holding, Tech |
| Niederlande | 25,8% (2024) | Hoch | Moderat | Stark | Sehr breit | Holdinggesellschaften, EU-Vertrieb |
| Luxemburg | Wettbewerbsfähig | Hoch | Moderat | Stark | Sehr breit | Holding, Finanzwesen, Fonds |
| Singapur | 17% | Sehr hoch | Einfach | Stark | Breit | Asien-HQ, Handel, Tech |
| Hongkong | 8,25–16,5% | Sehr hoch | Einfach | Stark | Wachsend | Asien-Gateway, China-Zugang |
| VAE (Freihandelszone) | 0–9% | Hoch | Einfach | Moderat | Wachsend | Regionaler Hub, Steueroptimierung |
| Estland | 20% (aufgeschoben) | Sehr hoch | Einfach | Moderat | Breit | Digitale Nomaden, EU-Entität |
| Kaimaninseln | 0% | Moderat | Moderat | Moderat | Begrenzt | Investmentfonds, Holdingvehikel |

## Auswahlempfehlung

- **Primärer Betriebsmarkt**: Gründen, wo Umsatz und Kunden konzentriert sind.
- **Steuereffizienz**: Nutzen Sie Abkommensnetzwerke und Holdingstrukturen, nicht nur Kopfsatzsteuern.
- **Investorenpräferenz**: US-VCs bevorzugen oft Delaware C-Corp; EU-Investoren akzeptieren lokale Äquivalente.
- **IP-Holding**: IP in eine Gerichtsbarkeit mit starkem IP-Recht und günstigen Steuerabkommen auslagern.
- **Regulatorische Passgenauigkeit**: Finanzdienstleistungen, Gesundheitswesen und Krypto unterliegen sektorspezifischen Lizenzanforderungen.

## Mehrgerichtsbarkeits-Setup

Ein typischer Expansionspfad:

1. **Heimatgesellschaft** im primären Markt.
2. **Betriebstochtergesellschaften** in jedem Land mit Kunden oder Mitarbeitern.
3. **Holdinggesellschaft** in einer für Kapital und Dividenden optimierten Gerichtsbarkeit.
4. **IP-Holdinggesellschaft**, wo IP erstellt, lizenziert oder geschützt wird.

Transferpricing, Substance-Anforderungen und Anti-Avoidance-Regeln immer vorab modellieren.
`,
  },
  brandIdentity: {
    README: `# 03 — Markenidentität

Dieser Abschnitt definiert Markenstrategie, Positionierung, Persönlichkeit, Markenstimme (Tone of Voice) und visuelle Ausrichtung.

## Dateien

- \`brand_positioning_template.md\` — Ausfüllbares Positionierungsframework.
- \`visual_identity_brief.md\` — Briefing für Logo, Farben, Typografie und Bildsprache.
- \`tone_of_voice_guide.md\` — Stimmenprinzipien und Schreibregeln.
- \`brand_guidelines_checklist.md\` — Checkliste für ein vollständiges Brand Book.

## Zweck

Eine vollständige Markenidentität stellt sicher, dass jeder Kundenkontaktpunkt eine konsistente, differenzierte und glaubwürdige Botschaft vermittelt.
`,
    brand_guidelines_checklist: `# Checkliste für Markenleitlinien

Diese Checkliste stellt sicher, dass das Brand Book vollständig und praxistauglich ist.

## Strategie

- [ ] Markenzweck / Mission Statement
- [ ] Vision Statement
- [ ] Kernwerte
- [ ] Positionierungsaussage
- [ ] Zielgruppenprofile
- [ ] Wettbewerbsdifferenzierung
- [ ] Markenversprechen
- [ ] Markensäulen

## Identität

- [ ] Logo und Logo-Variationen
- [ ] Mindestgröße und Schutzraum
- [ ] Falsche Logo-Verwendung
- [ ] Farbpalette (primär, sekundär, neutral)
- [ ] Farbbarrierefreiheit
- [ ] Typografie-System
- [ ] Iconographie-Stil
- [ ] Bild- und Fotostil
- [ ] Illustrationsstil
- [ ] Bewegungs- und Soundprinzipien (falls anwendbar)

## Stimme

- [ ] Tone-of-Voice-Prinzipien
- [ ] Schreibstilregeln
- [ ] Kanalspezifische Ton-Anpassungen
- [ ] Boilerplate und Claims
- [ ] Namenskonventionen für Produkte und Funktionen
- [ ] Inklusive Sprachrichtlinie

## Anwendung

- [ ] Visitenkarte
- [ ] Briefbogen und E-Mail-Signatur
- [ ] Website-Designsystem
- [ ] Social-Media-Vorlagen
- [ ] Präsentationsvorlagen
- [ ] Dokumentenvorlagen
- [ ] Marken-Merchandise
- [ ] Beschilderung und Umweltgrafik
- [ ] App- und Produkt-UI (falls anwendbar)

## Governance

- [ ] Versionshistorie
- [ ] Markenverantwortlicher / Kontakt
- [ ] Abweichungsgenehmigungsprozess
- [ ] Verteilungs- und Zugriffsregeln
- [ ] Review-Zeitplan
- [ ] Asset-Bibliothek

## Freigabe

| Rolle | Name | Freigegeben | Datum |
|-------|------|-------------|-------|
| Brand / Marketing Lead | | | |
| Design Lead | | | |
| Executive Sponsor | | | |
`,
    brand_positioning_template: `# Markenpositionierungsvorlage

Mit dieser Vorlage definieren Sie eine klare, differenzierte Marktposition.

## 1. Markenessenz

In einem Satz: Wofür steht die Marke?

> _Beispiel: Stripe — „Das Bruttosozialprodukt des Internets erhöhen."_

## 2. Zielgruppe

### Primäre Zielgruppe

- Rolle / Demografie:
- Pain Points:
- Ziele:
- Auslöser für Kauf:

### Sekundäre Zielgruppe

- Rolle / Demografie:
- Warum sie wichtig ist:

## 3. Marktkontext

- Kategorie:
- Marktgröße und Wachstum:
- Schlüsseltrends:
- Regulatorische oder ökonomische Kräfte:

## 4. Wettbewerbsumfeld

| Wettbewerber | Positionierung | Stärke | Schwäche |
|--------------|----------------|--------|----------|
| | | | |
| | | | |
| | | | |

## 5. Differenzierung

Was macht diese Marke einzigartig und schwer kopierbar?

- **Funktionale Differenzierung**:
- **Emotionale Differenzierung**:
- **Beweispunkte**:

## 6. Markenversprechen

Das eine Versprechen, das die Marke ihren Kunden gibt:

> _Beispiel: „Wir machen professionelle Analytics für jedes Team in Minuten verfügbar."_

## 7. Positionierungsaussage

Für **[Zielgruppe]** ist **[Markenname]** die/das **[Kategorie]**, die/das **[Hauptnutzen]** bietet, weil **[Glaubwürdiger Grund]**.

## 8. Markensäulen

Drei bis fünf Attribute, die die Markenerfahrung definieren:

1.
2.
3.
4.
5.

## 9. Markenpersönlichkeit

Wenn die Marke eine Person wäre, wie würde sie sich verhalten?

| Eigenschaft | Wie sie sich zeigt |
|-------------|--------------------|
| | |
| | |
| | |

## 10. Customer-Journey-Übersicht

Ordnen Sie die wichtigsten Phasen, in denen die Positionierung verstärkt werden muss:

| Phase | Kundenbedürfnis | Markenaktion |
|-------|-----------------|--------------|
| Bewusstsein | | |
| Erwägung | | |
| Kauf | | |
| Onboarding | | |
| Bindung | | |
| Advocacy | | |
`,
    tone_of_voice_guide: `# Tone-of-Voice-Leitfaden

Regeln dafür, wie die Marke in Text und Sprache kommuniziert.

## 1. Stimmenprinzipien

Definieren Sie 3–5 Prinzipien. Für jedes formulieren Sie, was wir sind und was wir nicht sind.

| Prinzip | Wir sind | Wir sind nicht |
|---------|----------|----------------|
| Beispiel: Klar | Direkt und ohne Fachjargon | Zu technisch oder vage |
| | | |
| | | |
| | | |

## 2. Schreibstil

### Grammatik und Mechanik

- Satzlänge: kurz (≤20 Wörter) / mittel / variiert
- Aktiv vs. Passiv:
- Erste, zweite oder dritte Person?
- Oxford-Komma:
- Groß- und Kleinschreibung: Sentence case / Title Case / alles Kleinbuchstaben
- Datums-, Zeit- und Zahlenformate:

### Wortschatz

- Wörter, die wir verwenden:
- Wörter, die wir vermeiden:
| Fachbegriffe: erklären oder voraussetzen?

### Inklusive Sprache

- Geschlechtsneutrale Begriffe verwenden.
- Redewendungen vermeiden, die sich nicht übersetzen lassen.
| Menschen mit Würde und Angemessenheit beschreiben.
- Barrierefreiheit beachten (Alt-Text, klare Sprache).

## 3. Kanalstimme

| Kanal | Ton-Anpassung | Beispiel |
|-------|-----------------|----------|
| Website | | |
| E-Mail-Marketing | | |
| Social Media | | |
| Support-Chat | | |
| Pressemitteilung | | |
| Interne Notiz | | |

## 4. Dos und Don'ts der Kommunikation

### Dos

- Beginnen Sie mit dem Kundennutzen.
- Verwenden Sie konkrete Beispiele und Daten.
- Seien Sie ehrlich über Einschränkungen.
- Korrekturlesen vor Veröffentlichung.

### Don'ts

- Übertreibungen oder unbelegte Behauptungen.
- Konkurrenz direkt kritisieren.
- Fachjargon ohne Erklärung verwenden.
| Lange Textblöcke ohne Überschriften oder Listen.

## 5. Nachrichtenarchitektur

### Standardbeschreibung (Boilerplate)

Eine 50–75 Wörter umfassende Unternehmensbeschreibung für Presse, Partner und Verzeichnisse.

> _[Boilerplate hier einfügen]_

### Claim-Optionen

1.
2.
3.

### Elevator Pitch

30-sekündige mündliche Zusammenfassung.

> _[Pitch hier einfügen]_

## 6. Review-Checkliste

Vor Veröffentlichung jeder Inhalte:

- [ ] Klingt es wie die Marke?
- [ ] Ist der Kundennutzen klar?
- [ ] Ist die Sprache inklusiv und barrierefrei?
- [ ] Sind Behauptungen korrekt und verteidigbar?
- [ ] Ist der Call-to-Action klar?
`,
    visual_identity_brief: `# Briefing für die visuelle Markenidentität

Eine Vorlage zur Briefing von Designern zu Logo, Farbwelt, Typografie und Bildsprache.

## 1. Markenkontext

- Markenname:
- Branche:
- Zielgruppe:
- Positionierungszusammenfassung:
- Tonfall / Persönlichkeit:

## 2. Logo-Anforderungen

- **Wortmarke, Symbol oder Kombination?**
- **Primäre Anwendungsfälle**: digital, Print, App-Icon, Beschilderung, Merchandise
- **Stilrichtung**: minimalistisch, kraftvoll, verspielt, premium, technisch
- **Unbedingt enthaltene Elemente**:
- **Unbedingt zu vermeidende Elemente**:
- **Konkurrenzlogos, denen man nicht ähnlich sehen sollte**:

## 3. Farbpalette

| Rolle | Farbname | HEX | Verwendung |
|-------|----------|-----|------------|
| Primärfarbe | | | |
| Sekundärfarbe | | | |
| Akzentfarbe | | | |
| Neutral | | | |
| Dunkel | | | |
| Hell | | | |

### Farbverwendungsregeln

- Minimale Kontrastverhältnisse für Barrierefreiheit:
- Erlaubte / verbotene Hintergrundkombinationen:
- Wann die Akzentfarbe eingesetzt wird:

## 4. Typografie

| Rolle | Schriftfamilie | Schnitte | Verwendung |
|-------|----------------|----------|------------|
| Überschriften | | | |
| Fließtext | | | |
| Akzent / Monospace | | | |

### Typografische Regeln

- Basisschriftgröße und Zeilenabstand:
- Überschriftenskala:
- Maximale Zeilenlängen für Lesbarkeit:

## 5. Bildsprache

- Fotostil: authentisch, inszeniert, abstrakt, dokumentarisch
- Illustrationsstil: flach, 3D, Strichzeichnung, isometrisch
- Icon-Stil: outline, gefüllt, Duoton
- Themen und Stimmung:
- Vielfalt und Inklusion:

## 6. Bewegung und Interaktion (Digital)

- Animationstil: dezent, verspielt, präzise
- Übergänge und Mikrointeraktionen:
- Sound- oder Haptik-Erwägungen:

## 7. Anwendungsbeispiele

Erforderliche Mockups:

- [ ] Visitenkarte
- [ ] Briefbogen
- [ ] Website-Startseite
- [ ] Mobiles App-Icon
- [ ] Social-Media-Profil
- [ ] Präsentationsvorlage
- [ ] Marken-Merchandise

## 8. Deliverables

- [ ] Logo-Dateien (SVG, PNG, EPS)
- [ ] Farbpaletten-Spezifikation
- [ ] Schriftdateien und Lizenz
- [ ] Icon-Set
- [ ] Foto- und Illustrationsbibliothek
- [ ] Markenleitlinien als PDF
- [ ] Design-System / Komponentenbibliothek
`,
  },
  sops: {
    README: `# 04 — Standardarbeitsanweisungen (SOPs)

Dieser Abschnitt enthält wiederverwendbare SOP-Vorlagen und anpassbare Verfahren für alle wesentlichen Unternehmensbereiche.

## Dateien

- \`sop_template.md\` — Standard-SOP-Format für alle Verfahren.
- \`sop_index.md\` — Masterliste aller SOPs nach Funktion und Verantwortlichem.
- \`hr/\` — Einstellung, Onboarding, Offboarding, Leistungsmanagement, Urlaub.
- \`finance/\` — Rechnungsstellung, Lohnbuchhaltung, Spesenabrechnung, Monatsabschluss.
- \`operations/\` — Beschaffung, Lieferantenmanagement, Qualitätskontrolle.
- \`marketing/\` — Kampagnenplanung, Content-Freigabe, Social Media.
- \`legal/\` — Vertragsprüfung, Betroffenenrechte, IP-Anmeldungen.
- \`it/\` — Zugangskontrolle, Vorfallreaktion, Software-Beschaffung.

## Governance

Alle SOPs folgen der Dokumentenkontrollrichtlinie in \`06-governance/\` und werden im dort definierten Rhythmus überprüft.
`,
    finance: {
      'sop-fin-001-invoicing': `# SOP-FIN-001: Rechnungsstellung und Forderungsmanagement

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-FIN-001 |
| Titel | Rechnungsstellung und Forderungsmanagement |
| Funktion | Finanzen |
| Verantwortlich | Finanzmanager |
| Prüfer | CFO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Sichere, zeitnahe Rechnungsstellung und wirksames Einziehen offener Forderungen.

## 2. Geltungsbereich

Gilt für alle Kundenrechnungen und Zahlungseingänge.

## 3. Rollen und Verantwortlichkeiten

| Rolle | Verantwortlichkeit |
|-------|--------------------|
| Vertrieb / Account Management | Freigabe von Meilensteinen, Lieferungen und Abrechnungskontakten |
| Finanzen | Erstellung von Rechnungen, Zahlungszuordnung, Forderungsmanagement |
| Recht | Bearbeitung von Zahlungsstreitigkeiten und Eskalationen |

## 4. Ablauf

1. **Auslöser**
   - Vertrieb bestätigt Meilenstein, Lieferung oder Abonnementverlängerung zur Abrechnung.
   - Abrechnungsdaten werden im Finanzsystem erfasst oder synchronisiert.

2. **Rechnungserstellung**
   - Finanzen prüft Preise, Steuern (USt/VAT) und Bestellnummern.
   - Rechnung wird innerhalb eines Werktages erstellt und versendet.

3. **Zahlungsverfolgung**
   - Finanzen überwacht Fälligkeiten und ordnet Zahlungseingänge zu.
   - Nicht zugeordnete Zahlungen werden innerhalb von zwei Werktagen geklärt.

4. **Mahnwesen**
   - Automatisierte Erinnerungen werden in definierten Intervallen versendet.
   - Überfällige Rechnungen werden nach 15 Tagen an den Account Manager eskaliert.
   - Recht wird bei 60+ Tagen oder vorab definierter Schwelle eingeschaltet.

5. **Abstimmung**
   - Monatliche Debitorenalterung wird geprüft.
   - Wertberichtigungen für uneinbringliche Forderungen werden bewertet.

## 5. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Rechnungen innerhalb SLA erstellt | ≥95% | Monatlich |
| DSO (Days Sales Outstanding / Debitorenlaufzeit) | ≤30 Tage | Monatlich |
| Forderungsausfallquote | ≤1% | Quartalsweise |
`,
      'sop-fin-002-expenses': `# SOP-FIN-002: Spesenabrechnung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-FIN-002 |
| Titel | Spesenabrechnung |
| Funktion | Finanzen |
| Verantwortlich | Finanzmanager |
| Prüfer | CFO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Erstattung genehmigter Geschäftsausgaben an Mitarbeitende zeitnah und korrekt.

## 2. Geltungsbereich

Gilt für alle Mitarbeitenden, die geschäftliche Ausgaben tätigen.

## 3. Ablauf

1. **Ausgabe entsteht**
   - Mitarbeitende zahlt eine förderfähige Geschäftsausgabe.
   - Originalbeleg wird aufbewahrt.

2. **Einreichung**
   - Mitarbeitende reicht Spesenabrechnung innerhalb von 30 Tagen ein.
   - Abrechnung enthält Datum, Betrag, Kategorie, Geschäftszweck und Beleg.

3. **Genehmigung**
   - Vorgesetzte prüft und genehmigt innerhalb von fünf Werktagen.
   - Finanzen verifiziert Richtlinienkonformität und Beleggültigkeit.

4. **Erstattung**
   - Genehmigte Spesen werden in der nächsten Lohnbuchhaltung oder Spesenläufe ausgezahlt.
   - Mitarbeitende erhält Zahlungsbestätigung.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Erstattungsdurchlaufzeit | ≤10 Werktage | Monatlich |
| Abgelehnte Spesenquote | ≤5% | Monatlich |
`,
      'sop-fin-003-payroll': `# SOP-FIN-003: Lohnbuchhaltung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-FIN-003 |
| Titel | Lohnbuchhaltung |
| Funktion | Finanzen |
| Verantwortlich | Lohnbuchhalter |
| Prüfer | CFO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Mitarbeitende pünktlich und korrekt zu bezahlen sowie steuerliche und melderechtliche Pflichten zu erfüllen.

## 2. Geltungsbereich

Gilt für alle im Lohn- und Gehaltsverzeichnis erfassten Mitarbeitenden.

## 3. Ablauf

1. **Datenerfassung**
   - Personalwesen übermittelt Neueinstellungen, Kündigungen und Änderungen.
   - Vorgesetzte genehmigen Arbeitszeiterfassung und variable Vergütungen.

2. **Validierung**
   - Lohnbuchhaltung prüft Daten auf Vollständigkeit und Richtigkeit.
   - Steuerklasse, Sozialabgaben und Lohnpfändungsänderungen werden eingepflegt.

3. **Lohnabrechnung**
   - Lohnlauf wird im Lohnbuchhaltungssystem ausgeführt.
   - Vorabmeldungen werden von Finanzen geprüft und freigegeben.

4. **Zahlung und Buchung**
   - Gehälter werden am vereinbarten Zahltag überwiesen.
   - Buchungen in der Finanzbuchhaltung werden erstellt.

5. **Meldung**
   - Lohnsteuer- und Sozialversicherungsmeldungen werden fristgerecht übermittelt.
   - Gehaltsabrechnungen werden den Mitarbeitenden zugänglich gemacht.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Pünktliche Lohnzahlung | 100% | Pro Lauf |
| Lohnabrechnungsgenauigkeit | ≥99% | Quartalsweise |
| Melderechtliche Compliance | 100% | Pro Meldung |
`,
      'sop-fin-004-month-end': `# SOP-FIN-004: Monatsabschluss

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-FIN-004 |
| Titel | Monatsabschluss |
| Funktion | Finanzen |
| Verantwortlich | Controller |
| Prüfer | CFO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Zeitnahe und korrekte monatliche Abschlussberichte erstellen.

## 2. Geltungsbereich

Gilt für alle Gesellschaften, die in die konsolidierte Berichterstattung einbezogen werden.

## 3. Ablauf

1. **Vorbereitung**
   - Kalender und Aufgabenzuordnung bestätigen.
   - Nebenbuch-Einträge einfrieren.

2. **Umsatzrealisierung**
   - Verträge prüfen und Umsatz nach Bilanzierungsrichtlinie erfassen.
   - Debitoren und passive Erträge abstimmen.

3. **Aufwandsrückstellungen**
   - Rückstellungen und Abgrenzungen buchen.
   - Kreditoren und Kreditkarten abstimmen.

4. **Lohn und Kopfzahlen**
   - Lohnkosten pro Abteilung und Gesellschaft bestätigen.
   - Kopfzahlenänderungen abstimmen.

5. **Anlagevermögen und Abschreibungen**
   - Zugänge, Abgänge und Abschreibungen buchen.

6. **Intercompany-Abstimmung**
   - Konzerninterne Salden abstimmen und in der Konsolidierung eliminieren.

7. **Prüfung und Freigabe**
   - Controller prüft vorläufige Abschlüsse.
   - CFO genehmigt finale Abschlüsse.

8. **Verteilung und Archivierung**
   - Abschlusspaket an Führungsteam verteilen.
   - Arbeitsunterlagen archivieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Abschlussdurchlaufzeit | ≤10 Werktage | Monatlich |
| Ungelöste Abstimmungsdifferenzen | 0 | Monatlich |
`,
      'sop-fin-005-procurement': `# SOP-FIN-005: Bestellung und Beschaffung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-FIN-005 |
| Titel | Bestellung und Beschaffung |
| Funktion | Finanzen |
| Verantwortlich | Beschaffungsleitung |
| Prüfer | CFO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Ausgabenkontrolle, ordnungsgemäße Genehmigungen und zuverlässige Lieferantenbeziehungen sicherstellen.

## 2. Geltungsbereich

Gilt für alle Einkäufe oberhalb eines definierten Schwellenwerts. Niedrigwertige Einkäufe können einer separaten Richtlinie folgen.

## 3. Ablauf

1. **Anforderung**
   - Anfordernde Person erstellt Bedarfsanmeldung mit Begründung, Budgetcode und Lieferant.

2. **Genehmigung**
   - Vorgesetzte prüft Bedarf und Budget.
   - Finanzen genehmigt, falls Betrag Delegationsgrenze überschreitet.

3. **Bestellung**
   - Beschaffung stellt nummerierte Bestellung an Lieferanten.
   - Bestellung wird im Beschaffungssystem erfasst.

4. **Wareneingang**
   - Waren / Dienstleistungen werden von der anfordernden Person entgegengenommen.
   - Wareneingang wird erfasst und Rechnung mit Bestellung verknüpft.

5. **Zahlung**
   - Drei-Wege-Match (Bestellung, Wareneingang, Rechnung) verifiziert.
   - Rechnung wird zahlungsgemäß den Bedingungen zur Zahlung freigegeben.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Bestellabdeckungsquote | ≥90% des Ausgabevolumens | Monatlich |
| Drei-Wege-Match-Abweichungen | ≤5% | Monatlich |
| Beschaffungsdurchlaufzeit | ≤7 Tage | Monatlich |
`,
    },
    hr: {
      'sop-hr-001-recruitment': `# SOP-HR-001: Rekrutierung und Einstellung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-HR-001 |
| Titel | Rekrutierung und Einstellung |
| Funktion | Personalwesen |
| Verantwortlich | HR-Manager |
| Prüfer | Head of People |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Konsistente, faire und compliant Einstellungspraktiken, die qualifizierte Kandidat:innen anziehen und ein positives Candidate Experience bieten.

## 2. Geltungsbereich

Gilt für alle Festanstellungen, Teilzeit- und befristete Einstellungen. Gilt nicht für Auftragnehmer oder Praktikant:innen, sofern keine Umwandlung erfolgt.

## 3. Begriffsdefinitionen

| Begriff | Definition |
|---------|------------|
| Stellenausschreibung | Genehmigter Bedarf zur Besetzung einer Position |
| Hiring Manager | Führungskraft, die die Einstellung beantragt |
| Arbeitsvertragsangebot | Formelles Anstellungsangebot |

## 4. Rollen und Verantwortlichkeiten

| Rolle | Verantwortlichkeit |
|-------|--------------------|
| Hiring Manager | Rolle definieren, Interview führen, Empfehlung abgeben |
| HR | Stelle ausschreiben, vorselektieren, koordinieren, Angebot aussprechen |
| Finanzen | Budget und Vergütungsband bestätigen |
| Recht | Arbeitsverträge und Background-Check-Richtlinie prüfen |

## 5. Ablauf

1. **Stellenausschreibung**
   - Hiring Manager reicht Stellenanforderung mit Titel, Level, Aufgaben und Budget ein.
   - Finanzen bestätigt Budgetverfügbarkeit.
   - HR genehmigt Ausschreibung und weist Recruiter zu.

2. **Sourcing**
   - HR erstellt Stellenanzeige nach genehmigter Vorlage.
   - Stelle wird auf Karriereseite, relevanten Jobbörsen und internen Kanälen veröffentlicht.
   - Recruiter sourct passiv Kandidat:innen bei Bedarf.

3. **Vorselektion**
   - Recruiter prüft Bewerbungen gegen Mindestanforderungen.
   - Kurzlistierte Kandidat:innen absolvieren Telefon- oder Videovorstellung.
   - Beste Kandidat:innen gehen in die Hiring-Manager-Interviews.

4. **Interviewphase**
   - Hiring Manager führt strukturierte Interviews mit dem Interviewpanel durch.
   - Panel verwendet kompetenzbasierte Scorecards.
   - HR koordiniert Referenzabklärungen für Finalkandidat:innen.

5. **Entscheidung**
   - Interviewpanel reicht Scorecards ein.
   - Hiring Manager und HR wählen Finalkandidat:in aus.
   - HR prüft Arbeitserlaubnis und bereitet Background-Check-Einwilligung vor.

6. **Angebot**
   - HR erstellt Arbeitsvertragsangebot innerhalb des genehmigten Vergütungsbands.
   - Recht prüft Angebot bei nicht-standardisierten Konditionen.
   - Hiring Manager oder HR sprechen Angebot mündlich aus, gefolgt von schriftlichem Angebot.

7. **Vor-Onboarding**
   - Kandidat:in nimmt Angebot an und sendet unterschriebene Unterlagen zurück.
   - HR initiiert Background-Check und sammelt erforderliche Unterlagen.
   - IT wird benachrichtigt, um Ausstattung und Konten vorzubereiten.

## 6. Erforderliche Eingaben und Ergebnisse

| Eingabe | Quelle | Ergebnis | Ziel |
|---------|--------|----------|------|
| Stellenanforderung | Hiring Manager | Genehmigte Ausschreibung | HR |
| Stellenanzeige | HR | Veröffentliches Inserat | Öffentliche Kanäle |
| Interview-Scorecards | Panel | Einstellungsempfehlung | Hiring Manager / HR |
| Angebotsgenehmigung | HR / Finanzen | Unterschriebener Arbeitsvertrag | Personalakte |

## 7. Werkzeuge, Systeme und Referenzen

- Applicant Tracking System (ATS)
- HR-Informationssystem (HRIS)
| Arbeitsvertragsvorlage
- Background-Check-Dienstleister
| Verwandte SOP: SOP-HR-002 Neues Mitarbeiter-Onboarding

## 8. Ausnahmen und Eskalation

- Angebote oberhalb des genehmigten Bands erfordern CFO- oder CEO-Genehmigung.
- Abweichungen von der standardisierten Background-Check-Richtlinie erfordern Rechtsfreigabe.
- Streitigkeiten über Kandidatenauswahl werden an Head of People eskaliert.

## 9. Kennzahlen und KPIs

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Time to fill | ≤45 Tage | Monatlich |
| Offer-Acceptance-Rate | ≥85% | Quartalsweise |
| Candidate Satisfaction | ≥4,0 / 5 | Quartalsweise |
`,
      'sop-hr-002-onboarding': `# SOP-HR-002: Neues Mitarbeiter-Onboarding

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-HR-002 |
| Titel | Neues Mitarbeiter-Onboarding |
| Funktion | Personalwesen |
| Verantwortlich | HR-Manager |
| Prüfer | Head of People |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Neue Mitarbeitende in das Unternehmen integrieren, mit den nötigen Werkzeugen und Wissen ausstatten und die Zeit bis zur vollen Produktivität verkürzen.

## 2. Geltungsbereich

Gilt für alle Neueinstellungen, die vom Arbeitsvertragsangebot in den aktiven Mitarbeiterstatus übergehen.

## 3. Begriffsdefinitionen

| Begriff | Definition |
|---------|------------|
| Onboarding-Plan | 30-60-90-Tage-Roadmap für neue Mitarbeitende |
| Buddy | Peer, der neue Mitarbeitende im Unternehmen unterstützt |

## 4. Rollen und Verantwortlichkeiten

| Rolle | Verantwortlichkeit |
|-------|--------------------|
| HR | Koordination von Onboarding, Unterlagen und Orientierung |
| IT | Bereitstellung von Konten, Ausstattung und Zugriffen |
| Hiring Manager | Festlegung von Rollenerwartungen und Onboarding-Plan |
| Buddy | Informelle Unterstützung und kulturelle Einführung |
| Lohnbuchhaltung | Eintrag in Lohnbuchhaltungssystem |

## 5. Ablauf

1. **Vor Tag 1**
   - HR bestätigt Startdatum und sendet Willkommens-E-Mail.
   - IT bereitet Laptop, Peripherie und Systemkonten vor.
   - Hiring Manager erstellt Onboarding-Plan und plant Meetings für die erste Woche.
   - Buddy wird zugewiesen und per E-Mail vorgestellt.

2. **Tag 1**
   - HR führt Orientierung durch: Unternehmensübersicht, Werte, Richtlinien, Benefits.
   - Mitarbeitende vervollständigt erforderliche Dokumente und Bankverbindung.
   - IT übergibt Ausstattung und prüft Zugriffe.
   - Hiring Manager führt rollenspezifisches Willkommensgespräch.

3. **Woche 1**
   - Neue Mitarbeitende trifft wichtige Stakeholder.
   - Pflichtschulungen (Sicherheit, Compliance, Antidiskriminierung) werden abgeschlossen.
   - Rollenverantwortung und Ziele für den ersten Monat werden besprochen.

4. **30-Tage-Check-in**
   - Hiring Manager prüft Fortschritt gegen Onboarding-Plan.
   - HR erfragt Erfahrung und löst Probleme.

5. **60-Tage-Check-in**
   - Vertiefung der Rollenziele und übergreifende Projekte.
   - Entwicklungsbedarfe identifizieren.

6. **90-Tage-Check-in**
   - Formelle Probezeit- / Onboarding-Abschlussbewertung.
   - Fortlaufende Leistungsziele setzen.

## 6. Erforderliche Eingaben und Ergebnisse

| Eingabe | Quelle | Ergebnis | Ziel |
|---------|--------|----------|------|
| Unterschriebener Vertrag | HR | Aktiver Mitarbeiterdatensatz | HRIS |
| Onboarding-Plan | Hiring Manager | Abgeschlossene Checkliste | HR |
| Schulungsnachweise | LMS | Compliance-Abschluss | HR / Compliance |

## 7. Werkzeuge, Systeme und Referenzen

- HRIS
- Learning-Management-System (LMS)
- IT-Asset-Tracking
- Onboarding-Checkliste

## 8. Ausnahmen und Eskalation

- Remote-Einstellungen: IT versendet Ausstattung und führt Einrichtung per Video-Call durch.
- Zugriffsprobleme: Innerhalb von 4 Stunden nach Meldung an IT eskalieren.

## 9. Kennzahlen und KPIs

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Onboarding-Abschluss | 100% bis Tag 30 | Monatlich |
| 90-Tage-Retention | ≥90% | Quartalsweise |
| Time to productivity | Pro Rolle definiert | Quartalsweise |
`,
      'sop-hr-003-offboarding': `# SOP-HR-003: Mitarbeiter-Offboarding

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-HR-003 |
| Titel | Mitarbeiter-Offboarding |
| Funktion | Personalwesen |
| Verantwortlich | HR-Manager |
| Prüfer | Head of People |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Mitarbeitende-Austritte respektvoll, compliant und sicher gestalten und dabei Unternehmensvermögen sowie Wissen schützen.

## 2. Geltungsbereich

Gilt für alle freiwilligen und unfreiwilligen Kündigungen, Eigenkündigungen und Renteneintritte.

## 3. Rollen und Verantwortlichkeiten

| Rolle | Verantwortlichkeit |
|-------|--------------------|
| HR | Koordination des Austritts, Unterlagen und Abfindung |
| Vorgesetzte | Kommunikation der Entscheidung, Übergabe planen |
| IT | Widerruf von Zugriffen und Rücknahme von Ausstattung |
| Finanzen | Abrechnung der Schlusszahlung und Spesen |
| Recht | Beratung bei sensiblen oder risikoreichen Austritten |

## 4. Ablauf

1. **Kündigung oder Entscheidung**
   - Eigenkündigung: Mitarbeitende reicht schriftliche Kündigung ein.
   - Betriebsbedingte / personenbedingte Kündigung: Vorgesetzte und HR stimmen Begründung und Zeitpunkt ab.

2. **Austrittskommunikation**
   - Vorgesetzte und HR informieren relevante Stakeholder.
   - Team wird respektvoll und professionell informiert.

3. **Übergabeplanung**
   - Vorgesetzte dokumentiert laufende Arbeiten und weist Übergaben zu.
   - Mitarbeitende führt Wissenstransfer-Sitzungen durch.

4. **Zugriffs- und Asset-Rücknahme**
   - IT deaktiviert Systemzugriffe am letzten Arbeitstag.
   - Mitarbeitende gibt Laptop, Ausweise, Schlüssel und Firmeneigentum zurück.
   - Finanzen sperrt Firmenkreditkarten.

5. **Schlusszahlung und Benefits**
   - HR berechnet Schlusszahlung, Resturlaub und Ansprüche.
   - Benefits werden gekündigt; ggf. Mitteilung an die Agentur für Arbeit / Health Insurance (lokales Äquivalent).

6. **Exit-Interview**
   - HR führt freiwilliges Exit-Interview durch.
   - Feedback wird aggregiert an Führungsteam berichtet.

7. **Archivierung und Abschluss**
   - Personalakte wird laut Aufbewahrungspolitik archiviert.
   - Offboarding-Checkliste wird von HR, IT und Vorgesetzten abgezeichnet.

## 5. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Zugriff widerrufen bis Geschäftsschluss | 100% | Pro Austritt |
| Exit-Interview-Teilnahme | ≥70% freiwillige Austritte | Monatlich |
`,
      'sop-hr-004-performance': `# SOP-HR-004: Leistungsmanagement

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-HR-004 |
| Titel | Leistungsmanagement |
| Funktion | Personalwesen |
| Verantwortlich | HR-Manager |
| Prüfer | Head of People |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Individuelle Leistung mit Unternehmenszielen ausrichten, regelmäßiges Feedback geben und die Entwicklung von Mitarbeitenden unterstützen.

## 2. Geltungsbereich

Gilt für alle Mitarbeitenden.

## 3. Ablauf

1. **Zielvereinbarung**
   - Vorgesetzte und Mitarbeitende definieren SMART-Ziele innerhalb von 30 Tagen nach Periodenbeginn.
   - Ziele werden im Leistungssystem dokumentiert.

2. **Laufendes Feedback**
   - Vorgesetzte führen regelmäßige 1:1-Coachings.
   - Mitarbeitende und Peers geben Feedback bei Gelegenheit.

3. **Mid-Year-Review**
   - Vorgesetzte und Mitarbeitende prüfen Zielerreichung.
   - Ziele werden bei Prioritätsänderungen angepasst.

4. **Jahresgespräch**
   - Mitarbeitende führt Selbsteinschätzung durch.
   - Vorgesetzte bewertet Leistung und Verhalten anhand von Kompetenzen.
   - Bewertung und Entwicklungsplan werden besprochen.

5. **Entwicklungsplanung**
   - Schulungen, Stretch-Aufgaben oder Mentoring identifizieren.
   - Entwicklungsmaßnahmen dokumentieren.

6. **Kalibrierung und Vergütung**
   - Führungsteam kalibriert Bewertungen über Teams hinweg.
   - Vergütungsanpassungen werden nach Budget und Richtlinie verarbeitet.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Zielerreichungsrate | ≥80% | Jährlich |
| Review-Abschluss | 100% | Jährlich |
| Entwicklungspläne | ≥90% | Jährlich |
`,
      'sop-hr-005-leave': `# SOP-HR-005: Urlaubs- und Abwesenheitsanträge

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-HR-005 |
| Titel | Urlaubs- und Abwesenheitsanträge |
| Funktion | Personalwesen |
| Verantwortlich | HR-Manager |
| Prüfer | Head of People |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Abwesenheitsanträge konsistent verwalten und ausreichende Besetzung sicherstellen.

## 2. Geltungsbereich

Gilt für alle bezahlten und unbezahlten Abwesenheitsarten gemäß lokaler Richtlinie und gesetzlicher Vorgaben.

## 3. Ablauf

1. **Antrag**
   - Mitarbeitende reicht Urlaubsantrag über HRIS oder genehmigtes Formular ein.
   - Antrag enthält Daten, Abwesenheitsart und Vertretungsplan.

2. **Genehmigung**
   - Vorgesetzte prüft Antrag auf Besetzung und Arbeitslast.
   - Vorgesetzte genehmigt oder lehnt innerhalb von zwei Werktagen ab.
   - HR wird bei längeren Abwesenheiten oder gesetzlichen Ansprüchen informiert.

3. **Erfassung**
   - Genehmigte Abwesenheit wird im Urlaubs Kontingent erfasst.
   - Teamkalender wird aktualisiert.

4. **Rückkehr**
   - Mitarbeitende bestätigt Rückkehrdatum.
   - Vorgesetzte führt bei langen Abwesenheiten ein Rückkehrgespräch.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Antwortzeit auf Urlaubsanträge | ≤2 Werktage | Monatlich |
| Resturlaubs-Compliance | Gemäß lokalem Recht | Jährlich |
`,
    },
    it: {
      'sop-it-001-access-control': `# SOP-IT-001: Benutzerzugriffskontrolle und Bereitstellung

SOP-IT-001: Benutzerzugriffskontrolle und Bereitstellung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-IT-001 |
| Titel | Benutzerzugriffskontrolle und Bereitstellung |
| Funktion | IT / Sicherheit |
| Verantwortlich | IT-Manager |
| Prüfer | CISO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Systemzugriff basierend auf Rolle und dem Prinzip der geringsten Berechtigung vergeben, ändern und entziehen.

## 2. Geltungsbereich

Gilt für alle Benutzerkonten, Systeme, Anwendungen und physischen Zugänge.

## 3. Ablauf

1. **Zugriffsantrag**
   - Vorgesetzte reicht Zugriffsantrag mit Begründung und Rolle ein.

2. **Genehmigung**
   - Systemverantwortliche oder Sicherheit genehmigen anhand der Rollenmatrix.
   - Hochriskanter Zugriff erfordert zusätzliche Genehmigung.

3. **Bereitstellung**
   - IT erstellt Konto und weist passende Gruppen und Berechtigungen zu.
   - Multi-Faktor-Authentifizierung wird dort erzwungen, wo verfügbar.

4. **Review**
   - Zugriff für sensible Systeme quartalsweise überprüfen.
   - Vorgesetzte bescheinigen jährlich die Zugriffe ihrer Mitarbeitenden.

5. **Änderung**
   - Rollenänderungen lösen Zugriffsänderungsantrag aus.
   - IT passt Berechtigungen innerhalb eines Werktages an.

6. **Entzug**
   - HR informiert IT bei Austritt.
   - Zugriff wird am letzten Arbeitstag oder sofort bei Kündigung entzogen.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Bereitstellungsdauer | ≤1 Werktag | Monatlich |
| Entzug bis Geschäftsschluss | 100% | Pro Austritt |
| Zugriffsbescheinigungs-Abschluss | 100% | Jährlich |
`,
      'sop-it-002-software-procurement': `# SOP-IT-002: Software-Beschaffung und -genehmigung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-IT-002 |
| Titel | Software-Beschaffung und -genehmigung |
| Funktion | IT |
| Verantwortlich | IT-Manager |
| Prüfer | CISO / CFO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Software-Einkäufe managen, Sicherheitsprüfung sicherstellen und Schatten-IT kontrollieren.

## 2. Geltungsbereich

Gilt für alle SaaS-, On-Premise-Software und Tools, die geschäftlich genutzt werden.

## 3. Ablauf

1. **Anfrage**
   - Anfordernde Person reicht Software-Anfrage mit Geschäftsbedarf, Nutzer:innen und Budget ein.

2. **Sicherheitsprüfung**
   - IT / Sicherheit bewertet Datenverarbeitung, SSO, MFA und Compliance.
   - Hochriskante Anbieter erfordern Sicherheitsfragebogen oder Auftragsverarbeitungsvertrag (AVV).

3. **Budgetgenehmigung**
   - Finanzen bestätigt Budget und Preisgestaltung.
   - Beschaffung prüft Vertragskonditionen.

4. **Beschaffung**
   - IT oder Beschaffung erwirbt Lizenz.
   - Tool wird in Software-Inventar erfasst.

5. **Bereitstellung**
   - IT stellt Nutzer:innen bereit und integriert ggf. in Identity Provider.
   - Anfordernde Person bestätigt Funktionalität.

6. **Verlängerung und Außerbetriebnahme**
   - Verlängerungstermine verfolgen.
   | Nicht genutzte Lizenzen kündigen.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Sicherheitsprüfungsdauer | ≤3 Werktage | Monatlich |
| Unapproved Software erkannt | ≤2 pro Quartal | Quartalsweise |
`,
      'sop-it-003-incident-response': `# SOP-IT-003: Vorfallreaktion und Eskalation

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-IT-003 |
| Titel | Vorfallreaktion und Eskalation |
| Funktion | IT / Sicherheit |
| Verantwortlich | CISO |
| Prüfer | CTO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Schnell auf IT-Sicherheitsvorfälle reagieren und geschäftlichen Schaden minimieren.

## 2. Geltungsbereich

Gilt für alle Cyber-Sicherheitsvorfälle, Ausfälle durch Sicherheitsereignisse und vermutete Datenpannen.

## 3. Ablauf

1. **Erkennung**
   - Alarme, Meldungen und Anomalien überwachen.
   - Vorfall im Ticketsystem erfassen.

2. **Ersteinschätzung**
   - Schweregrad klassifizieren: Niedrig, Mittel, Hoch, Kritisch.
   - Vorfallreaktionsteam benachrichtigen.

3. **Containment**
   - Betroffene Systeme isolieren.
   - Logs und Beweise sichern.

4. **Beseitigung**
   - Bedrohungsakteur oder Ursache entfernen.
   | Schwachstellen patchen.

5. **Wiederherstellung**
   - Systeme aus sauberen Backups wiederherstellen.
   - Integrität prüfen, bevor Produktion freigegeben wird.

6. **Kommunikation**
   - Führungsteam und betroffene Nutzer:innen entsprechend informieren.
   - Recht für regulatorische Meldungen koordinieren.

7. **Post-Vorfall-Review**
   - Vorfallsbericht verfassen.
   - Kontrollen und SOPs aktualisieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Mean Time to Detect | ≤4 Stunden | Pro Vorfall |
| Mean Time to Respond | ≤1 Stunde kritisch | Pro Vorfall |
`,
      'sop-it-004-backup': `# SOP-IT-004: Backup und Disaster Recovery

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-IT-004 |
| Titel | Backup und Disaster Recovery |
| Funktion | IT |
| Verantwortlich | IT-Manager |
| Prüfer | CTO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Sicherstellen, dass Daten gesichert und im Verlust- oder Katastrophenfall wiederherstellbar sind.

## 2. Geltungsbereich

Gilt für alle kritischen Geschäftsdaten, Systeme und Konfigurationen.

## 3. Ablauf

1. **Backup-Plan**
   - RPO (Recovery Point Objective / maximal tolerierbarer Datenverlust) und RTO (Recovery Time Objective / maximal tolerierbare Ausfallzeit) pro System definieren.
   - Automatisierte Backups entsprechend Plan konfigurieren.

2. **Backup-Verifizierung**
   - Backup-Jobs täglich überwachen.
   - Fehlgeschlagene Backups untersuchen und wiederholen.

3. **Offsite / Cloud-Speicher**
   - Backups geografisch getrennt speichern.
   | Daten bei Übertragung und Speicherung verschlüsseln.

4. **Aufbewahrung**
   - Aufbewahrungsrichtlinie gemäß Datenklassifizierung anwenden.
   - Wiederherstellungsverfahren quartalsweise testen.

5. **Disaster Recovery**
   - Disaster-Recovery-Plan aktivieren, wenn Primärsysteme ausfallen.
   - Runbook zur Wiederherstellung von Diensten nach Priorität befolgen.
   - Stakeholder über Status informieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Backup-Erfolgsrate | ≥99% | Wöchentlich |
| Restore-Tests bestanden | 100% | Quartalsweise |
| RTO (Recovery Time Objective / maximal tolerierbare Ausfallzeit) erreicht | Pro System-SLA | Jährlicher DR-Test |
`,
      'sop-it-005-asset-management': `# SOP-IT-005: IT-Asset-Management

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-IT-005 |
| Titel | IT-Asset-Management |
| Funktion | IT |
| Verantwortlich | IT-Manager |
| Prüfer | CFO / CTO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

IT-Assets während ihres gesamten Lebenszyklus erfassen, warten und entsorgen.

## 2. Geltungsbereich

Gilt für alle firmeneigenen Hardware-, Softwarelizenzen und Mobilgeräte.

## 3. Ablauf

1. **Beschaffung**
   - Asset-Anforderung gemäß IT-Beschaffungs-SOP genehmigt.
   - Asset wird empfangen und im Asset-Register erfasst.

2. **Bereitstellung**
   - Asset konfiguriert und Nutzer:in zugewiesen.
   - Nutzer:in unterschreibt Annahme- und Nutzungsrichtlinie.

3. **Wartung**
   - Garantie, Support und Refresh-Zyklus verfolgen.
   | Patches und Updates einspielen.

4. **Veränderung**
   | Weitergaben zwischen Nutzer:innen oder Standorten dokumentieren.
   - Asset-Register aktualisieren.

5. **Entsorgung**
   - Daten vor Entsorgung oder Wiederverwendung sicher löschen.
   - Entsorgung über zugelassenen E-Waste-Anbieter.
   - Aus Asset-Register entfernen.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Asset-Register-Genauigkeit | ≥98% | Quartalsweise |
| Geräte vor Entsorgung gewischt | 100% | Pro Entsorgung |
`,
    },
    legal: {
      'sop-leg-001-contracts': `# SOP-LEG-001: Vertragsprüfung und -genehmigung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-LEG-001 |
| Titel | Vertragsprüfung und -genehmigung |
| Funktion | Recht |
| Verantwortlich | Legal Counsel |
| Prüfer | General Counsel |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Verträge einheitlich prüfen, verhandeln und genehmigen.

## 2. Geltungsbereich

Gilt für alle Verträge, Änderungen und wesentlichen Geschäftsbedingungen.

## 3. Ablauf

1. **Eingang**
   - Anfordernde Person reicht Vertragsantrag mit Geschäftskontext, Laufzeit und Wert ein.

2. **Triage**
   - Recht weist Risikostufe und Prüfer zu.
   - Standardisierte Niedrigwertverträge können vorgenehmigte Vorlagen nutzen.

3. **Prüfung**
   - Recht prüft Haftung, Indemnisierung, IP, Kündigung und Compliance.
   - Finanzen prüft Zahlungsbedingungen.
   - Sicherheit prüft Datenschutzklauseln bei Bedarf.

4. **Verhandlung**
   - Recht oder autorisierte Verhandler:in führt Änderungsverhandlungen.
   - Wesentliche Zugeständnisse erfordern Führungsfreigabe.

5. **Genehmigung**
   - Vertrag wird gemäß Delegationsmatrix genehmigt.

6. **Unterzeichnung und Archivierung**
   - Vertrag durch autorisierte Zeichnungsberechtigte unterschrieben.
   - Original im Vertragsarchiv gespeichert.
   - Schlüsseldaten erfasst und überwacht.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Vertragsprüfungsdauer | ≤5 Werktage Standard | Monatlich |
| Nutzung Standardvorlagen | ≥70% | Quartalsweise |
`,
      'sop-leg-002-dsar': `# SOP-LEG-002: Betroffenenrechte-Anfragen

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-LEG-002 |
| Titel | Betroffenenrechte-Anfragen |
| Funktion | Recht / Datenschutz |
| Verantwortlich | Datenschutzbeauftragter (DSB) |
| Prüfer | General Counsel |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Auskunfts-, Löschungs-, Berichtigungs-, Widerspruchs- und Datenübertragbarkeitsanfragen datenschutzkonform beantworten.

## 2. Geltungsbereich

Gilt für Anfragen auf Auskunft, Löschung, Berichtigung, Übertragbarkeit und Widerspruch.

## 3. Ablauf

1. **Eingang**
   - Anfrage über Datenschutz-E-Mail oder designierten Kanal erhalten.
   - Eingangsbestätigung innerhalb gesetzlicher Frist (z. B. 24–72 Stunden).

2. **Identitätsprüfung**
   - Identität der antragstellenden Person verifizieren, um unbefugte Offenlegung zu verhindern.

3. **Scope-Festlegung**
   - Umfang der Anfrage und anwendbares Recht bestimmen.
   - Betroffene Systeme und Daten identifizieren.

4. **Datensammlung**
   - Relevante Daten aus betroffenen Systemen sammeln.
   - Rechtsmittel oder Drittanbieterinformationen ausschließen.

5. **Prüfung und Schwärzung**
   - Recht prüft Ausnahmen oder Konflikte.
   - Schwärzung falls erforderlich.

6. **Antwort**
   - Antwort innerhalb gesetzlicher Frist (DSGVO: 1 Monat, ggf. verlängerbar).
   | Durchgeführte Maßnahme dokumentieren.

7. **Abschluss und Bericht**
   - Anfrage im Datenschutz-Register schließen.
   - Kennzahlen quartalsweise berichten.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Fristgerechte Beantwortung | 100% | Quartalsweise |
| Fehlerhafte Identitätsprüfungen | ≤1% | Quartalsweise |
`,
      'sop-leg-003-incident': `# SOP-LEG-003: Vorfall- und Datenpannen-Meldung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-LEG-003 |
| Titel | Vorfall- und Datenpannen-Meldung |
| Funktion | Recht / Sicherheit |
| Verantwortlich | Datenschutzbeauftragter (DSB) |
| Prüfer | General Counsel / CISO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Sicherheits- und Datenschutzvorfälle erkennen, bewerten und darauf reagieren.

## 2. Geltungsbereich

Gilt für alle vermuteten oder bestätigten Sicherheitsvorfälle und Verletzungen personenbezogener Daten.

## 3. Ablauf

1. **Erkennung und Meldung**
   - Vorfälle an security@ oder privacy@ melden.
   - Automatisierte Alarme durch IT-Sicherheit überwacht.

2. **Triage**
   - Schweregrad und Auswirkung einschätzen.
   - Vorfallreaktionsteam zusammenstellen.

3. **Containment**
   - Sofortmaßnahmen zur Schadensbegrenzung ergreifen.
   - Beweise sichern.

4. **Untersuchung**
   - Ursache und Ausmaß ermitteln.
   - Betroffene Daten und Personen identifizieren.

5. **Meldungsentscheidung**
   - Recht ermittelt Meldepflichten nach anwendbarem Recht.
   - Meldung bei Aufsichtsbehörden und betroffenen Personen innerhalb gesetzlicher Fristen (DSGVO: 72 Stunden bei hohem Risiko).

6. **Behebung**
   - Ursache beheben und Kontrollen stärken.

7. **Post-Vorfall-Review**
   - Lessons learned dokumentieren.
   - SOPs und Kontrollen bei Bedarf aktualisieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Vorfallreaktionszeit | ≤2 Stunden kritisch | Pro Vorfall |
| Datenpannen-Meldungs-Compliance | 100% | Pro Vorfall |
`,
      'sop-leg-004-ip': `# SOP-LEG-004: Schutzrechtsanmeldungen

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-LEG-004 |
| Titel | Schutzrechtsanmeldungen |
| Funktion | Recht / IP |
| Verantwortlich | IP-Beistand |
| Prüfer | General Counsel |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Geistiges Eigentum durch zeitnahe Anmeldungen und Eintragungen schützen.

## 2. Geltungsbereich

Gilt für Marken, Patente, Urheberrechte und Domainnamen.

## 3. Ablauf

1. **Identifikation**
   - F&E, Marketing oder Produkt melden neue Schutzrechte zur Prüfung.
   - IP-Beistand bewertet Schutzfähigkeit.

2. **Recherche und Freiheit**
   - Freiheitsrecherche für Marken; Patentierbarkeitsrecherche für Erfindungen.

3. **Anmeldeentscheidung**
   - Anmeldung basierend auf Geschäftswert, Kosten und Risiko entscheiden.
   - Budgetfreigabe einholen.

4. **Anmeldung**
   - Anmeldung in relevanten Gerichtsbarkeiten vorbereiten und einreichen (DPMA, EUIPO, WIPO nach Bedarf).
   - Anmeldedaten im IP-Register erfassen.

5. **Prüfung und Verlängerung**
   - Beschlüsse der Prüfungsstellen beantworten.
   - Verlängerungs- oder Aufrechterhaltungsgebühren zahlen.
   - Fristen überwachen.

6. **Durchsetzung**
   - Verletzungen überwachen.
   - Abmahnung, Widerspruch oder Klage je nach Lage einleiten.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Verlängerungsfristen eingehalten | 100% | Quartalsweise |
| IP-Register-Genauigkeit | ≥98% | Jährlich |
`,
      'sop-leg-005-whistleblower': `# SOP-LEG-005: Hinweisgeber-Beschwerdebearbeitung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-LEG-005 |
| Titel | Hinweisgeber-Beschwerdebearbeitung |
| Funktion | Recht / Compliance |
| Verantwortlich | Compliance Officer |
| Prüfer | General Counsel |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Hinweisgebermeldungen vertraulich und fair entgegennehmen, untersuchen und auflösen.

## 2. Geltungsbereich

Gilt für Meldungen über illegales, unethisches oder unsicheres Verhalten.

## 3. Ablauf

1. **Eingang**
   - Meldungen über Hotline, E-Mail oder designierten Kanal erhalten.
   - Eingangsbestätigung ohne Offenlegung der Identität.

2. **Triage**
   - Compliance Officer prüft Meldung und bestimmt Schweregrad.
   - Recht, HR oder externer Beistand bei Bedarf einbinden.

3. **Untersuchung**
   - Unparteiische Untersuchungsperson bestellen.
   - Vertraulichkeit wahren und Vergeltung verhindern.
   - Beweise sammeln und relevante Parteien befragen.

4. **Auflösung**
   - Feststellungen und empfohlene Maßnahmen festlegen.
   - Korrektur- oder disziplinarische Maßnahmen umsetzen.

5. **Rückmeldung**
   - Rückmeldung an Meldende Person, soweit rechtlich zulässig.
   | Unterlagen sicher aufbewahren.

6. **Berichterstattung**
   - Aggregierte Statistiken an Vorstand oder Prüfungsausschuss berichten.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Meldungen innerhalb 48 Stunden bestätigt | 100% | Quartalsweise |
| Vergeltungsbeschwerden | 0 | Quartalsweise |
`,
    },
    marketing: {
      'sop-mkt-001-campaigns': `# SOP-MKT-001: Kampagnenplanung und -start

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-MKT-001 |
| Titel | Kampagnenplanung und -start |
| Funktion | Marketing |
| Verantwortlich | Marketing Manager |
| Prüfer | CMO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Marketingkampagnen planen, durchführen und messen.

## 2. Geltungsbereich

Gilt für Produktlaunches, Demand-Generation-Kampagnen, Brand-Kampagnen und Events.

## 3. Ablauf

1. **Zielsetzung**
   - Kampagnenziel, Zielgruppe, Budget und KPIs definieren.
   - Abstimmung mit Vertrieb und Produkt.

2. **Strategie und Messaging**
   - Kampagnenthema und Messaging entwickeln.
   | Markenleitlinien-Compliance sicherstellen.

3. **Asset-Produktion**
   - Texte, Visuals, Landingpages, E-Mails und Anzeigen erstellen.
   - Content-Freigabeprozess durchlaufen.

4. **Kanalplan**
   - Kanäle auswählen und Kalender erstellen.
   - Tracking und Attribution einrichten.

5. **Launch**
   - Kampagne gemäß Plan ausführen.
   - Performance in der ersten Woche täglich überwachen.

6. **Review**
   - Ergebnisse gegen KPIs messen.
   - Learnings dokumentieren und Folgemaßnahmen optimieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Kampagnen-ROI | ≥3:1 | Pro Kampagne |
| Lead-Qualität-Score | ≥70 | Pro Kampagne |
`,
      'sop-mkt-002-content': `# SOP-MKT-002: Content-Freigabe und -veröffentlichung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-MKT-002 |
| Titel | Content-Freigabe und -veröffentlichung |
| Funktion | Marketing |
| Verantwortlich | Content Lead |
| Prüfer | CMO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Sicherstellen, dass alle veröffentlichten Inhalte korrekt, markenkonform und rechtlich unbedenklich sind.

## 2. Geltungsbereich

Gilt für Blogposts, Whitepapers, Videos, Webseiten und externe Kommunikation.

## 3. Ablauf

1. **Briefing**
   - Content-Eigentümer erstellt Briefing mit Zielgruppe, Botschaft und Kanal.

2. **Entwurf**
   - Texter:in oder Creator erstellt Entwurf gemäß Tone-of-Voice-Leitfaden.

3. **Review**
   - Redaktion prüft Qualität, Genauigkeit und Markenabstimmung.
   - Recht oder Compliance prüft regulierte Inhalte.
   - Fachexperte verifiziert technische Richtigkeit.

4. **Freigabe**
   - Genehmigende Person gibt Freigabe im Workflow-Tool.

5. **Veröffentlichung**
   - Content wird geplant oder veröffentlicht.
   - Verteilungsplan aktiviert.

6. **Messung**
   - Engagement verfolgen; veraltete Inhalte aktualisieren oder zurückziehen.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Content-Review-Durchlaufzeit | ≤5 Werktage | Monatlich |
| Veröffentlichte Fehler / Rückrufe | ≤1 pro Quartal | Quartalsweise |
`,
      'sop-mkt-003-social': `# SOP-MKT-003: Social-Media-Management

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-MKT-003 |
| Titel | Social-Media-Management |
| Funktion | Marketing |
| Verantwortlich | Social Media Manager |
| Prüfer | CMO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Social-Media-Kanäle konsistent, sicher und effektiv betreiben.

## 2. Geltungsbereich

Gilt für alle firmenverwalteten Social-Media-Accounts.

## 3. Ablauf

1. **Planung**
   - Redaktionskalender zu Kampagnen und Markensäulen pflegen.
   - Posting-Frequenz pro Kanal definieren.

2. **Erstellung**
   - Beiträge gemäß Tone-of-Voice und visuellen Leitlinien erstellen.
   - Genehmigte Hashtags und Handles verwenden.

3. **Freigabe**
   - Beiträge durch Content Lead oder designierte Person prüfen.
   - Sensitive Themen erfordern zusätzliche Prüfung.

4. **Veröffentlichung**
   - Beiträge über genehmigtes Tool planen.
   - Direkt nach Veröffentlichung auf Fehler und Engagement achten.

5. **Community-Management**
   - Kommentare und Nachrichten innerhalb SLA beantworten.
   - Negative oder rechtliche Themen an Führung eskalieren.

6. **Reporting**
   - Reichweite, Engagement und Sentiment verfolgen.
   - Monatlich an Marketing-Führung berichten.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Antwortzeit auf Nachrichten | ≤4 Stunden | Wöchentlich |
| Engagement-Rate | Kanal-Benchmark | Monatlich |
`,
      'sop-mkt-004-events': `# SOP-MKT-004: Eventplanung und -durchführung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-MKT-004 |
| Titel | Eventplanung und -durchführung |
| Funktion | Marketing |
| Verantwortlich | Events Manager |
| Prüfer | CMO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Events planen und durchführen, die Marketing- und Geschäftsziele erreichen.

## 2. Geltungsbereich

Gilt für Webinare, Messen, Konferenzen und firmeneigene Events.

## 3. Ablauf

1. **Ziel und Budget**
   - Eventziele, Zielgruppe und Budget definieren.
   - Budgetfreigabe einholen.

2. **Planung**
   - Veranstaltungsort oder Plattform wählen.
   - Ablaufplan und Zeitplan erstellen.
   - Speaker und Sponsoren identifizieren.

3. **Promotion**
   - Registrierungsseite und Werbemittel erstellen.
   - E-Mail- und Social-Media-Promotion durchführen.

4. **Durchführung**
   - Proben durchführen.
   - Logistik, AV und Personal managen.
   - Leads und Content erfassen.

5. **Follow-Up**
   - Teilnehmer bedanken und Materialien teilen.
   - Leads bewerten und an Vertrieb weitergeben.

6. **Review**
   - Ergebnisse gegen Ziele und Budget messen.
   - Lessons learned dokumentieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Teilnehmerzufriedenheit | ≥4,0 / 5 | Pro Event |
| Generierter Pipeline | Pro Ziel | Pro Event |
`,
      'sop-mkt-005-brand-assets': `# SOP-MKT-005: Markenasset-Anfragen

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-MKT-005 |
| Titel | Markenasset-Anfragen |
| Funktion | Marketing |
| Verantwortlich | Brand Manager |
| Prüfer | CMO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Anfragen für Markenassets verwalten und konsistente Nutzung sicherstellen.

## 2. Geltungsbereich

Gilt für Logos, Vorlagen, Fotografie, Designdateien und Markenleitlinien.

## 3. Ablauf

1. **Anfrage**
   - Internes Team reicht Asset-Anfrage mit Anwendungsfall und Deadline ein.

2. **Triage**
   - Brand Manager bewertet Anfrage und Priorität.
   - Bei vorhandenem Asset auf Self-Service-Bibliothek verweisen.

3. **Erstellung oder Bereitstellung**
   - Design-Team erstellt oder passt Asset an.
   - Asset folgt Markenleitlinien.

4. **Freigabe**
   - Brand Manager gibt finales Asset frei.

5. **Distribution**
   - Asset mit Nutzungshinweisen ausliefern.
   | Wiederverwendbares Asset in Bibliothek aufnehmen.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Erfüllungszeit Anfragen | ≤5 Werktage | Monatlich |
| Verstöße gegen Markenleitlinien | ≤2 | Quartalsweise |
`,
    },
    operations: {
      'sop-ops-001-vendor-management': `# SOP-OPS-001: Lieferantenauswahl und -management

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-OPS-001 |
| Titel | Lieferantenauswahl und -management |
| Funktion | Betrieb |
| Verantwortlich | Operations Manager |
| Prüfer | COO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Lieferanten auswählen, onboarden und managen, die Qualitäts-, Kosten- und Risikostandards erfüllen.

## 2. Geltungsbereich

Gilt für alle externen Lieferanten von Waren und Dienstleistungen.

## 3. Ablauf

1. **Bedarfsermittlung**
   - Anforderungen, Budget und Erfolgskriterien definieren.
   - Strategische Bedeutung und Risikoniveau bestimmen.

2. **Sourcing**
   - Potenzielle Lieferanten identifizieren.
   - Angebots- oder Preisanfrage je nach Bedarf versenden.

3. **Bewertung**
   - Lieferanten nach Kosten, Fähigkeit, Referenzen, Sicherheit und Compliance bewerten.
   - Due Diligence für Hochrisiko-Lieferanten.

4. **Auswahl und Vertrag**
   - Bevorzugten Lieferanten auswählen.
   - Konditionen verhandeln und Vertrag abschließen.
   - Lieferant im Lieferantenregister erfassen.

5. **Laufendes Management**
   - Leistung gegen SLAs verfolgen.
   - Periodische Reviews durchführen.
   | Risikobewertungen und Verlängerungen pflegen.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| SLA-Einhaltung Lieferanten | ≥95% | Quartalsweise |
| Kritische Lieferanten-Risk-Reviews | 100% | Jährlich |
`,
      'sop-ops-002-procurement': `# SOP-OPS-002: Beschaffung von Waren und Dienstleistungen

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-OPS-002 |
| Titel | Beschaffung von Waren und Dienstleistungen |
| Funktion | Betrieb |
| Verantwortlich | Beschaffungsleitung |
| Prüfer | COO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Den Einkauf von Waren und Dienstleistungen für den Betrieb standardisieren.

## 2. Geltungsbereich

Gilt für operative Einkäufe, die nicht unter SOP-FIN-005 fallen.

## 3. Ablauf

1. **Anfrage**
   - Nutzer:in reicht Beschaffungsanfrage mit Spezifikationen und Menge ein.

2. **Lieferantenauswahl**
   - Beschaffung identifiziert Lieferanten oder nutzt bevorzugte Lieferantenliste.
   - Wettbewerbsangebote für Hochwertkäufe einholen.

3. **Genehmigung**
   - Vorgesetzte und Finanzen genehmigen gemäß Delegationsmatrix.

4. **Bestellung und Wareneingang**
   - Beschaffung tätigt Bestellung.
   - Nutzer:in bestätigt Empfang und Qualität.

5. **Rechnung und Zahlung**
   - Beschaffung leitet Rechnung an Finanzen zur Zahlung weiter.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Kosteneinsparung gegen Budget | ≥3% | Quartalsweise |
| Termingerechte Lieferung | ≥95% | Monatlich |
`,
      'sop-ops-003-quality': `# SOP-OPS-003: Qualitätskontrolle und -sicherung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-OPS-003 |
| Titel | Qualitätskontrolle und -sicherung |
| Funktion | Betrieb |
| Verantwortlich | Quality Manager |
| Prüfer | COO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Sicherstellen, dass Produkte und Dienstleistungen konsistent definierte Qualitätsstandards erfüllen.

## 2. Geltungsbereich

Gilt für alle Liefergegenstände, Produkte und kundenorientierten Dienstleistungen.

## 3. Ablauf

1. **Standards definieren**
   - Qualitätskriterien und Akzeptanzschwellen dokumentieren.
   - Team in Standards schulen.

2. **Prüfen / Testen**
   - Inspektionen, Tests oder Reviews an definierten Prüfpunkten durchführen.
   - Ergebnisse erfassen.

3. **Mängel identifizieren**
   - Abweichungen und Ursachen erfassen.
   - Schweregrad klassifizieren.

4. **Korrigieren und Vorbeugen**
   - Korrekturmaßnahmen umsetzen.
   - Verfahren zur Wiederholungsvermeidung aktualisieren.

5. **Berichten**
   - Qualitätskennzahlen an Betriebsführung berichten.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Fehlerquote | ≤2% | Monatlich |
| Qualitätsbeschwerden Kunden | ≤1% | Monatlich |
`,
      'sop-ops-004-inventory': `# SOP-OPS-004: Lagerverwaltung

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-OPS-004 |
| Titel | Lagerverwaltung |
| Funktion | Betrieb |
| Verantwortlich | Lager- / Operations Manager |
| Prüfer | COO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Optimale Lagerbestände und korrekte Bestandsaufzeichnungen halten.

## 2. Geltungsbereich

Gilt für physische Lagerbestände, Rohmaterialien, Fertigprodukte und Betriebsmittel.

## 3. Ablauf

1. **Wareneingang**
   - Eingehende Sendungen inspizieren.
   - Mengen erfassen und Lagersystem aktualisieren.

2. **Lagerung**
   - Artikel an zugewiesenen Stellen lagern.
   - FIFO oder definierte Rotationspolitik einhalten.

3. **Entnahme**
   - Artikel gegen autorisierte Entnahme oder Kundenauftrag kommissionieren.
   - Lagersystem aktualisieren.

4. **Inventur**
   | Regelmäßige Stichtagsinventuren durchführen.
   | Mindestens jährlich volle körperliche Inventur.

5. **Abstimmung**
   - Abweichungen untersuchen und lösen.
   | Bestände mit ordnungsgemäßer Genehmigung korrigieren.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Bestandsgenauigkeit | ≥98% | Monatlich |
| Stockout-Rate | ≤2% | Monatlich |
`,
      'sop-ops-005-facilities': `# SOP-OPS-005: Gebäude- und Arbeitssicherheit

## Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | SOP-OPS-005 |
| Titel | Gebäude- und Arbeitssicherheit |
| Funktion | Betrieb |
| Verantwortlich | Facility Manager |
| Prüfer | COO |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | Jährlich |
| Version | 1.0 |
| Status | Entwurf |

## 1. Zweck

Einen sicheren, compliant und produktiven Arbeitsplatz erhalten.

## 2. Geltungsbereich

Gilt für alle Büros, Lager und vom Unternehmen bereitgestellten Home-Office-Ausstattungen.

## 3. Ablauf

1. **Inspektionen**
   - Regelmäßige Sicherheitsbegehungen durchführen.
   - Prüfprotokolle führen.

2. **Gefahrenmeldung**
   - Mitarbeitende melden Gefahren an Facility oder HR.
   - Facility bewertet und beseitigt innerhalb definierter Fristen.

3. **Notfallvorsorge**
   - Notausgänge, Beschilderung und Ausrüstung warten.
   | Übungen gemäß lokalem Recht durchführen.

4. **Home-Office-Ausstattung**
   - Ergonomische Bedürfnisse bewerten.
   | Genehmigte Ausstattung ausgeben und erfassen.

5. **Vorfallreaktion**
   - Arbeitsunfälle oder Sicherheitsvorfälle dokumentieren.
   | Versicherungen und Behörden nach Bedarf melden.

## 4. Kennzahlen

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| Sicherheitsvorfälle | Keine schweren Vorfälle | Monatlich |
| Gefahrenbeseitigungszeit | ≤5 Werktage | Monatlich |
`,
    },
    sop_index: `# SOP-Index

Masterliste aller Standardarbeitsanweisungen.

## Legende

| Status | Bedeutung |
|--------|-----------|
| Entwurf | In Entwicklung |
| Freigegeben | Aktiv und in Gebrauch |
| In Überprüfung | Geplanter oder laufender Review |
| Archiviert | Nicht mehr aktiv |

## Personalwesen

| ID | Titel | Verantwortlich | Review-Zyklus | Status |
|----|-------|----------------|---------------|--------|
| SOP-HR-001 | Rekrutierung und Einstellung | | Jährlich | Entwurf |
| SOP-HR-002 | Neues Mitarbeiter-Onboarding | | Jährlich | Entwurf |
| SOP-HR-003 | Mitarbeiter-Offboarding | | Jährlich | Entwurf |
| SOP-HR-004 | Leistungsmanagement | | Jährlich | Entwurf |
| SOP-HR-005 | Urlaubs- und Abwesenheitsanträge | | Jährlich | Entwurf |

## Finanzen

| ID | Titel | Verantwortlich | Review-Zyklus | Status |
|----|-------|----------------|---------------|--------|
| SOP-FIN-001 | Rechnungsstellung und Forderungsmanagement | | Jährlich | Entwurf |
| SOP-FIN-002 | Spesenabrechnung | | Jährlich | Entwurf |
| SOP-FIN-003 | Lohnbuchhaltung | | Jährlich | Entwurf |
| SOP-FIN-004 | Monatsabschluss | | Jährlich | Entwurf |
| SOP-FIN-005 | Bestellung und Beschaffung | | Jährlich | Entwurf |

## Betrieb

| ID | Titel | Verantwortlich | Review-Zyklus | Status |
|----|-------|----------------|---------------|--------|
| SOP-OPS-001 | Lieferantenauswahl und -management | | Jährlich | Entwurf |
| SOP-OPS-002 | Beschaffung von Waren und Dienstleistungen | | Jährlich | Entwurf |
| SOP-OPS-003 | Qualitätskontrolle und -sicherung | | Jährlich | Entwurf |
| SOP-OPS-004 | Lagerverwaltung | | Jährlich | Entwurf |
| SOP-OPS-005 | Gebäude- und Arbeitssicherheit | | Jährlich | Entwurf |

## Marketing

| ID | Titel | Verantwortlich | Review-Zyklus | Status |
|----|-------|----------------|---------------|--------|
| SOP-MKT-001 | Kampagnenplanung und -start | | Jährlich | Entwurf |
| SOP-MKT-002 | Content-Freigabe und -veröffentlichung | | Jährlich | Entwurf |
| SOP-MKT-003 | Social-Media-Management | | Jährlich | Entwurf |
| SOP-MKT-004 | Eventplanung und -durchführung | | Jährlich | Entwurf |
| SOP-MKT-005 | Markenasset-Anfragen | | Jährlich | Entwurf |

## Recht und Compliance

| ID | Titel | Verantwortlich | Review-Zyklus | Status |
|----|-------|----------------|---------------|--------|
| SOP-LEG-001 | Vertragsprüfung und -genehmigung | | Jährlich | Entwurf |
| SOP-LEG-002 | Betroffenenrechte-Anfragen | | Jährlich | Entwurf |
| SOP-LEG-003 | Vorfall- und Datenpannen-Meldung | | Jährlich | Entwurf |
| SOP-LEG-004 | Schutzrechtsanmeldungen | | Jährlich | Entwurf |
| SOP-LEG-005 | Hinweisgeber-Beschwerdebearbeitung | | Jährlich | Entwurf |

## Informationstechnologie

| ID | Titel | Verantwortlich | Review-Zyklus | Status |
|----|-------|----------------|---------------|--------|
| SOP-IT-001 | Benutzerzugriffskontrolle und Bereitstellung | | Jährlich | Entwurf |
| SOP-IT-002 | Software-Beschaffung und -genehmigung | | Jährlich | Entwurf |
| SOP-IT-003 | Vorfallreaktion und Eskalation | | Jährlich | Entwurf |
| SOP-IT-004 | Backup und Disaster Recovery | | Jährlich | Entwurf |
| SOP-IT-005 | IT-Asset-Management | | Jährlich | Entwurf |
`,
    sop_template: `# Vorlage für Standardarbeitsanweisungen (SOP)

Diese Vorlage ist für jede SOP in der Organisation zu verwenden.

## SOP-Kopfzeile

| Feld | Wert |
|------|------|
| SOP-ID | z. B. SOP-HR-001 |
| Titel | |
| Funktion | z. B. Personalwesen |
| Verantwortlich | Name und Rolle |
| Prüfer | Name und Rolle |
| Gültig ab | JJJJ-MM-TT |
| Review-Zyklus | z. B. Jährlich |
| Version | z. B. 1.0 |
| Status | Entwurf / Freigegeben / Archiviert |

## 1. Zweck

Darstellung, warum diese SOP existiert und welches Ziel sie erreicht.

## 2. Geltungsbereich

Definieren Sie, für wen und was diese SOP gilt.

- **Gilt für**:
- **Gilt nicht für**:

## 3. Begriffsdefinitionen

| Begriff | Definition |
|---------|------------|
| | |

## 4. Rollen und Verantwortlichkeiten

| Rolle | Verantwortlichkeit |
|-------|--------------------|
| | |

## 5. Ablauf

Nummerierte Schritte. Entscheidungspunkte, Genehmigungen und Dokumentationen einfügen.

1. Schritt eins.
2. Schritt zwei.
3. Schritt drei.

### 5.1 Teilablauf (falls erforderlich)

1. Unterschritt.
2. Unterschritt.

## 6. Erforderliche Eingaben und Ergebnisse

| Eingabe | Quelle | Ergebnis | Ziel |
|---------|--------|----------|------|
| | | | |

## 7. Werkzeuge, Systeme und Referenzen

- Systeme:
- Formulare:
| Verwandte SOPs:
- Richtlinien:
- Vorschriften:

## 8. Ausnahmen und Eskalation

- Zulässige Ausnahmen:
- Eskalationspfad:

## 9. Kennzahlen und KPIs

| Kennzahl | Ziel | Häufigkeit |
|----------|------|------------|
| | | |

## 10. Änderungshistorie

| Version | Datum | Autor | Änderungsbeschreibung |
|---------|-------|-------|-----------------------|
| 1.0 | | | Erstveröffentlichung |

## 11. Freigabe

| Rolle | Name | Unterschrift / Datum |
|-------|------|----------------------|
| Verantwortlich | | |
| Funktionsleitung | | |
| Compliance (falls erforderlich) | | |
`,
  },
  reports: {
    README: `# 05 — Berichtsstrukturen

Dieser Abschnitt standardisiert wiederkehrende Geschäftsberichte, damit sie konsistent, entscheidungsorientiert und einfach zu erstellen sind.

## Dateien

- \`report_structures.md\` — Prinzipien und Anatomie jedes Geschäftsberichts.
- \`executive_report_template.md\` — Monatliche / ad-hoc Executive-Zusammenfassung.
- \`quarterly_business_review_template.md\` — QBR-Format und KPI-Tabellen.
- \`status_report_template.md\` — Projekt- oder Initiativenstatusbericht.

## Nutzung

Jede Vorlage kann in ein Dokument oder eine Präsentation kopiert werden. Abschnitte werden an Zielgruppe und Kadenz angepasst.
`,
    executive_report_template: `# Vorlage für Executive Report

Vorlage für monatliche oder ad-hoc Executive-Updates.

---

## Berichtsmetadaten

| Feld | Wert |
|------|------|
| Berichtstitel | Executive Report — [Monat / Quartal] |
| Zeitraum | JJJJ-MM-TT bis JJJJ-MM-TT |
| Erstellt von | Name, Titel |
| Datum | JJJJ-MM-TT |
| Verteiler | CEO, CFO, COO, Vorstand (falls zutreffend) |

## Executive Summary

- Highlights 1.
- Highlights 2.
- Top-Risiko oder -Sorge.
- Erforderliche Entscheidung.

## Geschäftsperformance

| Kennzahl | Ziel | Ist | Abweichung | Trend |
|----------|------|-----|------------|-------|
| Umsatz | | | | |
| Bruttomarge | | | | |
| Nettoergebnis / Burn | | | | |
| Kopfzahl | | | | |
| Kunden-NPS | | | | |

## Funktionsupdates

### Vertrieb und Umsatz

- Pipeline-Übersicht
- Gewonnene und verlorene Deals
- Forecast vs. Plan

### Betrieb

- Wesentliche Betriebskennzahlen
- Blocker und Lösungen
- Qualitäts- / Liefer-Updates

### Produkt / Dienstleistung

- Releases oder Meilensteine
- Kundenfeedback-Themen
- Roadmap-Updates

### Personal

- Hiring-Fortschritt
- Retention-Highlights
- Kultur- / Engagement-Notizen

### Finanzen

- Liquiditätslage
- Burn Rate und Runway
| Wesentliche Ausgaben oder Abweichungen

## Risiken und Themen

| Risiko | Auswirkung | Wahrscheinlichkeit | Mitigation | Eigentümer |
|--------|------------|--------------------|------------|------------|
| | | | | |

## Entscheidungen und Maßnahmen

| Maßnahme | Eigentümer | Fälligkeit | Status |
|----------|------------|------------|--------|
| | | | |

## Anhang

- Detaillierte Finanzberichte
- Diagramme und unterstützende Daten
`,
    quarterly_business_review_template: `# Vorlage für Quarterly Business Review (QBR)

Vorlage für quartalsweise Leadership-Reviews.

---

## QBR-Metadaten

| Feld | Wert |
|------|------|
| Quartal | Q[X] JJJJ |
| Datum | JJJJ-MM-TT |
| Moderator | |
| Teilnehmer | |

## 1. Quartal im Überblick

### Scorecard

| Zielbereich | KPI | Ziel | Ist | Status |
|-------------|-----|------|-----|--------|
| Finanzen | Umsatz | | | |
| Finanzen | Bruttomarge | | | |
| Kunde | NPS | | | |
| Kunde | Churn | | | |
| Intern | Termingerechte Lieferung | | | |
| Intern | Mitarbeiterengagement | | | |
| Lernen | Produkt-Releases | | | |

### Top-Erfolge

1.
2.
3.

### Top-Herausforderungen

1.
2.
3.

## 2. Finanzreview

- Umsatz und Wachstum vs. Plan
- Aufwandsanalyse
- Profitabilität und Cashflow
- Prognose für nächstes Quartal

## 3. Markt- und Kundenreview

- Markttrends
- Wettbewerbs-Updates
- Kundenfeedback-Zusammenfassung
- Win/Loss-Analyse

## 4. Funktionale Deep Dives

### Vertrieb

- Pipeline-Coverage
- Conversion-Rates
- Top-Deals

### Marketing

- Kampagnenperformance
- Lead-Generierung
- Markenkennzahlen

### Produkt / Entwicklung

- Roadmap-Fortschritt
- Qualitätskennzahlen
- Technische Schulden

### Betrieb

- Kapazität und Effizienz
- Lieferantenperformance
- Kostenkontrolle

### Personal

- Hiring vs. Plan
- Retention
- Entwicklung und Engagement

## 5. Strategische Initiativen

| Initiative | Status | Fortschritt | Nächster Meilenstein | Eigentümer |
|------------|--------|-------------|----------------------|------------|
| | | | | |

## 6. Risiken und Mitigationen

| Risiko | Trend | Mitigation | Eigentümer |
|--------|-------|------------|------------|
| | | | |

## 7. Prioritäten nächstes Quartal

1.
2.
3.
4.
5.

## 8. Offene Entscheidungen

| Entscheidung | Optionen | Empfehlung | Entscheider | Frist |
|--------------|----------|------------|-------------|-------|
| | | | | |

## 9. Anhang

- Detaillierte Finanzdaten
| Abteilungsdashboards
- Kundenzitate / Case Studies
`,
    report_structures: `# Berichtsstrukturen

Leitprinzipien für alle Geschäftsberichte.

## 1. Prinzipien

- **Zielgruppenorientiert**: Tiefe und Tonfall auf den Leser abstimmen.
- **Entscheidungsorientiert**: Jeder Bericht sollte zu einer Aktion, Entscheidung oder Erkenntnis führen.
- **Konsistentes Format**: Gleiche Struktur, Bezeichnungen und Kennzahlen über Zeit.
- **Visuelle Klarheit**: Diagramme und Tabellen bevorzugen gegenüber dichtem Text.
- **Single Source of Truth**: Aus genehmigten Systemen ziehen und Annahmen definieren.
- **Termintreue**: Im vereinbarten Rhythmus liefern.

## 2. Anatomie eines Geschäftsberichts

Jeder wiederkehrende Bericht sollte enthalten:

1. **Titel und Metadaten**
   - Berichtsname
   - Abgedeckter Zeitraum
   - Erstellt von und Datum
   - Verteilerliste

2. **Executive Summary**
   - 3–5 Kernaussagen zu Geschehnissen und Relevanz.
   - Erforderliche Entscheidungen.

3. **Kontext / Methodik**
   - Umfang, Definitionen und Datenquellen.
   | Annahmen oder Ausnahmen.

4. **Hauptteil / Erkenntnisse**
   - Nach Themen, Priorität oder Funktion gegliedert.
   - Diagramme, Tabellen und Callouts nutzen.

5. **Maßnahmen und Verantwortliche**
   - Tabelle mit Next Steps, Eigentümern und Fristen.

6. **Anhang (bei Bedarf)**
   - Detaillierte Daten, Definitionen oder unterstützende Analysen.

## 3. Berichtstypen und Kadenz

| Bericht | Kadenz | Zielgruppe | Hauptzweck |
|---------|--------|------------|------------|
| Executive Report | Monatlich | CEO / Vorstand | Strategischer Snapshot |
| Quarterly Business Review | Quartalsweise | Leadership-Team | Tiefe Leistungsbewertung |
| Status Report | Wöchentlich / 14-tägig | Projektbeteiligte | Initiativfortschritt |
| Finanzbericht | Monatlich | CFO / Finanzen | Finanzielle Performance |
| Betriebsbericht | Wöchentlich | COO / Betrieb | Betriebsgesundheit |
| Marketingbericht | Monatlich | CMO / Marketing | Kampagnen- und Pipeline-Performance |
| Vertriebsbericht | Wöchentlich | CRO / Vertrieb | Pipeline und Umsatz |
| Risiko- und Compliancebericht | Quartalsweise | General Counsel / Audit | Risikolage |

## 4. Schreibregeln

- Headline im ersten Absatz nennen.
- Aktive Sprache verwenden.
| Akronyme bei erster Verwendung definieren.
- Absätze kurz halten (max. 3–4 Sätze).
| Numrierte Listen für Maßnahmen verwenden.
- Für jede Kennzahl ein „Was bedeutet das?" formulieren.

## 5. Verteilung und Speicherung

- Berichte werden im designierten Shared Drive oder Reporting-Portal gespeichert.
- Verteilerlisten werden vom Berichtseigentümer gepflegt.
| Historische Berichte nach Geschäftsjahr archiviert.
`,
    status_report_template: `# Statusbericht-Vorlage

Vorlage für Projekt- oder Initiativen-Statusberichte.

---

## Berichtsmetadaten

| Feld | Wert |
|------|------|
| Projekt / Initiative | |
| Berichtszeitraum | |
| Erstellt von | |
| Datum | |
| Verteiler | |

## Gesamtstatus

| Status | Bedeutung |
|--------|-----------|
| 🟢 Auf Kurs | Keine Probleme erwartet |
| 🟡 Risiko | Probleme vorhanden, aber lösbar |
| 🔴 Abseits | Erhebliches Risiko, Ziele zu verfehlen |
| ⚪ Pausiert | Pausiert oder blockiert |

**Aktueller Status**: [Auswählen]

## Zusammenfassung

- Einzeiliger Projekt-zweck.
- Wesentlicher Fortschritt in dieser Periode.
- Top-Risiko oder -Blocker.

## Meilensteine

| Meilenstein | Zieldatum | Ist-Datum | Status | Notizen |
|-------------|-----------|-----------|--------|---------|
| | | | | |

## Schlüsselkennzahlen

| Kennzahl | Ziel | Aktuell | Trend |
|----------|------|---------|-------|
| | | | |

## Risiken und Themen

| Thema | Auswirkung | Eigentümer | Mitigation | Status |
|-------|------------|------------|------------|--------|
| | | | | |

## In dieser Periode erledigt

1.
2.
3.

## Geplant nächste Periode

1.
2.
3.

## Benötigte Entscheidungen

| Entscheidung | Von | Bis wann |
|--------------|-----|----------|
| | | |

## Budget / Ressourcen

| Posten | Budget | Verbraucht | Verbleibend |
|--------|--------|------------|-------------|
| | | | |

## Notizen und Anhang

- Links zu detaillierten Plänen
- Schlüsseldokumente oder Deliverables
`,
  },
  governance: {
    README: `# 06 — Governance

Dieser Abschnitt definiert, wie Dokumente, SOPs und Berichte kontrolliert, überprüft, genehmigt und archiviert werden.

## Dateien

- \`document_control_policy.md\` — Versionierung, Eigentümerschaft, Speicherung und Aufbewahrung.
- \`review_cycle_calendar.md\` — Standard-Review-Zeitplan nach Dokumententyp.
- \`approval_matrix.md\` — Wer was genehmigen muss, nach Kategorie und Auswirkung.

## Ziel

Sicherstellen, dass das Framework mit Unternehmenswachstum korrekt, compliant und nutzbar bleibt.
`,
    approval_matrix: `# Genehmigungsmatrix

Definiert, wer Dokumente, Verträge, Ausgaben und Maßnahmen nach Kategorie und Auswirkung genehmigen muss.

## Dokumentengenehmigungen

| Dokumententyp | Eigentümer prüft | Funktionsleitung | Recht | Compliance | CEO / Vorstand |
|---------------|------------------|------------------|-------|------------|----------------|
| SOP | ✓ | ✓ | — | Falls reguliert | — |
| Richtlinie | ✓ | ✓ | Falls rechtlich | Falls reguliert | ✓ |
| Markenleitlinien | ✓ | ✓ | ✓ | — | ✓ |
| Vertrag < 10.000 € | ✓ | ✓ | — | — | — |
| Vertrag 10.000–50.000 € | ✓ | ✓ | ✓ | Falls DP/Daten | — |
| Vertrag > 50.000 € | ✓ | ✓ | ✓ | Falls DP/Daten | ✓ |
| Arbeitsvertragsangebot — Standard | ✓ | ✓ | — | — | — |
| Arbeitsvertragsangebot — Abweichend | ✓ | ✓ | ✓ | — | Falls C-Level |
| Marketingkampagne | ✓ | ✓ | Falls Behauptungen | Falls reguliert | Falls > Budget |
| Pressemitteilung | ✓ | ✓ | ✓ | Falls reguliert | ✓ |

## Ausgabengenehmigungen

| Ausgabenkategorie | Schwellenwert | Genehmiger |
|---------------------|----------------|------------|
| Routine-Betriebsausgaben | Bis 500 € | Vorgesetzte |
| Betriebsausgaben | 500–5.000 € | Funktionsleitung |
| Investition / wesentliche Ausgabe | 5.000–25.000 € | CFO + Funktionsleitung |
| Große Investition | Über 25.000 € | CEO + Vorstand (falls erforderlich) |

## Personalmaßnahmen

| Maßnahme | Genehmiger |
|----------|------------|
| Einstellung im Budget | Hiring Manager + HR |
| Einstellung über Budget / neue Rolle | Funktionsleitung + CFO + HR |
| Kündigung — freiwillig | Vorgesetzte + HR |
| Kündigung — betriebsbedingt | Funktionsleitung + HR + Recht |
| Gehaltsänderung >10% | Funktionsleitung + CFO + HR |
| Beförderung | Funktionsleitung + HR |

## IT- und Sicherheitsmaßnahmen

| Maßnahme | Genehmiger |
|----------|------------|
| Standardbenutzerzugriff | Vorgesetzte + IT |
| Privilegierter Zugriff | Funktionsleitung + CISO |
| Neue Software < 1.000 €/Monat | Vorgesetzte + IT |
| Neue Software > 1.000 €/Monat | Funktionsleitung + CISO + Finanzen |
| Sicherheitsausnahme | CISO + Recht |
| Vorfalldeklaration | CISO + Recht / DSB |

## Notfallbefugnis

In dringenden Fällen darf die höchste verantwortliche Führungskraft außerhalb normaler Schwellen genehmigen, sofern:

- Notfall und Begründung innerhalb von 24 Stunden dokumentiert werden.
- Der zuständige Genehmiger innerhalb von 24 Stunden informiert wird.
- Eine retrospektive Prüfung innerhalb von 30 Tagen stattfindet.

## Delegation

- Genehmiger können schriftlich delegieren.
- Delegation muss in der Genehmigungsmatrix-Register erfasst werden.
- Delegation überträgt keine Verantwortung.
`,
    document_control_policy: `# Dokumentenkontrollrichtlinie

## 1. Zweck

Sicherstellen, dass Dokumente im Marken- und Unternehmensframework korrekt, zugänglich und ordnungsgemäß gepflegt sind.

## 2. Geltungsbereich

Gilt für alle Richtlinien, SOPs, Leitlinien, Vorlagen und kontrollierten Berichte.

## 3. Dokumentenkategorien

| Kategorie | Beispiele | Kontrollstufe |
|-----------|-----------|---------------|
| Kritisch | Richtlinien, SOPs, Verträge, Finanzkontrollen | Hoch |
| Standard | Leitlinien, Vorlagen, Schulungsmaterialien | Mittel |
| Referenz | Artikel, Marktforschung, historische Berichte | Niedrig |

## 4. Benennung und Nummerierung

- **Richtlinien**: POL-[FUNKTION]-### — z. B. POL-IT-001
- **SOPs**: SOP-[FUNKTION]-### — z. B. SOP-HR-001
- **Leitlinien**: GL-[FUNKTION]-### — z. B. GL-MKT-001
- **Berichte**: RPT-[TYP]-JJJJ-MM — z. B. RPT-EXEC-2026-07
- **Vorlagen**: TPL-[TYP] — z. B. TPL-OFFER

## 5. Versionskontrolle

- Semantische Versionierung verwenden: MAJOR.MINOR.
- Major-Version bei inhaltlichen Änderungen, die Schulungen erfordern.
- Minor-Version bei Klarstellungen, Korrekturen oder Formatierung.
- Entwürfe tragen 0.x bis zur Freigabe.
| Veraltete Versionen werden archiviert, nicht gelöscht.

## 6. Speicherung und Zugriff

- Master-Dokumente im designierten Dokumentenrepository speichern.
| Zugriffsrechte rollenbasiert vergeben.
- Öffentliche Dokumente vor Veröffentlichung von Recht / Marketing freigeben.

## 7. Änderungskontrolle

1. Änderungsantrag einreichen.
2. Dokumenteneigentümer bewertet Auswirkung.
3. Betroffene Stakeholder prüfen.
4. Genehmiger laut Genehmigungsmatrix freigeben.
5. Neue Version veröffentlichen; alte Version archivieren.
6. Betroffene Teams über Änderungen informieren.

## 8. Aufbewahrung

| Dokumentenart | Aufbewahrungsfrist |
|-----------------|--------------------|
| Richtlinien und SOPs | Aktuelle Version + 7 Jahre |
| Finanzunterlagen | 7+ Jahre (DE: 10 Jahre) |
| Personalakten | Beschäftigungsdauer + 7 Jahre |
| Verträge | Laufzeit + 7 Jahre |
| Vorfallberichte | 7 Jahre |
| Schulungsnachweise | Dauer + 3 Jahre |

## 9. Rollen

| Rolle | Verantwortlichkeit |
|-------|--------------------|
| Dokumenteneigentümer | Pflegt Korrektheit und plant Reviews |
| Funktionsleitung | Prüft und genehmigt funktionsspezifische Dokumente |
| Compliance / Recht | Prüft regulierte oder hochriskante Dokumente |
| Company Secretary / COO | Pflegt das Master-Dokumentenregister |

## 10. Compliance

- Mitarbeitende müssen aktuelle Versionen kontrollierter Dokumente verwenden.
- Abweichungen von SOPs müssen dokumentiert und genehmigt werden.
- Jährliche Audit der Dokumentenkontroll-Compliance.
`,
    review_cycle_calendar: `# Review-Zyklus-Kalender

Standard-Review-Zeitplan für Framework-Dokumente.

## Review-Auslöser

Dokumente müssen planmäßig oder früher überprüft werden, wenn:

- Gesetze oder Vorschriften sich ändern.
- Wesentliche Unternehmensänderungen eintreten (Fusion, neuer Markt, neues Produkt).
- Ein Vorfall oder Audit-Fund auf eine Lücke hinweist.
- Prozess- oder Systemänderungen das Verfahren wesentlich beeinflussen.

## Standard-Zeitplan

| Dokumententyp | Review-Häufigkeit | Review-Monat | Verantwortliche Rolle |
|---------------|-------------------|--------------|-----------------------|
| Richtlinien | Jährlich | Q1 | Funktionsleitung |
| SOPs | Jährlich | Q2 | SOP-Eigentümer |
| Markenleitlinien | Jährlich | Q1 | Brand Manager |
| Tone-of-Voice-Leitfaden | Jährlich | Q1 | Content Lead |
| Namensframework | Zweijährlich | Q1 | Brand / Recht |
| Rechtsstruktur-Vorlagen | Nach Bedarf / Zweijährlich | Q1 | Recht / Finanzen |
| Compliance-Checkliste | Jährlich | Q1 | Compliance Officer |
| Berichtsvorlagen | Jährlich | Q4 | COO / Funktionsleitungen |
| Dokumentenkontrollrichtlinie | Jährlich | Q1 | Company Secretary |
| Genehmigungsmatrix | Jährlich | Q1 | CEO / COO |
| Lieferanten-Risikobewertungen | Jährlich | Q3 | Beschaffung / Sicherheit |
| IT-Sicherheits-SOPs | Jährlich | Q3 | CISO |
| Datenschutz-SOPs | Jährlich | Q1 | DSB |

## Review-Workflow

1. **30 Tage vor Frist**: Eigentümer erhält Erinnerung.
2. **Review**: Eigentümer aktualisiert Inhalt, prüft Links und konsultiert Stakeholder.
3. **15 Tage vor Frist**: Entwurf zur Genehmigung eingereicht.
4. **Genehmigung**: Genehmiger prüft und gibt Freigabe.
5. **Veröffentlichung**: Neue Version veröffentlicht; alte archiviert.
6. **Kommunikation**: Änderungen an betroffene Teams kommuniziert.

## Kalendervorlage

| Monat | Reviews fällig |
|-------|----------------|
| Januar | Richtlinien, Markenleitlinien, Namensgebung, Compliance, Dokumentenkontrolle, Datenschutz |
| Februar | |
| März | |
| April | SOPs |
| Mai | |
| Juni | |
| Juli | |
| August | |
| September | Lieferantenrisiko, IT-Sicherheit |
| Oktober | |
| November | Berichtsvorlagen |
| Dezember | |

## Eskalation bei Überfälligkeit

- Überfällig um 15 Tage: Erinnerung an Eigentümer und Funktionsleitung.
- Überfällig um 30 Tage: Eskalation an COO / General Counsel.
`,
  },
} as const;
